import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback, useContext } from "react";
import { createPortal } from "react-dom";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import api from "../../api/axios";
import { useMailbox } from "../../context/MailboxContext";
import { RequiredActionsContext } from "../../context/RequiredActionsContext";
import { useBodyLock } from "../../hooks/useDisableBodyScroll";
import useNativeSwipeBack from "../../hooks/useNativeSwipeBack";
import { AUTH_GRADIENT_STYLE } from "../../constants/authVisuals";
import SegmentedModeToggle from "../common/SegmentedModeToggle";

import Linkify from "linkify-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const linkifyOptions = {
  attributes: {
    target: "_blank",
    rel: "noopener noreferrer",
    class: "underline text-blue-300 hover:text-blue-200",
  },
};

const normalizeNotificationMeta = (rawMeta) => {
  if (!rawMeta) return {};
  if (typeof rawMeta === "string") {
    try {
      const parsed = JSON.parse(rawMeta);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }
  return typeof rawMeta === "object" ? rawMeta : {};
};

const isRoundAssignmentNotification = (notification) => {
  if (!notification) return false;

  const kind = String(notification.kind || "").trim().toLowerCase();
  if (kind === "round_assignment") return true;

  const meta = normalizeNotificationMeta(notification.meta);
  const mode = String(meta.mode || "").trim().toLowerCase();

  return (
    mode === "table" ||
    mode === "spy" ||
    mode === "portrait" ||
    mode === "prompts_qna" ||
    mode === "wrong_answer" ||
    mode === "alias" ||
    mode === "film_emoji" ||
    mode === "character_facts" ||
    mode === "question_words" ||
    mode === "line_puzzle" ||
    mode === "glyph_relay" ||
    mode === "mixed_signals" ||
    mode === "common_thread" ||
    mode === "photo_year" ||
    mode === "conversation_cards" ||
    mode === "closest_guess" ||
    mode === "last_answer" ||
    mode === "mislead_quiz" ||
    meta.table_number != null ||
    meta.tableNumber != null ||
    meta.round_number != null ||
    meta.roundNumber != null ||
    meta.subject_code != null ||
    meta.subjectCode != null ||
    meta.wrong_answer_active_question != null ||
    meta.alias_active_word != null ||
    Array.isArray(meta.film_emoji_emojis) ||
    Array.isArray(meta.character_facts_items) ||
    Array.isArray(meta.question_words_items) ||
    Array.isArray(meta.question_prompts) ||
    Array.isArray(meta.action_prompts) ||
    Array.isArray(meta.alias_queue) ||
    Array.isArray(meta.glyph_relay_options) ||
    Array.isArray(meta.mixed_signals_words) ||
    meta.common_thread_word != null ||
    meta.photo_year_image_url != null ||
    meta.conversation_cards_question_en != null ||
    meta.closest_guess_phase != null ||
    meta.last_answer_phase != null ||
    meta.line_puzzle_tile_data_url != null ||
    meta.mislead_question != null
  );
};

const formatNotificationDate = (iso) => {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat(undefined, {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return "";
  }
};

const formatNotificationDateTime = (iso) => {
  if (!iso) return "";
  try {
    const value = new Date(iso);
    if (Number.isNaN(value.getTime())) return "";
    return new Intl.DateTimeFormat(undefined, {
      day: "numeric",
      month: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(value);
  } catch {
    return "";
  }
};

const toAbsoluteMediaUrl = (rawUrl) => {
  const candidate = String(rawUrl || "").trim();
  if (!candidate) return null;
  if (/^(data|blob):/i.test(candidate)) return candidate;
  if (/^https?:\/\//i.test(candidate)) return candidate;
  if (candidate.startsWith("//")) {
    const protocol =
      typeof window !== "undefined" && window.location?.protocol
        ? window.location.protocol
        : "https:";
    return `${protocol}${candidate}`;
  }

  const base = String(api?.defaults?.baseURL || "").trim();
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "https://vaultgrid.net";

  try {
    if (/^https?:\/\//i.test(base)) {
      return new URL(candidate, base).toString();
    }
    return new URL(candidate, origin).toString();
  } catch {
    return candidate.startsWith("/") ? `${origin}${candidate}` : `${origin}/${candidate}`;
  }
};

const parsePartyDateMs = (rawValue) => {
  const candidate = String(rawValue || "").trim();
  if (!candidate) return NaN;

  const normalized = candidate
    .replace(" ", "T")
    .replace(/(\.\d{3})\d+/, "$1")
    .replace(/([+-]\d{2})(\d{2})$/, "$1:$2");
  const parsed = Date.parse(normalized);
  if (Number.isFinite(parsed)) return parsed;

  const fallbackParsed = Date.parse(candidate);
  return Number.isFinite(fallbackParsed) ? fallbackParsed : NaN;
};

const EVENT_DEEP_LINK_QUERY_KEYS = [
  "confirm",
  "intent",
  "activation_date",
  "host_confirm",
  "force_payment",
  "pay",
  "manual_pay",
  "manual_payment",
  "confirm_intent",
  "confirm_attend",
  "concept_poll",
  "waitlist",
];

const MAILBOX_INFO_ONLY_ACTION_KINDS = new Set([
  "intent_cleanup",
  "activation_date_cleanup",
  "waitlist_slot_cleanup",
  "event_deleted_by_host",
  "event_deleted_host_confirmation_timeout",
  "event_deleted_concept_not_activated",
  "participant_missed_confirmation",
]);

const HOST_ROLE_ACTION_KINDS = new Set([
  "event_cohost_request",
  "event_host_transfer_request",
]);

const toPositiveInt = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const out = Math.floor(n);
  return out > 0 ? out : null;
};

const trimUrlTrailingPunctuation = (raw) => {
  let value = String(raw || "").trim();
  while (value && /[),.;!?]$/.test(value)) {
    value = value.slice(0, -1);
  }
  return value;
};

const extractUrlsFromText = (text) => {
  const value = String(text || "");
  if (!value) return [];

  const out = [];
  const absoluteRe = /https?:\/\/[^\s<>"']+/gi;
  const relativeRe = /\/(?:events\/\d+[^\s<>"']*|events\/search[^\s<>"']*|vault\/main[^\s<>"']*|api\/quests\/go\/[^\s<>"']*)/gi;

  let match;
  while ((match = absoluteRe.exec(value)) !== null) {
    const candidate = trimUrlTrailingPunctuation(match[0]);
    if (candidate) out.push(candidate);
  }
  while ((match = relativeRe.exec(value)) !== null) {
    const candidate = trimUrlTrailingPunctuation(match[0]);
    if (candidate) out.push(candidate);
  }

  return out;
};

const parseMaybeUrl = (rawUrl) => {
  const normalized = trimUrlTrailingPunctuation(rawUrl);
  if (!normalized) return null;
  if (!/^https?:\/\//i.test(normalized) && !normalized.startsWith("/")) return null;
  const origin = typeof window !== "undefined" ? window.location.origin : "https://vaultgrid.net";
  try {
    return new URL(normalized, origin);
  } catch {
    return null;
  }
};

const scoreUrlCandidate = (rawUrl) => {
  const value = String(rawUrl || "").trim().toLowerCase();
  if (!value) return 0;

  if (value.includes("/api/quests/go/")) return 100;
  if (value.includes("concept_poll=1")) return 95;
  if (value.includes("host_confirm=1")) return 90;
  if (value.includes("activation_date=1")) return 85;
  if (
    value.includes("/buy/") ||
    value.includes("manual_pay=1") ||
    value.includes("manual_payment=1") ||
    value.includes("force_payment=1") ||
    value.includes("pay=1")
  ) {
    return 80;
  }
  if (value.includes("confirm_intent=1") || value.includes("intent=1")) return 70;
  if (value.includes("confirm_attend=1") || value.includes("confirm=1")) return 60;
  if (value.includes("/events/search")) return 50;
  if (value.includes("/clubs/") && value.includes("/manage/ownership")) return 46;
  if (value.includes("/clubs/") && value.includes("/manage/requests")) return 45;
  if (value.includes("/events/")) return 40;
  if (value.includes("/menu/chats/event/")) return 30;
  if (value.includes("/vault/main")) return 20;
  return 10;
};

const MAILBOX_INLINE_URL_RE = /(https?:\/\/[^\s<>"']+|\/(?:events\/\d+[^\s<>"']*|events\/search[^\s<>"']*|clubs\/\d+[^\s<>"']*|vault\/main[^\s<>"']*|api\/quests\/go\/[^\s<>"']*))/gi;

const MAILBOX_LINK_ONLY_LINE_RE =
  /^\s*(open event|event page|please confirm via direct link|открыть событие|страница события|пожалуйста,\s*подтвердите по прямой ссылке|otvoriť podujatie|stránka podujatia)\s*:?\s*$/i;

const removeInlineLinksForActionMessage = (rawText) => {
  const input = String(rawText || "");
  if (!input) return "";

  const withoutUrls = input.replace(MAILBOX_INLINE_URL_RE, "");
  const lines = withoutUrls
    .split("\n")
    .map((line) => line.trimEnd())
    .filter((line) => !MAILBOX_LINK_ONLY_LINE_RE.test(line.trim()));

  return lines
    .join("\n")
    .replace(/[ \t]+([,.;!?])/g, "$1")
    .replace(/\(\s*\)/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const getEventActionLabel = (deepParams, t) => {
  const has = (key) => deepParams.get(key) === "1";
  const wantsPayment = has("pay") || has("manual_pay") || has("manual_payment") || has("force_payment");

  if (has("concept_poll")) {
    return t("mailbox.actions.open_date_poll", "Open date poll");
  }
  if (has("waitlist")) {
    return t("mailbox.actions.check_waitlist_availability", "Check availability");
  }
  if (has("host_confirm")) {
    return t("mailbox.actions.open_host_confirmation", "Open host confirmation");
  }
  if (has("activation_date")) {
    return t("mailbox.actions.open_date_confirmation", "Open date confirmation");
  }
  if ((has("intent") || has("confirm_intent")) && wantsPayment) {
    return t("mailbox.actions.open_payment", "Open payment");
  }
  if (has("intent") || has("confirm_intent")) {
    return t("mailbox.actions.open_intention_confirmation", "Confirm intention");
  }
  if (has("confirm") || has("confirm_attend")) {
    return t("mailbox.actions.open_attendance_confirmation", "Confirm arrival");
  }
  return t("mailbox.actions.open_event", "Open event");
};

const buildEventSearchDeepLink = ({ eventId, sourceParams, actionKind, t }) => {
  const safeEventId = toPositiveInt(eventId);
  if (!safeEventId) return null;

  if (actionKind === "anonymous_host_feedback") {
    return {
      to: `/events/search?host_comments_event=${safeEventId}`,
      label: t("mailbox.actions.open_participant_feedback", "View participant feedback"),
    };
  }

  const deepParams = new URLSearchParams();
  if (sourceParams instanceof URLSearchParams) {
    EVENT_DEEP_LINK_QUERY_KEYS.forEach((key) => {
      if (sourceParams.get(key) === "1") {
        deepParams.set(key, "1");
      }
    });
  }

  if (actionKind === "concept_date_poll" && !deepParams.has("concept_poll")) {
    deepParams.set("concept_poll", "1");
  }

  const target = new URLSearchParams();
  target.set("event", String(safeEventId));
  EVENT_DEEP_LINK_QUERY_KEYS.forEach((key) => {
    if (deepParams.get(key) === "1") target.set(key, "1");
  });

  return {
    to: `/events/search?${target.toString()}`,
    label: getEventActionLabel(deepParams, t),
  };
};

const buildActionFromUrl = ({ rawUrl, fallbackEventId, actionKind, sender, t }) => {
  const parsed = parseMaybeUrl(rawUrl);
  if (!parsed) return null;

  const pathname = String(parsed.pathname || "").trim();
  const searchParams = parsed.searchParams || new URLSearchParams();

  if (/^\/api\/quests\/go\//i.test(pathname)) {
    return {
      to: "/vault/main?open_tasks=1",
      label: t("mailbox.actions.open_tasks", "Open tasks"),
    };
  }

  if (/^\/vault\/main\/?$/i.test(pathname)) {
    const next = new URLSearchParams(searchParams);
    if (
      String(sender || "").toLowerCase() === "tasks" &&
      next.get("open_tasks") !== "1"
    ) {
      next.set("open_tasks", "1");
    }
    const query = next.toString();
    return {
      to: `/vault/main${query ? `?${query}` : ""}`,
      label: next.get("open_tasks") === "1"
        ? t("mailbox.actions.open_tasks", "Open tasks")
        : t("mailbox.actions.open_vault", "Open vault"),
    };
  }

  const clubRequestsMatch = pathname.match(/^\/clubs\/(\d+)(?:-[^/?#]+)?\/manage\/requests\/?$/i);
  if (clubRequestsMatch) {
    return {
      to: `${pathname}${parsed.search || ""}`,
      label: t("mailbox.actions.open_club_requests", "Open join requests"),
    };
  }

  const clubOwnershipMatch = pathname.match(/^\/clubs\/(\d+)(?:-[^/?#]+)?\/manage\/ownership\/?$/i);
  if (clubOwnershipMatch) {
    return {
      to: `${pathname}${parsed.search || ""}`,
      label: t("mailbox.actions.respond_to_club_creator_invitation", "Respond to invitation"),
    };
  }

  const buyEventMatch = pathname.match(/\/events\/(\d+)(?:[-/][^/?#]*)?\/buy\/?$/i);
  if (buyEventMatch) {
    const payParams = new URLSearchParams("intent=1&pay=1&manual_pay=1");
    return buildEventSearchDeepLink({
      eventId: buyEventMatch[1],
      sourceParams: payParams,
      actionKind,
      t,
    });
  }

  const directEventMatch = pathname.match(/\/events\/(\d+)(?:[-/]|$)/i);
  if (directEventMatch) {
    return buildEventSearchDeepLink({
      eventId: directEventMatch[1],
      sourceParams: searchParams,
      actionKind,
      t,
    });
  }

  if (/^\/events\/search\/?$/i.test(pathname)) {
    return buildEventSearchDeepLink({
      eventId: searchParams.get("event") || fallbackEventId,
      sourceParams: searchParams,
      actionKind,
      t,
    });
  }

  const chatEventMatch = pathname.match(/\/menu\/chats\/event\/(\d+)(?:\/|$)/i);
  if (chatEventMatch) {
    const query = parsed.search ? parsed.search : "";
    return {
      to: `/menu/chats/event/${chatEventMatch[1]}${query}`,
      label: t("mailbox.actions.open_chat", "Open chat"),
    };
  }

  if (/^\/menu\/singles\/?$/i.test(pathname)) {
    const query = parsed.search ? parsed.search : "";
    return {
      to: `/menu/singles${query}`,
      label: t("mailbox.actions.open_singles", "Open Singles"),
    };
  }

  return null;
};

const resolveNotificationAction = (notification, t) => {
  if (!notification || isRoundAssignmentNotification(notification)) return null;

  const sender = String(notification?.sender || "").trim().toLowerCase();
  const meta = normalizeNotificationMeta(notification?.meta);
  const actionKind = String(meta?.action_kind || meta?.actionKind || "").trim().toLowerCase();

  const hostRoleRequestStatus = String(
    meta?.request_status || meta?.requestStatus || "pending"
  ).trim().toLowerCase();
  if (
    HOST_ROLE_ACTION_KINDS.has(actionKind)
    && hostRoleRequestStatus === "pending"
    && !notification?.is_read
  ) {
    return {
      kind: "required_action",
      notificationId: toPositiveInt(notification?.id),
      label: t("mailbox.actions.respond_to_host_role_request", "Respond to request"),
    };
  }

  if (MAILBOX_INFO_ONLY_ACTION_KINDS.has(actionKind)) {
    return null;
  }

  if (sender === "tasks" || actionKind === "task_completed") {
    return {
      to: "/vault/main?open_tasks=1",
      label: t("mailbox.actions.open_tasks", "Open tasks"),
    };
  }

  const fallbackEventId = toPositiveInt(meta?.event_id ?? meta?.eventId);
  const fallbackClubId = toPositiveInt(meta?.club_id ?? meta?.clubId);

  const urlCandidates = [
    meta?.action_url,
    meta?.actionUrl,
    meta?.open_url,
    meta?.openUrl,
    meta?.event_url,
    meta?.eventUrl,
    meta?.url,
    meta?.link,
    ...extractUrlsFromText(notification?.message),
    ...extractUrlsFromText(notification?.title),
  ]
    .filter(Boolean)
    .sort((a, b) => scoreUrlCandidate(b) - scoreUrlCandidate(a));

  for (const rawUrl of urlCandidates) {
    const action = buildActionFromUrl({
      rawUrl,
      fallbackEventId,
      actionKind,
      sender,
      t,
    });
    if (action?.to) return action;
  }

  if (actionKind === "club_join_request" && fallbackClubId) {
    return {
      to: `/clubs/${fallbackClubId}/manage/requests`,
      label: t("mailbox.actions.open_club_requests", "Open join requests"),
    };
  }

  if (actionKind === "club_creator_invitation" && fallbackClubId) {
    return {
      to: `/clubs/${fallbackClubId}/manage/ownership`,
      label: t("mailbox.actions.respond_to_club_creator_invitation", "Respond to invitation"),
    };
  }

  if (actionKind === "concept_date_poll" && fallbackEventId) {
    return buildEventSearchDeepLink({
      eventId: fallbackEventId,
      sourceParams: new URLSearchParams("concept_poll=1"),
      actionKind,
      t,
    });
  }

  if (fallbackEventId) {
    return buildEventSearchDeepLink({
      eventId: fallbackEventId,
      sourceParams: new URLSearchParams(),
      actionKind,
      t,
    });
  }

  return null;
};

const isPendingHostRoleRequestNotification = (notification) => {
  const meta = normalizeNotificationMeta(notification?.meta);
  const actionKind = String(meta?.action_kind || meta?.actionKind || "").trim().toLowerCase();
  const requestStatus = String(
    meta?.request_status || meta?.requestStatus || "pending"
  ).trim().toLowerCase();
  return HOST_ROLE_ACTION_KINDS.has(actionKind) && requestStatus === "pending";
};

const getNotificationDisplayMessage = (notification, t) => {
  const meta = normalizeNotificationMeta(notification?.meta);
  const actionKind = String(meta?.action_kind || meta?.actionKind || "").trim().toLowerCase();
  const requestStatus = String(
    meta?.request_status || meta?.requestStatus || "pending"
  ).trim().toLowerCase();
  if (HOST_ROLE_ACTION_KINDS.has(actionKind) && requestStatus === "pending") {
    const hostCode = String(meta?.requester_code || meta?.requesterCode || "").trim();
    const eventName = String(meta?.event_name || meta?.eventName || "").trim();
    return t(
      actionKind === "event_cohost_request"
        ? "mailbox.host_role_request.co_host_invite"
        : "mailbox.host_role_request.host_transfer",
      {
        host: hostCode || t("mailbox.host_role_request.host_fallback", "Host"),
        event: eventName || t("mailbox.host_role_request.event_fallback", "event"),
      }
    );
  }
  const message = notification?.message || "";
  if (/^(?:Skvelá správa — vaše podujatie|Skvělá zpráva — vaše akce|Great news — your event|Отличные новости — ваше событие)/u.test(message)) {
    return message.replace(/\n{2,}(?:Tím VaultGrid|Tým VaultGrid|VaultGrid Team|Команда VaultGrid)\s*$/u, "");
  }
  return message;
};

const getNotificationLanguage = (notification, interfaceLanguage) => {
  const explicit = String(normalizeNotificationMeta(notification?.meta)?.language || "").toLowerCase().slice(0, 2);
  if (["sk", "cs", "en", "ru"].includes(explicit)) return explicit;
  const message = String(notification?.message || "");
  if (message.startsWith("Skvelá správa — vaše podujatie")) return "sk";
  if (message.startsWith("Skvělá zpráva — vaše akce")) return "cs";
  if (message.startsWith("Great news — your event")) return "en";
  if (message.startsWith("Отличные новости — ваше событие")) return "ru";
  return interfaceLanguage;
};


const DatePill = ({ label, className = "" }) => {
  if (!label) return null;
  return (
    <div className={`flex justify-center will-change-opacity ${className}`}>
      <div className="flex h-7 w-fit items-center justify-center rounded-full border border-white/18 bg-white/[0.12] px-3 shadow-[0_8px_18px_rgba(7,16,44,0.12)] backdrop-blur-[14px]">
        <span className="text-center text-[13px] font-semibold leading-none text-white/88 whitespace-nowrap">
          {label}
        </span>
      </div>
    </div>
  );
};

const MAILBOX_FILTER_STICKY_STACK_PX = 58;

function SkeletonBlock({ className = "", dark = false }) {
  return (
    <span
      className={`vg-chat-loading-shimmer block rounded-full ${
        dark ? "bg-black/10" : "bg-white/16"
      } ${className}`}
      aria-hidden="true"
    />
  );
}

function MailboxListLoadingSkeleton({ rows = 6, compact = false }) {
  return (
    <div className={compact ? "py-2" : "pt-1"} aria-hidden="true">
      {!compact ? (
        <div className="mb-3 flex justify-center">
          <SkeletonBlock className="h-7 w-[96px] bg-white/12" />
        </div>
      ) : null}
      {Array.from({ length: rows }).map((_, index) => (
        <div
          key={`mailbox-list-skeleton-${index}`}
          className={`vg-chat-loading-row ${index > 0 ? "mt-2" : ""}`}
          style={{ animationDelay: `${index * 70}ms` }}
        >
          <div className="flex w-full items-center rounded-[22px] border border-white/10 bg-white/[0.06] px-4 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] backdrop-blur-[14px]">
            <SkeletonBlock className="mr-3 h-2.5 w-2.5 shrink-0 bg-white/20" />
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-center gap-2">
                <SkeletonBlock className="h-[13px] w-[42px] bg-white/18" />
                <SkeletonBlock className="h-[17px] w-[54%] bg-white/22" />
              </div>
              <SkeletonBlock className="mt-[9px] h-[12px] w-[82%] bg-white/12" />
            </div>
            <SkeletonBlock className="ml-3 h-[18px] w-[12px] shrink-0 bg-white/10" />
          </div>
        </div>
      ))}
    </div>
  );
}

const RoundAssignmentWrapper = ({
  children,
  showMessage = true,
  message = "",
  emptyMessage,
}) => (
  <div className="mt-4 flex flex-col items-center text-center">
    {children}
    {showMessage && (
      <div className="mt-4 text-xs text-white/70 whitespace-pre-wrap break-words max-w-full">
        {String(message || "").trim() ? (
          <Linkify options={linkifyOptions}>{message}</Linkify>
        ) : (
          emptyMessage
        )}
      </div>
    )}
  </div>
);

// ────────── Round Assignment Notification Content ──────────
export const RoundAssignmentContent = ({
  notification,
  onNotificationUpdate,
  hideEmbeddedRules = false,
}) => {
  const { t, i18n } = useTranslation("menu");
  const interfaceLanguage = String(i18n?.resolvedLanguage || i18n?.language || "").toLowerCase();
  const isSlovakInterface = interfaceLanguage.startsWith("sk");
  const pickLocalizedText = (en, sk, fallback = "") => (
    isSlovakInterface
      ? (String(sk || "").trim() || String(en || "").trim() || fallback)
      : (String(en || "").trim() || String(sk || "").trim() || fallback)
  );

  const [nextLoading, setNextLoading] = useState(false);
  const [nextError, setNextError] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [answerValue, setAnswerValue] = useState("");
  const [answerParts, setAnswerParts] = useState(["", "", ""]);
  const [commonThreadAnswers, setCommonThreadAnswers] = useState([]);
  const [commonThreadCategoryValue, setCommonThreadCategoryValue] = useState("");
  const [mixedSignalsSolvedOverrideRoundId, setMixedSignalsSolvedOverrideRoundId] = useState(null);
  const [spyGuessValue, setSpyGuessValue] = useState("");
  const [spyVoteTargetUserId, setSpyVoteTargetUserId] = useState(null);
  const [spyGuessFormOpen, setSpyGuessFormOpen] = useState(false);
  const [glyphRelaySelected, setGlyphRelaySelected] = useState("");
  const [glyphRelayRevealPressed, setGlyphRelayRevealPressed] = useState(false);
  const [revealLoading, setRevealLoading] = useState(false);
  const [revealError, setRevealError] = useState("");
  const [submitCooldownLeft, setSubmitCooldownLeft] = useState(0);
  const [closestGuessSecondsLeft, setClosestGuessSecondsLeft] = useState(0);
  const [photoYearSecondsLeft, setPhotoYearSecondsLeft] = useState(0);
  const [photoYearClientPhase, setPhotoYearClientPhase] = useState("");
  const [photoYearRevealRequested, setPhotoYearRevealRequested] = useState(false);
  const [conversationCardsSecondsLeft, setConversationCardsSecondsLeft] = useState(0);
  const [lastAnswerSecondsLeft, setLastAnswerSecondsLeft] = useState(0);
  const [lastAnswerTickRequested, setLastAnswerTickRequested] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [qnaMissionVisible, setQnaMissionVisible] = useState(false);
  const [characterFactsVisible, setCharacterFactsVisible] = useState(true);
  const [questionWordVisibleIndex, setQuestionWordVisibleIndex] = useState(null);
  const qnaMissionHideTimer = useRef(null);
  const characterFactsHideTimer = useRef(null);
  const questionWordHideTimer = useRef(null);

  useEffect(() => {
    setNextLoading(false);
    setNextError("");
    setSubmitLoading(false);
    setSubmitError("");
    setRulesOpen(false);
    setMisleadAnswerVisible(false);
    setSpyGuessValue("");
    setSpyVoteTargetUserId(null);
    setSpyGuessFormOpen(false);
    setGlyphRelaySelected("");
    setGlyphRelayRevealPressed(false);
    setRevealLoading(false);
    setRevealError("");
    setCommonThreadAnswers([]);
    setCommonThreadCategoryValue("");
    setMixedSignalsSolvedOverrideRoundId(null);
    setPhotoYearSecondsLeft(0);
    setPhotoYearRevealRequested(false);
    setConversationCardsSecondsLeft(0);
    setLastAnswerSecondsLeft(0);
    setLastAnswerTickRequested(false);
    setQnaMissionVisible(false);
    setCharacterFactsVisible(true);
    setQuestionWordVisibleIndex(null);
    window.clearTimeout(qnaMissionHideTimer.current);
    window.clearTimeout(characterFactsHideTimer.current);
    window.clearTimeout(questionWordHideTimer.current);
  }, [notification?.id]);

  useEffect(() => () => {
    window.clearTimeout(qnaMissionHideTimer.current);
    window.clearTimeout(characterFactsHideTimer.current);
    window.clearTimeout(questionWordHideTimer.current);
  }, []);

  const meta = normalizeNotificationMeta(notification?.meta);
  const mode = meta.mode || "table";
  const tableNumber = meta.table_number ?? meta.tableNumber;
  const isSpy = Boolean(meta.is_spy ?? meta.isSpy);
  const startsFirst = Boolean(meta.starts_first ?? meta.startsFirst);
  const word = meta.word;
  const spyCategory = String(meta.spy_category ?? meta.spyCategory ?? "").trim();
  const spyCategorySk = String(meta.spy_category_sk ?? meta.spyCategorySk ?? "").trim();
  const spyClueRound = Number(meta.spy_clue_round ?? meta.spyClueRound ?? 1);
  const spyVotingOpen = Boolean(meta.spy_voting_open ?? meta.spyVotingOpen);
  const spyVotesRaw = normalizeNotificationMeta(meta.spy_votes ?? meta.spyVotes);
  const spyVotes = Object.entries(spyVotesRaw || {}).reduce((acc, [voterUid, targetUid]) => {
    const voter = Number(voterUid);
    const target = Number(targetUid);
    if (Number.isFinite(voter) && Number.isFinite(target) && voter > 0 && target > 0) {
      acc[String(voter)] = target;
    }
    return acc;
  }, {});
  const spyMyVoteTargetUserId = Number(
    meta.spy_my_vote_target_user_id ?? meta.spyMyVoteTargetUserId ?? 0
  );
  const spyHasVoted = Boolean(meta.spy_has_voted ?? meta.spyHasVoted);
  const spyRevealedUserId = Number(
    meta.spy_revealed_user_id ?? meta.spyRevealedUserId ?? 0
  );
  const spyRevealStatus = String(meta.spy_reveal_status ?? meta.spyRevealStatus ?? "").trim();
  const spyLastChanceOpen = Boolean(
    meta.spy_last_chance_open ?? meta.spyLastChanceOpen
  );
  const spyIsEliminated = Boolean(
    meta.spy_is_eliminated ?? meta.spyIsEliminated
  );
  const spyRoundSolved = Boolean(meta.spy_round_solved ?? meta.spyRoundSolved);
  const spyCanStartNext = Boolean(meta.spy_can_start_next ?? meta.spyCanStartNext);
  const spyWinner = String(meta.spy_winner ?? meta.spyWinner ?? "").trim();
  const spySolutionTitle = String(
    meta.spy_solution_title ?? meta.spySolutionTitle ?? ""
  ).trim();
  const spyCanStartVote = Boolean(
    meta.spy_can_start_vote
    ?? meta.spyCanStartVote
    ?? (
      startsFirst
      && !spyIsEliminated
      && !spyVotingOpen
      && !spyLastChanceOpen
      && !spyRoundSolved
      && !spyRevealStatus
    )
  );
  const spyCanVote = Boolean(meta.spy_can_vote ?? meta.spyCanVote);
  const spyCanContinue = Boolean(meta.spy_can_continue ?? meta.spyCanContinue);
  const spyCanSubmitFinalGuess = Boolean(
    meta.spy_can_submit_final_guess ?? meta.spyCanSubmitFinalGuess
  );
  const spyGuessSubmittedText = String(
    meta.spy_guess_submitted_text ?? meta.spyGuessSubmittedText ?? ""
  ).trim();
  const spyGuessCorrectRaw = meta.spy_guess_correct ?? meta.spyGuessCorrect;
  const hasSpyGuessResult = typeof spyGuessCorrectRaw === "boolean";
  const spyParticipantsRaw = meta.spy_participants ?? meta.spyParticipants ?? [];
  const spyParticipants = Array.isArray(spyParticipantsRaw)
    ? spyParticipantsRaw
        .map((item) => {
          if (!item || typeof item !== "object") return null;
          const userId = Number(item.user_id ?? item.userId ?? 0);
          if (!Number.isFinite(userId) || userId <= 0) return null;
          return {
            userId,
            name: String(item.name || "").trim() || String(item.code || "").trim() || `#${userId}`,
            code: String(item.code || "").trim(),
            avatarUrl: toAbsoluteMediaUrl(item.avatar_url ?? item.avatarUrl ?? ""),
          };
        })
        .filter(Boolean)
    : [];
  const subjectCode = meta.subject_code ?? meta.subjectCode;
  const subjectPhotoUrl = toAbsoluteMediaUrl(
    meta.subject_photo_url ??
      meta.subjectPhotoUrl ??
      meta.subject_avatar_custom_url ??
      meta.subjectAvatarCustomUrl
  );
  const starterCode = meta.starter_code ?? meta.starterCode;
  const gameNumber = meta.game_number ?? meta.gameNumber;
  const badgeUrl = meta.badge_url ?? meta.badgeUrl;
  const wrongAnswerActiveQuestion = normalizeNotificationMeta(
    meta.wrong_answer_active_question ?? meta.wrongAnswerActiveQuestion
  );
  const aliasActiveWord = normalizeNotificationMeta(
    meta.alias_active_word ?? meta.aliasActiveWord
  );
  const filmEmojiEmojisRaw = meta.film_emoji_emojis ?? meta.filmEmojiEmojis ?? [];
  const filmEmojiEmojis = Array.isArray(filmEmojiEmojisRaw)
    ? filmEmojiEmojisRaw.map((item) => String(item || "").trim()).filter(Boolean)
    : [];
  const filmEmojiAllEmojisCount = Number(
    meta.film_emoji_all_emojis_count ?? meta.filmEmojiAllEmojisCount ?? 0
  );
  const filmEmojiRoundSolved = Boolean(meta.film_emoji_round_solved ?? meta.filmEmojiRoundSolved);
  const filmEmojiCanStartNext = Boolean(meta.film_emoji_can_start_next ?? meta.filmEmojiCanStartNext);
  const filmEmojiSolutionTitle = String(
    meta.film_emoji_solution_title ?? meta.filmEmojiSolutionTitle ?? ""
  ).trim();
  const filmEmojiSubmissionCorrectRaw =
    meta.film_emoji_submission_correct ?? meta.filmEmojiSubmissionCorrect;
  const hasFilmEmojiSubmissionResult = typeof filmEmojiSubmissionCorrectRaw === "boolean";
  const filmEmojiSubmittedText = String(
    meta.film_emoji_submitted_text ?? meta.filmEmojiSubmittedText ?? ""
  ).trim();
  const filmEmojiRetryAfterSeconds = Number(
    meta.film_emoji_retry_after_seconds ?? meta.filmEmojiRetryAfterSeconds ?? 0
  );
  const characterFactsItemsRaw = meta.character_facts_items ?? meta.characterFactsItems ?? [];
  const characterFactsItems = Array.isArray(characterFactsItemsRaw)
    ? characterFactsItemsRaw
        .map((item) => {
          if (!item || typeof item !== "object") return null;
          const en = String(item.en || "").trim();
          if (!en) return null;
          return { en };
        })
        .filter(Boolean)
    : [];
  const characterFactsTotalCount = Number(
    meta.character_facts_total_count ?? meta.characterFactsTotalCount ?? 0
  );
  const characterFactsRoundSolved = Boolean(
    meta.character_facts_round_solved ?? meta.characterFactsRoundSolved
  );
  const characterFactsCanStartNext = Boolean(
    meta.character_facts_can_start_next ?? meta.characterFactsCanStartNext
  );
  const characterFactsSolutionTitle = String(
    meta.character_facts_solution_title ?? meta.characterFactsSolutionTitle ?? ""
  ).trim();
  const characterFactsSubmissionCorrectRaw =
    meta.character_facts_submission_correct ?? meta.characterFactsSubmissionCorrect;
  const hasCharacterFactsSubmissionResult =
    typeof characterFactsSubmissionCorrectRaw === "boolean";
  const characterFactsSubmittedText = String(
    meta.character_facts_submitted_text ?? meta.characterFactsSubmittedText ?? ""
  ).trim();
  const characterFactsRetryAfterSeconds = Number(
    meta.character_facts_retry_after_seconds ?? meta.characterFactsRetryAfterSeconds ?? 0
  );
  const questionWordsItemsRaw = meta.question_words_items ?? meta.questionWordsItems ?? [];
  const questionWordsItems = Array.isArray(questionWordsItemsRaw)
    ? questionWordsItemsRaw
        .map((item) => {
          if (!item || typeof item !== "object") return null;
          const en = String(item.en || "").trim();
          const sk = String(item.sk || "").trim();
          if (!en && !sk) return null;
          return { en, sk };
        })
        .filter(Boolean)
    : [];
  const questionWordsTotalCount = Number(
    meta.question_words_total_count ?? meta.questionWordsTotalCount ?? 0
  );
  const questionWordsRoundSolved = Boolean(
    meta.question_words_round_solved ?? meta.questionWordsRoundSolved
  );
  const questionWordsCanStartNext = Boolean(
    meta.question_words_can_start_next ?? meta.questionWordsCanStartNext
  );
  const questionWordsSolutionTitle = String(
    meta.question_words_solution_title ?? meta.questionWordsSolutionTitle ?? ""
  ).trim();
  const questionWordsSolutionTitleEnglish = questionWordsSolutionTitle.split("/")[0].trim();
  const questionWordsSubmissionCorrectRaw =
    meta.question_words_submission_correct ?? meta.questionWordsSubmissionCorrect;
  const hasQuestionWordsSubmissionResult =
    typeof questionWordsSubmissionCorrectRaw === "boolean";
  const questionWordsSubmittedText = String(
    meta.question_words_submitted_text ?? meta.questionWordsSubmittedText ?? ""
  ).trim();
  const questionWordsRetryAfterSeconds = Number(
    meta.question_words_retry_after_seconds ?? meta.questionWordsRetryAfterSeconds ?? 0
  );
  const linePuzzleTileDataUrl = String(
    meta.line_puzzle_tile_data_url ?? meta.linePuzzleTileDataUrl ?? ""
  ).trim();
  const linePuzzleTileIndex = Number(
    meta.line_puzzle_tile_index ?? meta.linePuzzleTileIndex ?? 0
  );
  const linePuzzleTilesTotal = Number(
    meta.line_puzzle_tiles_total ?? meta.linePuzzleTilesTotal ?? 6
  );
  const linePuzzleTableHasDistractor = Boolean(
    meta.line_puzzle_table_has_distractor ?? meta.linePuzzleTableHasDistractor
  );
  const linePuzzleRoundSolved = Boolean(
    meta.line_puzzle_round_solved ?? meta.linePuzzleRoundSolved
  );
  const linePuzzleCanStartNext = Boolean(
    meta.line_puzzle_can_start_next ?? meta.linePuzzleCanStartNext
  );
  const linePuzzleSolutionTitle = String(
    meta.line_puzzle_solution_title ?? meta.linePuzzleSolutionTitle ?? ""
  ).trim();
  const linePuzzleSubmissionCorrectRaw =
    meta.line_puzzle_submission_correct ?? meta.linePuzzleSubmissionCorrect;
  const hasLinePuzzleSubmissionResult =
    typeof linePuzzleSubmissionCorrectRaw === "boolean";
  const linePuzzleSubmittedText = String(
    meta.line_puzzle_submitted_text ?? meta.linePuzzleSubmittedText ?? ""
  ).trim();
  const linePuzzleRetryAfterSeconds = Number(
    meta.line_puzzle_retry_after_seconds ?? meta.linePuzzleRetryAfterSeconds ?? 0
  );
  const answerRevealed = Boolean(meta.answer_revealed ?? meta.answerRevealed);
  const answerRevealVotesCount = Number(
    meta.answer_reveal_votes_count ?? meta.answerRevealVotesCount ?? 0
  );
  const answerRevealVotesRequired = Number(
    meta.answer_reveal_votes_required ?? meta.answerRevealVotesRequired ?? 0
  );
  const answerRevealVoted = Boolean(meta.answer_reveal_voted ?? meta.answerRevealVoted);
  const mixedSignalsRole = String(
    meta.mixed_signals_role ?? meta.mixedSignalsRole ?? ""
  ).trim();
  const mixedSignalsWordsRaw = meta.mixed_signals_words ?? meta.mixedSignalsWords ?? [];
  const mixedSignalsWords = Array.isArray(mixedSignalsWordsRaw)
    ? mixedSignalsWordsRaw
        .map((item) => {
          if (!item || typeof item !== "object") return null;
          const en = String(item.en || "").trim();
          const sk = String(item.sk || "").trim();
          if (!en && !sk) return null;
          return { en, sk };
        })
        .filter(Boolean)
    : [];
  const mixedSignalsRoundSolved = Boolean(
    meta.mixed_signals_round_solved ?? meta.mixedSignalsRoundSolved
  );
  const mixedSignalsCanStartNext = Boolean(
    meta.mixed_signals_can_start_next ?? meta.mixedSignalsCanStartNext
  );
  const mixedSignalsSolutionTitle = String(
    meta.mixed_signals_solution_title ?? meta.mixedSignalsSolutionTitle ?? ""
  ).trim();
  const mixedSignalsSubmissionCorrectRaw =
    meta.mixed_signals_submission_correct ?? meta.mixedSignalsSubmissionCorrect;
  const hasMixedSignalsSubmissionResult =
    typeof mixedSignalsSubmissionCorrectRaw === "boolean";
  const mixedSignalsSubmittedWordsRaw =
    meta.mixed_signals_submitted_words ?? meta.mixedSignalsSubmittedWords ?? [];
  const mixedSignalsSubmittedWords = Array.isArray(mixedSignalsSubmittedWordsRaw)
    ? mixedSignalsSubmittedWordsRaw.map((item) => String(item || "").trim()).slice(0, 3)
    : [];
  const mixedSignalsSubmittedWordsKey = mixedSignalsSubmittedWords.join("\u0001");
  const mixedSignalsSubmittedMask = [0, 1, 2].map(
    (idx) => String(mixedSignalsSubmittedWords[idx] || "").trim().length > 0
  );
  const mixedSignalsWordResultsRaw =
    meta.mixed_signals_word_results ?? meta.mixedSignalsWordResults ?? [];
  const mixedSignalsWordResults = Array.isArray(mixedSignalsWordResultsRaw)
    ? mixedSignalsWordResultsRaw.map((item) => Boolean(item)).slice(0, 3)
    : [];
  const mixedSignalsCorrectSubmittedCount = mixedSignalsWordResults.reduce(
    (count, isCorrect, idx) => count + (mixedSignalsSubmittedMask[idx] && isCorrect ? 1 : 0),
    0
  );
  const mixedSignalsRetryAfterSeconds = Number(
    meta.mixed_signals_retry_after_seconds ?? meta.mixedSignalsRetryAfterSeconds ?? 0
  );
  const mixedSignalsRoundId = String(
    meta.quiz_run_round_id ?? meta.quizRunRoundId ?? ""
  ).trim();
  const effectiveMixedSignalsRoundSolved =
    mixedSignalsRoundSolved ||
    (Boolean(mixedSignalsRoundId) && mixedSignalsSolvedOverrideRoundId === mixedSignalsRoundId);
  const commonThreadRole = String(
    meta.common_thread_role ?? meta.commonThreadRole ?? ""
  ).trim();
  const commonThreadWordRaw = meta.common_thread_word ?? meta.commonThreadWord ?? null;
  const commonThreadWord =
    commonThreadWordRaw && typeof commonThreadWordRaw === "object"
      ? {
          en: String(commonThreadWordRaw.en || "").trim(),
          sk: String(commonThreadWordRaw.sk || "").trim(),
        }
      : null;
  const commonThreadWordsTotal = Number(
    meta.common_thread_words_total ?? meta.commonThreadWordsTotal ?? 0
  );
  const commonThreadPhase = String(
    meta.common_thread_phase ?? meta.commonThreadPhase ?? "words"
  ).trim();
  const commonThreadWordGuessesRaw =
    meta.common_thread_word_guesses ?? meta.commonThreadWordGuesses ?? [];
  const commonThreadWordGuesses = Array.isArray(commonThreadWordGuessesRaw)
    ? commonThreadWordGuessesRaw.map((item) => String(item || "").trim())
    : [];
  const commonThreadWordResultsRaw =
    meta.common_thread_word_results ?? meta.commonThreadWordResults ?? [];
  const commonThreadWordResults = Array.isArray(commonThreadWordResultsRaw)
    ? commonThreadWordResultsRaw.map((item) => Boolean(item))
    : [];
  const commonThreadCategoryGuess = String(
    meta.common_thread_category_guess ?? meta.commonThreadCategoryGuess ?? ""
  ).trim();
  const commonThreadCategoryCorrectRaw =
    meta.common_thread_category_correct ?? meta.commonThreadCategoryCorrect;
  const hasCommonThreadCategoryResult = typeof commonThreadCategoryCorrectRaw === "boolean";
  const commonThreadRoundSolved = Boolean(
    meta.common_thread_round_solved ?? meta.commonThreadRoundSolved
  );
  const commonThreadCanStartNext = Boolean(
    meta.common_thread_can_start_next ?? meta.commonThreadCanStartNext
  );
  const commonThreadSolutionCategoryEn = String(
    meta.common_thread_solution_category_en ?? meta.commonThreadSolutionCategoryEn ?? ""
  ).trim();
  const commonThreadSolutionCategorySk = String(
    meta.common_thread_solution_category_sk ?? meta.commonThreadSolutionCategorySk ?? ""
  ).trim();
  const commonThreadSolutionWordsRaw =
    meta.common_thread_solution_words ?? meta.commonThreadSolutionWords ?? [];
  const commonThreadSolutionWords = Array.isArray(commonThreadSolutionWordsRaw)
    ? commonThreadSolutionWordsRaw
        .map((item) => {
          if (!item || typeof item !== "object") return null;
          const en = String(item.en || "").trim();
          const sk = String(item.sk || "").trim();
          if (!en && !sk) return null;
          return { en, sk };
        })
        .filter(Boolean)
    : [];
  const glyphRelayRole = String(meta.glyph_relay_role ?? meta.glyphRelayRole ?? "").trim();
  const glyphRelayOptionsRaw = meta.glyph_relay_options ?? meta.glyphRelayOptions ?? [];
  const glyphRelayOptions = Array.isArray(glyphRelayOptionsRaw)
    ? glyphRelayOptionsRaw
        .map((item) => {
          if (!item || typeof item !== "object") return null;
          const id = String(item.id || "").trim();
          const label = String(item.label || "").trim();
          const imageUrl = toAbsoluteMediaUrl(item.image_url ?? item.imageUrl ?? "");
          if (!id || !imageUrl) return null;
          return { id, label: label || id, imageUrl };
        })
        .filter(Boolean)
    : [];
  const glyphRelayFragmentsRaw = meta.glyph_relay_fragments ?? meta.glyphRelayFragments ?? [];
  const glyphRelayFragments = Array.isArray(glyphRelayFragmentsRaw)
    ? glyphRelayFragmentsRaw
        .map((item) => {
          if (!item || typeof item !== "object") return null;
          const id = String(item.id || "").trim();
          const step = Number(item.step || 0);
          const imageUrl = toAbsoluteMediaUrl(item.image_url ?? item.imageUrl ?? "");
          if (!id || !imageUrl || !Number.isFinite(step) || step <= 0) return null;
          return { id, step, imageUrl };
        })
        .filter(Boolean)
    : [];
  const glyphRelayCurrentFragmentRaw =
    meta.glyph_relay_current_fragment ?? meta.glyphRelayCurrentFragment ?? null;
  const glyphRelayCurrentFragment =
    glyphRelayCurrentFragmentRaw && typeof glyphRelayCurrentFragmentRaw === "object"
      ? {
          id: String(glyphRelayCurrentFragmentRaw.id || "").trim(),
          step: Number(glyphRelayCurrentFragmentRaw.step || 0),
          imageUrl: toAbsoluteMediaUrl(
            glyphRelayCurrentFragmentRaw.image_url ?? glyphRelayCurrentFragmentRaw.imageUrl ?? ""
          ),
        }
      : null;
  const glyphRelayFragmentCount = Number(
    meta.glyph_relay_fragment_count ?? meta.glyphRelayFragmentCount ?? glyphRelayFragments.length
  );
  const glyphRelayCurrentFragmentIndex = Number(
    meta.glyph_relay_current_fragment_index ?? meta.glyphRelayCurrentFragmentIndex ?? 0
  );
  const glyphRelayPhase = String(meta.glyph_relay_phase ?? meta.glyphRelayPhase ?? "explain").trim();
  const glyphRelayRoundSolved = Boolean(meta.glyph_relay_round_solved ?? meta.glyphRelayRoundSolved);
  const glyphRelayCanStartNext = Boolean(meta.glyph_relay_can_start_next ?? meta.glyphRelayCanStartNext);
  const glyphRelaySolutionTitle = String(
    meta.glyph_relay_solution_title ?? meta.glyphRelaySolutionTitle ?? ""
  ).trim();
  const glyphRelaySolutionOptionRaw =
    meta.glyph_relay_solution_option ?? meta.glyphRelaySolutionOption ?? null;
  const glyphRelaySolutionOption =
    glyphRelaySolutionOptionRaw && typeof glyphRelaySolutionOptionRaw === "object"
      ? {
          id: String(glyphRelaySolutionOptionRaw.id || "").trim(),
          label: String(glyphRelaySolutionOptionRaw.label || "").trim(),
          imageUrl: toAbsoluteMediaUrl(
            glyphRelaySolutionOptionRaw.image_url ?? glyphRelaySolutionOptionRaw.imageUrl ?? ""
          ),
        }
      : null;
  const glyphRelaySelectedOptionId = String(
    meta.glyph_relay_selected_option_id ?? meta.glyphRelaySelectedOptionId ?? ""
  ).trim();
  const glyphRelaySubmissionCorrectRaw =
    meta.glyph_relay_submission_correct ?? meta.glyphRelaySubmissionCorrect;
  const hasGlyphRelaySubmissionResult = typeof glyphRelaySubmissionCorrectRaw === "boolean";
  const closestGuessPhase = String(
    meta.closest_guess_phase ?? meta.closestGuessPhase ?? "waiting"
  ).trim();
  const closestGuessQuestionTitle = String(
    meta.closest_guess_question_title ?? meta.closestGuessQuestionTitle ?? ""
  ).trim();
  const closestGuessQuestionTitleEn = String(
    meta.closest_guess_question_title_en ?? meta.closestGuessQuestionTitleEn ?? closestGuessQuestionTitle
  ).trim();
  const closestGuessQuestionTitleSk = String(
    meta.closest_guess_question_title_sk ?? meta.closestGuessQuestionTitleSk ?? closestGuessQuestionTitleEn
  ).trim();
  const closestGuessPromptEn = String(
    meta.closest_guess_prompt_en ?? meta.closestGuessPromptEn ?? ""
  ).trim();
  const closestGuessPromptSk = String(
    meta.closest_guess_prompt_sk ?? meta.closestGuessPromptSk ?? ""
  ).trim();
  const closestGuessImageUrl = toAbsoluteMediaUrl(
    meta.closest_guess_image_url ?? meta.closestGuessImageUrl ?? ""
  );
  const closestGuessCorrectText = String(
    meta.closest_guess_correct_text ?? meta.closestGuessCorrectText ?? ""
  ).trim();
  const closestGuessUnitEn = String(
    meta.closest_guess_unit_en ?? meta.closestGuessUnitEn ?? ""
  ).trim();
  const closestGuessUnitSk = String(
    meta.closest_guess_unit_sk ?? meta.closestGuessUnitSk ?? ""
  ).trim();
  const closestGuessValueDecimals = Number(
    meta.closest_guess_value_decimals ?? meta.closestGuessValueDecimals ?? 0
  );
  const closestGuessDurationSeconds = Number(
    meta.closest_guess_duration_seconds ?? meta.closestGuessDurationSeconds ?? 90
  );
  const closestGuessDeadlineAt = String(
    meta.closest_guess_deadline_at ?? meta.closestGuessDeadlineAt ?? ""
  ).trim();
  const closestGuessMyAnswer = String(
    meta.closest_guess_my_answer ?? meta.closestGuessMyAnswer ?? ""
  ).trim();
  const closestGuessTableResultsRaw = normalizeNotificationMeta(
    meta.closest_guess_table_results ?? meta.closestGuessTableResults
  );
  const closestGuessTableResults = Object.entries(closestGuessTableResultsRaw || {}).reduce(
    (acc, [tableNo, raw]) => {
      const tableNumber = Number(tableNo);
      if (!Number.isFinite(tableNumber) || tableNumber <= 0 || !raw || typeof raw !== "object") return acc;
      acc[String(tableNumber)] = {
        count: Number(raw.count || 0),
        averageText: String(raw.average_text || raw.averageText || "").trim(),
        diffValue: Number(raw.diff_value ?? raw.diffValue),
      };
      return acc;
    },
    {}
  );
  const closestGuessWinnerTablesRaw = meta.closest_guess_winner_tables ?? meta.closestGuessWinnerTables ?? [];
  const closestGuessWinnerTables = Array.isArray(closestGuessWinnerTablesRaw)
    ? closestGuessWinnerTablesRaw
        .map((item) => Number(item))
        .filter((item) => Number.isFinite(item) && item > 0)
    : [];
  const closestGuessScoreboardRaw = normalizeNotificationMeta(
    meta.closest_guess_scoreboard ?? meta.closestGuessScoreboard
  );
  const closestGuessScoreboard = Object.entries(closestGuessScoreboardRaw || {}).reduce((acc, [tableNo, score]) => {
    const tableNumber = Number(tableNo);
    if (Number.isFinite(tableNumber) && tableNumber > 0) {
      acc[String(tableNumber)] = Number(score || 0);
    }
    return acc;
  }, {});
  const photoYearPhase = String(
    meta.photo_year_phase ?? meta.photoYearPhase ?? "discussion"
  ).trim();
  const photoYearTitle = String(
    meta.photo_year_title ?? meta.photoYearTitle ?? ""
  ).trim();
  const photoYearImageUrl = toAbsoluteMediaUrl(
    meta.photo_year_image_url ?? meta.photoYearImageUrl ?? ""
  );
  const photoYearSourceUrl = String(
    meta.photo_year_source_url ?? meta.photoYearSourceUrl ?? ""
  ).trim();
  const photoYearCredit = String(
    meta.photo_year_credit ?? meta.photoYearCredit ?? ""
  ).trim();
  const photoYearCorrectYear = Number(
    meta.photo_year_correct_year ?? meta.photoYearCorrectYear ?? 0
  );
  const photoYearStartedAt = String(
    meta.photo_year_started_at ?? meta.photoYearStartedAt ?? ""
  ).trim();
  const photoYearDiscussionSeconds = Number(
    meta.photo_year_discussion_seconds ?? meta.photoYearDiscussionSeconds ?? 60
  );
  const photoYearAnswerSeconds = Number(
    meta.photo_year_answer_seconds ?? meta.photoYearAnswerSeconds ?? 120
  );
  const photoYearDiscussionEndsAt = String(
    meta.photo_year_discussion_ends_at ?? meta.photoYearDiscussionEndsAt ?? ""
  ).trim();
  const photoYearDeadlineAt = String(
    meta.photo_year_deadline_at ?? meta.photoYearDeadlineAt ?? ""
  ).trim();
  const photoYearMyAnswer = String(
    meta.photo_year_my_answer ?? meta.photoYearMyAnswer ?? ""
  ).trim();
  const photoYearMyAnswerValue = Number(
    meta.photo_year_my_answer_value ?? meta.photoYearMyAnswerValue ?? 0
  );
  const photoYearAnswersCount = Number(
    meta.photo_year_answers_count ?? meta.photoYearAnswersCount ?? 0
  );
  const photoYearParticipantsCount = Number(
    meta.photo_year_participants_count ?? meta.photoYearParticipantsCount ?? 0
  );
  const photoYearResultsRaw = meta.photo_year_results ?? meta.photoYearResults ?? [];
  const photoYearResults = Array.isArray(photoYearResultsRaw)
    ? photoYearResultsRaw
        .map((item) => {
          if (!item || typeof item !== "object") return null;
          const userId = Number(item.user_id ?? item.userId ?? 0);
          const year = Number(item.year ?? 0);
          const diff = Number(item.diff ?? 0);
          if (!Number.isFinite(userId) || userId <= 0 || !Number.isFinite(year) || year <= 0) return null;
          return {
            userId,
            name: String(item.name || "Player").trim() || "Player",
            year,
            diff: Number.isFinite(diff) && diff >= 0 ? diff : null,
            winner: Boolean(item.winner),
          };
        })
        .filter(Boolean)
    : [];
  const photoYearWinnerUserIdsRaw =
    meta.photo_year_winner_user_ids ?? meta.photoYearWinnerUserIds ?? [];
  const photoYearWinnerUserIds = Array.isArray(photoYearWinnerUserIdsRaw)
    ? photoYearWinnerUserIdsRaw
        .map((item) => Number(item))
        .filter((item) => Number.isFinite(item) && item > 0)
    : [];
  const photoYearRoundSolved = Boolean(
    meta.photo_year_round_solved ?? meta.photoYearRoundSolved
  );
  const photoYearCanStartNext = Boolean(
    meta.photo_year_can_start_next ?? meta.photoYearCanStartNext
  );
  const photoYearEffectivePhase = photoYearRoundSolved
    ? "revealed"
    : (photoYearClientPhase || photoYearPhase || "discussion");
  const conversationCardsPhase = String(
    meta.conversation_cards_phase ?? meta.conversationCardsPhase ?? "discussing"
  ).trim();
  const conversationCardsViewerReady = Boolean(
    meta.conversation_cards_viewer_ready ?? meta.conversationCardsViewerReady
  );
  const conversationCardsReadyCount = Number(
    meta.conversation_cards_ready_count ?? meta.conversationCardsReadyCount ?? 0
  );
  const conversationCardsPlayersCount = Number(
    meta.conversation_cards_players_count ?? meta.conversationCardsPlayersCount ?? 0
  );
  const conversationCardsQuestionEn = String(
    meta.conversation_cards_question_en ?? meta.conversationCardsQuestionEn ?? ""
  ).trim();
  const conversationCardsQuestionSk = String(
    meta.conversation_cards_question_sk ?? meta.conversationCardsQuestionSk ?? ""
  ).trim();
  const conversationCardsDiscussionSeconds = Number(
    meta.conversation_cards_discussion_seconds ?? meta.conversationCardsDiscussionSeconds ?? 0
  );
  const conversationCardsUnlockAt = String(
    meta.conversation_cards_unlock_at ?? meta.conversationCardsUnlockAt ?? ""
  ).trim();
  const conversationCardsMetaSecondsLeft = Number(
    meta.conversation_cards_seconds_left ?? meta.conversationCardsSecondsLeft ?? 0
  );
  const conversationCardsCanStartNext = Boolean(
    meta.conversation_cards_can_start_next ?? meta.conversationCardsCanStartNext
  );
  const lastAnswerPhase = String(
    meta.last_answer_phase ?? meta.lastAnswerPhase ?? "ready"
  ).trim();
  const lastAnswerRoundIndex = Number(
    meta.last_answer_round_index ?? meta.lastAnswerRoundIndex ?? 1
  );
  const lastAnswerQuestionRaw = normalizeNotificationMeta(
    meta.last_answer_question ?? meta.lastAnswerQuestion
  );
  const lastAnswerQuestionEn = String(
    lastAnswerQuestionRaw.en ?? lastAnswerQuestionRaw.question_en ?? lastAnswerQuestionRaw.questionEn ?? ""
  ).trim();
  const lastAnswerQuestionSk = String(
    lastAnswerQuestionRaw.sk ?? lastAnswerQuestionRaw.question_sk ?? lastAnswerQuestionRaw.questionSk ?? ""
  ).trim();
  const lastAnswerPlayersRaw = meta.last_answer_players ?? meta.lastAnswerPlayers ?? [];
  const lastAnswerPlayers = Array.isArray(lastAnswerPlayersRaw)
    ? lastAnswerPlayersRaw
        .map((item) => {
          if (!item || typeof item !== "object") return null;
          const userId = Number(item.user_id ?? item.userId ?? 0);
          if (!Number.isFinite(userId) || userId <= 0) return null;
          return {
            userId,
            name: String(item.name || "").trim() || String(item.code || "").trim() || `#${userId}`,
            code: String(item.code || "").trim(),
            avatarUrl: toAbsoluteMediaUrl(item.avatar_url ?? item.avatarUrl ?? ""),
          };
        })
        .filter(Boolean)
    : [];
  const normalizeLastAnswerPlayer = (rawPlayer) => {
    const raw = normalizeNotificationMeta(rawPlayer);
    const userId = Number(raw.user_id ?? raw.userId ?? 0);
    if (!Number.isFinite(userId) || userId <= 0) return null;
    return {
      userId,
      name: String(raw.name || "").trim() || String(raw.code || "").trim() || `#${userId}`,
      code: String(raw.code || "").trim(),
      avatarUrl: toAbsoluteMediaUrl(raw.avatar_url ?? raw.avatarUrl ?? ""),
    };
  };
  const lastAnswerCandidate = normalizeLastAnswerPlayer(
    meta.last_answer_candidate ?? meta.lastAnswerCandidate
  );
  const lastAnswerLeader = normalizeLastAnswerPlayer(
    meta.last_answer_current_leader ?? meta.lastAnswerCurrentLeader
  );
  const lastAnswerWinner = normalizeLastAnswerPlayer(
    meta.last_answer_winner ?? meta.lastAnswerWinner
  );
  const lastAnswerViewerUserId = Number(
    meta.last_answer_viewer_user_id ?? meta.lastAnswerViewerUserId ?? 0
  );
  const lastAnswerMyReady = Boolean(
    meta.last_answer_my_ready ?? meta.lastAnswerMyReady
  );
  const lastAnswerReadyCount = Number(
    meta.last_answer_ready_count ?? meta.lastAnswerReadyCount ?? 0
  );
  const lastAnswerReadyRequired = Number(
    meta.last_answer_ready_required ?? meta.lastAnswerReadyRequired ?? 1
  );
  const lastAnswerParticipantsCount = Number(
    meta.last_answer_participants_count ?? meta.lastAnswerParticipantsCount ?? lastAnswerPlayers.length
  );
  const lastAnswerTimerSeconds = Number(
    meta.last_answer_timer_seconds ?? meta.lastAnswerTimerSeconds ?? 20
  );
  const lastAnswerDeadlineAt = String(
    meta.last_answer_deadline_at ?? meta.lastAnswerDeadlineAt ?? ""
  ).trim();
  const lastAnswerPausedSecondsLeft = Number(
    meta.last_answer_paused_seconds_left ?? meta.lastAnswerPausedSecondsLeft ?? 0
  );
  const lastAnswerAnswerCount = Number(
    meta.last_answer_answer_count ?? meta.lastAnswerAnswerCount ?? 0
  );
  const quizRoundIndexRaw = Number(meta.quiz_round_index ?? meta.quizRoundIndex ?? 0);
  const quizRoundIndex =
    Number.isFinite(quizRoundIndexRaw) && quizRoundIndexRaw > 0
      ? Math.floor(quizRoundIndexRaw)
      : null;
  const aliasRoundSecondsRaw = Number(meta.alias_round_seconds ?? meta.aliasRoundSeconds);
  const aliasRoundSeconds =
    Number.isFinite(aliasRoundSecondsRaw) && aliasRoundSecondsRaw > 0
      ? Math.max(10, Math.min(300, Math.floor(aliasRoundSecondsRaw)))
      : 60;
  const misleadQuestion = normalizeNotificationMeta(
    meta.mislead_question ?? meta.misleadQuestion
  );
  const misleadRole = String(meta.mislead_role ?? meta.misleadRole ?? meta.role ?? "").trim();
  const misleadRoundClosed = Boolean(
    meta.mislead_round_closed ?? meta.misleadRoundClosed
  );
  const misleadVotesRaw = normalizeNotificationMeta(meta.mislead_votes ?? meta.misleadVotes);
  const misleadVotes = Object.entries(misleadVotesRaw || {}).reduce((acc, [uid, idx]) => {
    const i = Number(idx);
    if (Number.isFinite(i) && i >= 0 && i <= 4) acc[String(uid)] = i;
    return acc;
  }, {});
  const misleadParticipantCodesRaw = normalizeNotificationMeta(
    meta.mislead_participant_codes ?? meta.misleadParticipantCodes
  );
  const misleadParticipantCodes = Object.entries(misleadParticipantCodesRaw || {}).reduce(
    (acc, [uid, code]) => {
      const key = String(uid || "").trim();
      const value = String(code || "").trim();
      if (key && value) acc[key] = value;
      return acc;
    },
    {}
  );
  const misleadQuestionId = String(misleadQuestion.id || "").trim();
  const misleadQuestionEn = String(
    misleadQuestion.question_en ?? misleadQuestion.questionEn ?? ""
  ).trim();
  const misleadQuestionSk = String(
    misleadQuestion.question_sk ?? misleadQuestion.questionSk ?? ""
  ).trim();
  const misleadOptionsEnRaw = misleadQuestion.options_en ?? misleadQuestion.optionsEn ?? [];
  const misleadOptionsSkRaw = misleadQuestion.options_sk ?? misleadQuestion.optionsSk ?? [];
  const misleadOptionsEn = Array.isArray(misleadOptionsEnRaw)
    ? misleadOptionsEnRaw.map((v) => String(v || "").trim()).filter(Boolean)
    : [];
  const misleadOptionsSk = Array.isArray(misleadOptionsSkRaw)
    ? misleadOptionsSkRaw.map((v) => String(v || "").trim()).filter(Boolean)
    : [];
  const misleadCorrectIndex = Number(
    misleadQuestion.correct_index ?? misleadQuestion.correctIndex ?? -1
  );
  const misleadKnowerUserId = String(
    meta.mislead_knower_user_id ?? meta.misleadKnowerUserId ?? ""
  ).trim();
  const misleadDiscussionSeconds = Number(
    meta.mislead_discussion_seconds ?? meta.misleadDiscussionSeconds ?? 180
  );
  const misleadStartedAtRaw = String(
    meta.mislead_started_at ?? meta.misleadStartedAt ?? ""
  ).trim();
  const misleadCurrentUserVote = Number(
    meta.mislead_my_vote ?? meta.misleadMyVote ?? -1
  );
  const aliasStorageKey = notification?.id ? `vg_alias_timer_${notification.id}` : null;
  const activeSubmitCooldownFromMeta = (() => {
    if (mode === "film_emoji") return filmEmojiRetryAfterSeconds;
    if (mode === "character_facts") return characterFactsRetryAfterSeconds;
    if (mode === "question_words") return questionWordsRetryAfterSeconds;
    if (mode === "line_puzzle") return linePuzzleRetryAfterSeconds;
    if (mode === "mixed_signals") return mixedSignalsRetryAfterSeconds;
    return 0;
  })();

  const [aliasSecondsLeft, setAliasSecondsLeft] = useState(aliasRoundSeconds);
  const [aliasTimerRunning, setAliasTimerRunning] = useState(false);
  const [aliasHasStarted, setAliasHasStarted] = useState(false);
  const [misleadAnswerVisible, setMisleadAnswerVisible] = useState(false);
  const [misleadSecondsLeft, setMisleadSecondsLeft] = useState(0);
  const aliasDeadlineRef = useRef(0);

  useEffect(() => {
    if (mode !== "film_emoji") return;
    setAnswerValue(filmEmojiRoundSolved ? "" : filmEmojiSubmittedText);
  }, [mode, notification?.id, meta.quiz_run_round_id, filmEmojiSubmittedText, filmEmojiRoundSolved]);

  useEffect(() => {
    if (mode !== "character_facts") return;
    setAnswerValue(characterFactsRoundSolved ? "" : characterFactsSubmittedText);
  }, [mode, notification?.id, meta.quiz_run_round_id, characterFactsSubmittedText, characterFactsRoundSolved]);

  useEffect(() => {
    window.clearTimeout(characterFactsHideTimer.current);
    if (mode !== "character_facts" || characterFactsRoundSolved) {
      setCharacterFactsVisible(false);
      return undefined;
    }
    setCharacterFactsVisible(true);
    characterFactsHideTimer.current = window.setTimeout(() => {
      setCharacterFactsVisible(false);
    }, 5000);
    return () => window.clearTimeout(characterFactsHideTimer.current);
  }, [mode, notification?.id, meta.quiz_run_round_id, characterFactsRoundSolved]);

  useEffect(() => {
    if (mode !== "question_words") return;
    setAnswerValue(questionWordsRoundSolved ? "" : questionWordsSubmittedText);
  }, [mode, notification?.id, meta.quiz_run_round_id, questionWordsSubmittedText, questionWordsRoundSolved]);

  useEffect(() => {
    setQuestionWordVisibleIndex(null);
    window.clearTimeout(questionWordHideTimer.current);
  }, [mode, notification?.id, meta.quiz_run_round_id]);

  useEffect(() => {
    if (mode !== "line_puzzle") return;
    setAnswerValue(linePuzzleRoundSolved ? "" : linePuzzleSubmittedText);
  }, [mode, notification?.id, meta.quiz_run_round_id, linePuzzleSubmittedText, linePuzzleRoundSolved]);

  useEffect(() => {
    if (mode !== "mixed_signals") return;
    if (effectiveMixedSignalsRoundSolved) {
      setAnswerParts(["", "", ""]);
      return;
    }
    const nextParts = mixedSignalsSubmittedWordsKey.split("\u0001").slice(0, 3);
    while (nextParts.length < 3) {
      nextParts.push("");
    }
    setAnswerParts(nextParts);
  }, [mode, notification?.id, meta.quiz_run_round_id, effectiveMixedSignalsRoundSolved, mixedSignalsSubmittedWordsKey]);

  useEffect(() => {
    if (mode !== "common_thread") return;
    const total = Number.isFinite(commonThreadWordsTotal) && commonThreadWordsTotal > 0
      ? Math.floor(commonThreadWordsTotal)
      : Math.max(1, commonThreadWordGuesses.length);
    if (commonThreadRoundSolved) {
      setCommonThreadAnswers(Array.from({ length: total }, () => ""));
      setCommonThreadCategoryValue("");
      return;
    }
    setCommonThreadAnswers(
      Array.from({ length: total }, (_, idx) => String(commonThreadWordGuesses[idx] || "").trim())
    );
    setCommonThreadCategoryValue(commonThreadCategoryGuess);
  }, [
    mode,
    notification?.id,
    meta.quiz_run_round_id,
    commonThreadWordsTotal,
    commonThreadRoundSolved,
    commonThreadCategoryGuess,
    commonThreadWordGuesses.join("\u0001"),
  ]);

  useEffect(() => {
    if (mode !== "glyph_relay") return;
    if (glyphRelayRoundSolved) {
      setGlyphRelaySelected("");
      setGlyphRelayRevealPressed(false);
      return;
    }
    setGlyphRelaySelected(glyphRelaySelectedOptionId);
    setGlyphRelayRevealPressed(false);
  }, [mode, notification?.id, meta.quiz_run_round_id, glyphRelayRoundSolved, glyphRelaySelectedOptionId]);

  useEffect(() => {
    if (mode !== "closest_guess") return;
    setAnswerValue(closestGuessPhase === "collecting" ? closestGuessMyAnswer : "");
  }, [mode, notification?.id, meta.quiz_run_round_id, closestGuessPhase, closestGuessMyAnswer]);

  useEffect(() => {
    if (mode !== "photo_year") return;
    setAnswerValue(photoYearEffectivePhase === "collecting" ? photoYearMyAnswer : "");
  }, [mode, notification?.id, meta.quiz_run_round_id, photoYearEffectivePhase, photoYearMyAnswer]);

  useEffect(() => {
    if (mode !== "spy") return;
    setSpyVoteTargetUserId(
      Number.isFinite(spyMyVoteTargetUserId) && spyMyVoteTargetUserId > 0
        ? spyMyVoteTargetUserId
        : null
    );
    if (hasSpyGuessResult || spyRoundSolved) {
      setSpyGuessValue("");
      setSpyGuessFormOpen(false);
      return;
    }
    setSpyGuessValue(spyGuessSubmittedText);
  }, [
    mode,
    notification?.id,
    meta.quiz_run_round_id,
    spyMyVoteTargetUserId,
    hasSpyGuessResult,
    spyGuessSubmittedText,
    spyRoundSolved,
  ]);

  useEffect(() => {
    const seconds = Number.isFinite(activeSubmitCooldownFromMeta) && activeSubmitCooldownFromMeta > 0
      ? Math.floor(activeSubmitCooldownFromMeta)
      : 0;
    setSubmitCooldownLeft(seconds);
  }, [notification?.id, meta.quiz_run_round_id, activeSubmitCooldownFromMeta]);

  useEffect(() => {
    if (submitCooldownLeft <= 0) return undefined;
    const timerId = window.setInterval(() => {
      setSubmitCooldownLeft((prev) => (prev > 1 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timerId);
  }, [submitCooldownLeft]);

  const readAliasStoredState = useCallback(() => {
    if (!aliasStorageKey || typeof window === "undefined") {
      return { deadlineMs: 0, started: false };
    }
    try {
      const raw = window.localStorage.getItem(aliasStorageKey);
      if (!raw) return { deadlineMs: 0, started: false };
      const parsed = JSON.parse(raw);
      const deadlineMs = Number(parsed?.deadlineMs || 0);
      const startedRaw = parsed?.started;
      const started = startedRaw === true;
      return {
        deadlineMs: Number.isFinite(deadlineMs) ? deadlineMs : 0,
        started,
      };
    } catch {
      return { deadlineMs: 0, started: false };
    }
  }, [aliasStorageKey]);

  const writeAliasStoredState = useCallback(
    ({ deadlineMs, started }) => {
      if (!aliasStorageKey || typeof window === "undefined") return;
      try {
        window.localStorage.setItem(aliasStorageKey, JSON.stringify({ deadlineMs, started }));
      } catch {
        // ignore storage failures
      }
    },
    [aliasStorageKey]
  );

  const startAliasTimer = useCallback(() => {
    const deadline = Date.now() + aliasRoundSeconds * 1000;
    aliasDeadlineRef.current = deadline;
    writeAliasStoredState({ deadlineMs: deadline, started: true });
    setAliasSecondsLeft(aliasRoundSeconds);
    setAliasHasStarted(true);
    setAliasTimerRunning(true);
  }, [aliasRoundSeconds, writeAliasStoredState]);

  useEffect(() => {
    if (mode !== "alias") {
      setAliasTimerRunning(false);
      setAliasHasStarted(false);
      return;
    }

    const now = Date.now();
    const { deadlineMs, started } = readAliasStoredState();
    setAliasHasStarted(Boolean(started));

    if (!started) {
      aliasDeadlineRef.current = 0;
      setAliasSecondsLeft(aliasRoundSeconds);
      setAliasTimerRunning(false);
      return;
    }

    aliasDeadlineRef.current = deadlineMs;
    const seconds = Math.max(0, Math.ceil((deadlineMs - now) / 1000));
    setAliasSecondsLeft(seconds);
    setAliasTimerRunning(seconds > 0);
  }, [
    mode,
    notification?.id,
    aliasRoundSeconds,
    readAliasStoredState,
  ]);

  useEffect(() => {
    if (mode !== "alias" || !aliasTimerRunning) return;

    const update = () => {
      const deadline = aliasDeadlineRef.current || 0;
      const seconds = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setAliasSecondsLeft(seconds);
      if (seconds <= 0) {
        setAliasTimerRunning(false);
      }
    };

    update();
    const timerId = window.setInterval(update, 250);
    return () => window.clearInterval(timerId);
  }, [mode, aliasTimerRunning]);

  useEffect(() => {
    if (mode !== "mislead_quiz") return;
    const startedMs = misleadStartedAtRaw ? Date.parse(misleadStartedAtRaw) : NaN;
    const duration = Number.isFinite(misleadDiscussionSeconds) && misleadDiscussionSeconds > 0
      ? misleadDiscussionSeconds
      : 180;
    if (!Number.isFinite(startedMs)) {
      setMisleadSecondsLeft(duration);
      return;
    }

    const tick = () => {
      const elapsed = Math.floor((Date.now() - startedMs) / 1000);
      const left = Math.max(0, duration - elapsed);
      setMisleadSecondsLeft(left);
    };
    tick();
    const timerId = window.setInterval(tick, 500);
    return () => window.clearInterval(timerId);
  }, [mode, misleadStartedAtRaw, misleadDiscussionSeconds, notification?.id]);

  useEffect(() => {
    if (mode !== "closest_guess") return;
    const deadlineMs = closestGuessDeadlineAt ? Date.parse(closestGuessDeadlineAt) : NaN;
    const fallback = Number.isFinite(closestGuessDurationSeconds) && closestGuessDurationSeconds > 0
      ? Math.floor(closestGuessDurationSeconds)
      : 90;
    if (!Number.isFinite(deadlineMs)) {
      setClosestGuessSecondsLeft(closestGuessPhase === "collecting" ? fallback : 0);
      return undefined;
    }
    const tick = () => {
      const left = Math.max(0, Math.ceil((deadlineMs - Date.now()) / 1000));
      setClosestGuessSecondsLeft(left);
    };
    tick();
    const timerId = window.setInterval(tick, 500);
    return () => window.clearInterval(timerId);
  }, [mode, closestGuessDeadlineAt, closestGuessDurationSeconds, closestGuessPhase, notification?.id]);

  useEffect(() => {
    if (mode !== "photo_year") return;
    setPhotoYearRevealRequested(false);
    setPhotoYearClientPhase("");
  }, [mode, meta.quiz_run_round_id]);

  useEffect(() => {
    if (mode !== "last_answer") return;
    setLastAnswerTickRequested(false);
  }, [mode, notification?.id, lastAnswerPhase, lastAnswerDeadlineAt, lastAnswerRoundIndex]);

  useEffect(() => {
    if (mode !== "last_answer") return;
    const phase = lastAnswerPhase || "ready";
    if (phase !== "countdown") {
      setLastAnswerSecondsLeft(
        Number.isFinite(lastAnswerTimerSeconds) && lastAnswerTimerSeconds > 0
          ? Math.floor(lastAnswerTimerSeconds)
          : 20
      );
      return undefined;
    }
    const deadlineMs = lastAnswerDeadlineAt ? Date.parse(lastAnswerDeadlineAt) : NaN;
    if (!Number.isFinite(deadlineMs)) {
      setLastAnswerSecondsLeft(
        Number.isFinite(lastAnswerTimerSeconds) && lastAnswerTimerSeconds > 0
          ? Math.floor(lastAnswerTimerSeconds)
          : 20
      );
      return undefined;
    }
    const tick = () => {
      const left = Math.max(0, Math.ceil((deadlineMs - Date.now()) / 1000));
      setLastAnswerSecondsLeft(left);
    };
    tick();
    const timerId = window.setInterval(tick, 250);
    return () => window.clearInterval(timerId);
  }, [
    mode,
    lastAnswerPhase,
    lastAnswerDeadlineAt,
    lastAnswerTimerSeconds,
    notification?.id,
    lastAnswerRoundIndex,
  ]);

  useEffect(() => {
    if (mode !== "last_answer") return;
    if (lastAnswerPhase !== "countdown") return;
    if (lastAnswerTickRequested || !notification?.id || lastAnswerSecondsLeft > 0) return;

    setLastAnswerTickRequested(true);
    api.post("events/api/party-games/last-answer/tick/", {
      notification_id: notification.id,
    }).then((res) => {
      const updatedNotification = res?.data?.notification;
      if (updatedNotification && typeof onNotificationUpdate === "function") {
        onNotificationUpdate(updatedNotification);
      }
    }).catch((err) => {
      const updatedNotification = err?.response?.data?.notification;
      if (updatedNotification && typeof onNotificationUpdate === "function") {
        onNotificationUpdate(updatedNotification);
      }
    });
  }, [
    mode,
    notification?.id,
    lastAnswerPhase,
    lastAnswerSecondsLeft,
    lastAnswerTickRequested,
    onNotificationUpdate,
  ]);

  useEffect(() => {
    if (mode !== "photo_year") return;
    const serverPhase = photoYearPhase || "discussion";

    const computeSnapshot = () => {
      const nowMs = Date.now();
      const discussionSeconds = Number.isFinite(photoYearDiscussionSeconds) && photoYearDiscussionSeconds > 0
        ? Math.floor(photoYearDiscussionSeconds)
        : 60;
      const answerSeconds = Number.isFinite(photoYearAnswerSeconds) && photoYearAnswerSeconds > 0
        ? Math.floor(photoYearAnswerSeconds)
        : 120;
      const startedMs = parsePartyDateMs(photoYearStartedAt);
      let discussionMs = parsePartyDateMs(photoYearDiscussionEndsAt);
      if (!Number.isFinite(discussionMs) && Number.isFinite(startedMs)) {
        discussionMs = startedMs + discussionSeconds * 1000;
      }
      let deadlineMs = parsePartyDateMs(photoYearDeadlineAt);
      if (!Number.isFinite(deadlineMs) && Number.isFinite(discussionMs)) {
        deadlineMs = discussionMs + answerSeconds * 1000;
      }
      if (!Number.isFinite(deadlineMs) && Number.isFinite(startedMs)) {
        deadlineMs = startedMs + (discussionSeconds + answerSeconds) * 1000;
      }
      let nextPhase = serverPhase;

      if (photoYearRoundSolved || serverPhase === "revealed") {
        return { phase: "revealed", secondsLeft: 0 };
      }

      if (nextPhase === "discussion") {
        if (Number.isFinite(discussionMs)) {
          if (nowMs < discussionMs) {
            return {
              phase: "discussion",
              secondsLeft: Math.max(0, Math.ceil((discussionMs - nowMs) / 1000)),
            };
          }
          nextPhase = "collecting";
        } else {
          return { phase: "discussion", secondsLeft: discussionSeconds };
        }
      }

      if (nextPhase === "collecting") {
        if (Number.isFinite(deadlineMs)) {
          return {
            phase: "collecting",
            secondsLeft: Math.max(0, Math.ceil((deadlineMs - nowMs) / 1000)),
          };
        }
        return { phase: "collecting", secondsLeft: answerSeconds };
      }

      return { phase: nextPhase, secondsLeft: 0 };
    };

    const tick = () => {
      const snapshot = computeSnapshot();
      setPhotoYearClientPhase(snapshot.phase);
      setPhotoYearSecondsLeft(snapshot.secondsLeft);
    };
    tick();
    const timerId = window.setInterval(tick, 500);
    return () => window.clearInterval(timerId);
  }, [
    mode,
    photoYearPhase,
    photoYearStartedAt,
    photoYearDiscussionEndsAt,
    photoYearDeadlineAt,
    photoYearDiscussionSeconds,
    photoYearAnswerSeconds,
    photoYearRoundSolved,
    notification?.id,
    meta.quiz_run_round_id,
  ]);

  useEffect(() => {
    if (mode !== "conversation_cards") return;
    const unlockMs = conversationCardsUnlockAt ? Date.parse(conversationCardsUnlockAt) : NaN;
    const fallback = Number.isFinite(conversationCardsMetaSecondsLeft) && conversationCardsMetaSecondsLeft > 0
      ? Math.floor(conversationCardsMetaSecondsLeft)
      : (Number.isFinite(conversationCardsDiscussionSeconds) && conversationCardsDiscussionSeconds > 0
        ? Math.floor(conversationCardsDiscussionSeconds)
        : 0);
    if (!Number.isFinite(unlockMs)) {
      setConversationCardsSecondsLeft(conversationCardsCanStartNext ? 0 : fallback);
      return undefined;
    }
    const tick = () => {
      const left = Math.max(0, Math.ceil((unlockMs - Date.now()) / 1000));
      setConversationCardsSecondsLeft(left);
    };
    tick();
    const timerId = window.setInterval(tick, 500);
    return () => window.clearInterval(timerId);
  }, [
    mode,
    conversationCardsUnlockAt,
    conversationCardsDiscussionSeconds,
    conversationCardsMetaSecondsLeft,
    conversationCardsCanStartNext,
    notification?.id,
    meta.quiz_run_round_id,
  ]);

  useEffect(() => {
    if (mode !== "photo_year") return;
    if (photoYearEffectivePhase !== "collecting") return;
    if (photoYearRoundSolved || photoYearRevealRequested) return;
    if (!notification?.id || photoYearSecondsLeft > 0) return;

    setPhotoYearRevealRequested(true);
    api.post("events/api/party-games/photo-year/reveal/", {
      notification_id: notification.id,
    }).then((res) => {
      const updatedNotification = res?.data?.notification;
      if (updatedNotification && typeof onNotificationUpdate === "function") {
        onNotificationUpdate(updatedNotification);
      }
    }).catch((err) => {
      const updatedNotification = err?.response?.data?.notification;
      if (updatedNotification && typeof onNotificationUpdate === "function") {
        onNotificationUpdate(updatedNotification);
      }
    });
  }, [
    mode,
    notification?.id,
    photoYearEffectivePhase,
    photoYearRoundSolved,
    photoYearRevealRequested,
    photoYearSecondsLeft,
    onNotificationUpdate,
  ]);

  // Prompts Q&A fields and normalization
  const qnaTrigger = pickLocalizedText(meta.qna_trigger_en, meta.qna_trigger_sk);
  const qnaReaction = pickLocalizedText(meta.qna_reaction_en, meta.qna_reaction_sk);
  const qnaQuestion = pickLocalizedText(meta.qna_question_en, meta.qna_question_sk);
  const qnaPlayers = Array.isArray(meta.qna_players) ? meta.qna_players : [];
  const qnaGuessedIds = new Set(
    (Array.isArray(meta.qna_guessed_user_ids) ? meta.qna_guessed_user_ids : [])
      .map(Number)
      .filter((value) => Number.isFinite(value) && value > 0)
  );
  const qnaStarterUserId = Number(meta.qna_starter_user_id || 0);
  const qnaFinished = Boolean(meta.qna_finished);
  const qnaViewerUserId = Number(meta.qna_viewer_user_id || notification?.user_id || notification?.user?.id || 0);
  const qnaViewerGuessed = qnaGuessedIds.has(qnaViewerUserId);

  const gameLabel =
    typeof gameNumber === "number"
      ? t("party.common.game_label", { game: gameNumber })
      : null;

  const rulesByMode = {
    spy: [t("party.spy.rule_1"), t("party.spy.rule_2")],
    portrait: [t("party.portrait.rule_1"), t("party.portrait.rule_2")],
    prompts_qna: [t("party.prompts.rule_1"), t("party.prompts.rule_2")],
    wrong_answer: [t("party.wrong_answer.rule_1"), t("party.wrong_answer.rule_2")],
    alias: [t("party.alias.rule_1"), t("party.alias.rule_2")],
    film_emoji: [t("party.film_emoji.rule_1"), t("party.film_emoji.rule_2")],
    character_facts: [t("party.character_facts.rule_1"), t("party.character_facts.rule_2")],
    question_words: [t("party.question_words.rule_1"), t("party.question_words.rule_2")],
    line_puzzle: [t("party.line_puzzle.rule_1"), t("party.line_puzzle.rule_2")],
    closest_guess: [
      t("party.closest_guess.rule_1"),
      t("party.closest_guess.rule_2"),
    ],
    photo_year: [
      t("party.photo_year.rule_1"),
      t("party.photo_year.rule_2"),
    ],
    conversation_cards: [
      t("party.conversation_cards.rule_1"),
      t("party.conversation_cards.rule_2"),
    ],
    last_answer: [
      t("party.last_answer.rule_1"),
      t("party.last_answer.rule_2"),
    ],
    glyph_relay: [
      t("party.glyph_relay.rule_1"),
      t("party.glyph_relay.rule_2"),
    ],
    mixed_signals: [
      t("party.mixed_signals.rule_1"),
      t("party.mixed_signals.rule_2"),
    ],
    common_thread: [
      t("party.common_thread.rule_1"),
      t("party.common_thread.rule_2"),
    ],
    mislead_quiz: [t("party.mislead_quiz.rule_1"), t("party.mislead_quiz.rule_2")],
    table: [],
  };

  const rulesLines = (rulesByMode[mode] || [])
    .map((line) => String(line || "").trim())
    .filter(Boolean);

  const renderRulesFoldout = () => {
    if (hideEmbeddedRules) return null;
    if (rulesLines.length === 0) return null;
    return (
      <div className="mt-4 w-full max-w-sm">
        <button
          type="button"
          onClick={() => setRulesOpen((prev) => !prev)}
          className="w-full rounded-full px-4 py-2 flex items-center justify-between bg-white/12 border border-white/20"
        >
          <span className="text-xs font-semibold uppercase tracking-[0.14em] text-white/85">
            {t("party.common.rules_title")}
          </span>
          <span className="text-xs font-semibold text-white/85">
            {rulesOpen ? t("party.common.rules_hide") : t("party.common.rules_show")}
          </span>
        </button>
        {rulesOpen ? (
          <div className="mt-2 rounded-2xl bg-white/10 border border-white/15 px-4 py-3 text-left">
            {rulesLines.map((line, idx) => (
              <p key={idx} className="text-xs text-white/85 leading-relaxed">
                {line}
              </p>
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  const revealSupportedModes = new Set([
    "film_emoji",
    "character_facts",
    "question_words",
    "line_puzzle",
    "common_thread",
  ]);

  const requestRevealAnswer = async () => {
    if (!notification?.id || revealLoading) return;
    setRevealLoading(true);
    setRevealError("");
    try {
      const res = await api.post("events/api/party-games/reveal-answer/vote/", {
        notification_id: notification.id,
      });
      const updatedNotification = res?.data?.notification;
      if (updatedNotification && typeof onNotificationUpdate === "function") {
        onNotificationUpdate(updatedNotification);
      }
    } catch (err) {
      const updatedNotification = err?.response?.data?.notification;
      if (updatedNotification && typeof onNotificationUpdate === "function") {
        onNotificationUpdate(updatedNotification);
      }
      setRevealError(err?.response?.data?.error || t("party.reveal_answer.error_failed"));
    } finally {
      setRevealLoading(false);
    }
  };

  const renderAnswerRevealControl = ({ isSolved = false, force = false } = {}) => {
    if (!force && !revealSupportedModes.has(String(mode || "").trim())) return null;
    if (isSolved || answerRevealed) return null;
    const required = Number.isFinite(answerRevealVotesRequired) && answerRevealVotesRequired > 0
      ? Math.floor(answerRevealVotesRequired)
      : 1;
    const count = Number.isFinite(answerRevealVotesCount) && answerRevealVotesCount > 0
      ? Math.floor(answerRevealVotesCount)
      : 0;
    return (
      <div className="mt-3 w-full max-w-sm rounded-2xl border border-white/15 bg-white/8 px-3 py-3 text-center">
        <div className="text-xs font-semibold text-white/75">
          {t("party.reveal_answer.votes_to_show", { count, required })}
        </div>
        <button
          type="button"
          onClick={requestRevealAnswer}
          disabled={revealLoading || answerRevealVoted}
          className={
            "mt-2 w-full rounded-full px-4 py-2 flex items-center justify-center " +
            (revealLoading || answerRevealVoted
              ? "bg-white/20 cursor-not-allowed"
              : "bg-white/15 border border-white/25")
          }
        >
          <span className="text-sm font-semibold text-white">
            {revealLoading
              ? t("party.reveal_answer.btn_voting")
              : answerRevealVoted
                ? t("party.reveal_answer.btn_counted")
                : t("party.reveal_answer.btn_show")}
          </span>
        </button>
        {revealError ? (
          <div className="mt-2 text-xs text-red-100 break-words">{revealError}</div>
        ) : null}
      </div>
    );
  };

  if (!notification) return null;

  if (mode === "conversation_cards") {
    const requestConversationCardsReady = async () => {
      if (submitLoading || conversationCardsViewerReady || !notification?.id) return;
      setSubmitLoading(true);
      setSubmitError("");
      try {
        const res = await api.post("events/api/party-games/conversation-cards/ready/", {
          notification_id: notification.id,
        });
        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        const updatedNotification = err?.response?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
        setSubmitError(
          err?.response?.data?.error || t("party.conversation_cards.error_ready_failed")
        );
      } finally {
        setSubmitLoading(false);
      }
    };

    if (conversationCardsPhase === "lobby") {
      return (
        <RoundAssignmentWrapper showMessage={false}>
          <div className="mb-3 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
            {gameLabel || t("party.conversation_cards.lobby_title")}
          </div>
          <div className="w-full max-w-sm rounded-3xl border border-white/15 bg-white/10 px-5 py-6 text-center">
            <div className="text-5xl" aria-hidden="true">💬</div>
            <div className="mt-4 text-xl font-black text-white">
              {conversationCardsViewerReady
                ? t("party.conversation_cards.ready_confirmed")
                : t("party.conversation_cards.ready_prompt")}
            </div>
            <div className="mt-3 text-sm leading-relaxed text-white/75">
              {t("party.conversation_cards.waiting_for_table")}
            </div>
            <div className="mt-4 text-3xl font-black tabular-nums text-white">
              {Math.max(0, conversationCardsReadyCount)}/{Math.max(1, conversationCardsPlayersCount)}
            </div>
          </div>
          <button
            type="button"
            onClick={requestConversationCardsReady}
            disabled={submitLoading || conversationCardsViewerReady}
            className={
              "mt-4 w-full max-w-sm rounded-full px-4 py-3 flex items-center justify-center " +
              (submitLoading || conversationCardsViewerReady
                ? "bg-white/20 cursor-not-allowed"
                : "bg-emerald-500/95 shadow-md shadow-emerald-950/50")
            }
          >
            <span className="text-sm font-semibold text-white">
              {submitLoading
                ? t("party.conversation_cards.ready_saving")
                : conversationCardsViewerReady
                  ? t("party.conversation_cards.ready_done")
                  : t("party.conversation_cards.ready_button")}
            </span>
          </button>
          {submitError ? (
            <div className="mt-2 text-xs text-red-100 text-center break-words">{submitError}</div>
          ) : null}
          {renderRulesFoldout()}
        </RoundAssignmentWrapper>
      );
    }

    const questionText = pickLocalizedText(
      conversationCardsQuestionEn,
      conversationCardsQuestionSk,
      t("party.conversation_cards.fallback_question")
    );
    const canStartNext =
      !nextLoading &&
      (
        conversationCardsCanStartNext ||
        conversationCardsSecondsLeft <= 0 ||
        conversationCardsPhase === "ready"
      );

    const formatConversationTimer = (seconds) => {
      const value = Math.max(0, Math.floor(Number(seconds) || 0));
      const mins = Math.floor(value / 60);
      const sec = value % 60;
      return `${String(mins).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
    };

    const requestNextConversationCard = async () => {
      if (!canStartNext || !notification?.id) return;
      setNextLoading(true);
      setNextError("");
      setSubmitError("");
      try {
        const res = await api.post("events/api/party-games/conversation-cards/next/", {
          notification_id: notification.id,
        });
        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        const updatedNotification = err?.response?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
        const code = err?.response?.data?.error;
        if (code === "question_skip_cooldown" || code === "question_still_discussing") {
          setNextError(t("party.conversation_cards.error_locked"));
        } else if (code === "quiz_tasks_pool_empty") {
          setNextError(t("party.conversation_cards.error_empty"));
        } else {
          setNextError(code || t("party.conversation_cards.error_failed"));
        }
      } finally {
        setNextLoading(false);
      }
    };

    return (
      <RoundAssignmentWrapper showMessage={false}>
        {quizRoundIndex ? (
          <div className="mb-3 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
            {t("party.conversation_cards.question_label", { number: quizRoundIndex })}
            {gameLabel ? ` · ${gameLabel}` : ""}
          </div>
        ) : gameLabel ? (
          <div className="mb-3 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
            {gameLabel}
          </div>
        ) : null}

        <div className="w-full max-w-sm rounded-3xl bg-sky-700/90 px-4 py-5 text-center shadow-lg shadow-sky-950/60">
          <div className="text-2xl font-black leading-snug text-white break-words">
            {questionText}
          </div>
        </div>

        <div className="mt-4 w-full max-w-sm space-y-3">
          {conversationCardsSecondsLeft > 0 ? <div className="rounded-2xl bg-violet-500/25 px-4 py-4 text-center shadow-md shadow-violet-950/40">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
              {t("party.conversation_cards.discussion_time")}
            </div>
            <div className="mt-1 text-4xl font-extrabold text-white">
              {formatConversationTimer(conversationCardsSecondsLeft)}
            </div>
            <div className="mt-2 text-sm text-white/80">
              {t("party.conversation_cards.discussion_hint_locked")}
            </div>
          </div> : <p className="text-center text-sm font-semibold text-white/80">{t("party.conversation_cards.discussion_hint_ready")}</p>}

          <button
            type="button"
            onClick={requestNextConversationCard}
            disabled={!canStartNext}
            className={
              "w-full rounded-full px-4 py-3 flex items-center justify-center " +
              (!canStartNext
                ? "bg-white/20 cursor-not-allowed"
                : "bg-emerald-500/95 shadow-md shadow-emerald-950/50")
            }
          >
            <span className="text-sm font-semibold text-white text-center leading-snug">
              {nextLoading
                ? t("party.conversation_cards.btn_loading")
                : (canStartNext
                  ? t("party.conversation_cards.btn_next")
                  : t("party.conversation_cards.btn_next_in", {
                      time: formatConversationTimer(conversationCardsSecondsLeft),
                    }))}
            </span>
          </button>
        </div>

        {nextError ? (
          <div className="mt-2 text-xs text-red-100 text-center break-words">{nextError}</div>
        ) : null}
        {renderRulesFoldout()}
      </RoundAssignmentWrapper>
    );
  }

  if (mode === "last_answer") {
    const phase = lastAnswerPhase || "ready";
    const questionText = pickLocalizedText(
      lastAnswerQuestionEn,
      lastAnswerQuestionSk,
      t("party.last_answer.fallback_question")
    );
    const isCandidate =
      lastAnswerCandidate &&
      Number(lastAnswerCandidate.userId) === Number(lastAnswerViewerUserId);
    const isLeader =
      lastAnswerLeader &&
      Number(lastAnswerLeader.userId) === Number(lastAnswerViewerUserId);
    const isWinner =
      lastAnswerWinner &&
      Number(lastAnswerWinner.userId) === Number(lastAnswerViewerUserId);
    const readyRequired = Math.max(1, Math.floor(Number(lastAnswerReadyRequired) || 1));
    const readyCount = Math.max(0, Math.floor(Number(lastAnswerReadyCount) || 0));
    const answerCount = Math.max(0, Math.floor(Number(lastAnswerAnswerCount) || 0));
    const canReady = phase === "ready" && !lastAnswerMyReady && !submitLoading;
    const canClaim =
      (phase === "open" || phase === "countdown") &&
      !submitLoading &&
      !isLeader &&
      !lastAnswerCandidate;
    const canResolve = phase === "answering" && isCandidate && !submitLoading;
    const canStartNext = phase === "winner" && !nextLoading;

    const formatLastAnswerTimer = (seconds) => {
      const value = Math.max(0, Math.floor(Number(seconds) || 0));
      const mins = Math.floor(value / 60);
      const sec = value % 60;
      return `${String(mins).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
    };

    const renderLastAnswerPlayer = (player, label, tone = "blue") => {
      if (!player) return null;
      const toneClass = tone === "green"
        ? "bg-emerald-500/20 border-emerald-200/40"
        : tone === "red"
          ? "bg-rose-500/20 border-rose-200/40"
          : "bg-white/10 border-white/15";
      return (
        <div className={`w-full rounded-3xl border px-4 py-4 text-center ${toneClass}`}>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/70">
            {label}
          </div>
          <div className="mt-3 flex flex-col items-center">
            {player.avatarUrl ? (
              <img
                src={player.avatarUrl}
                alt={player.name}
                className="h-24 w-24 rounded-full border-2 border-white/60 object-cover shadow-lg shadow-blue-950/30"
              />
            ) : (
              <div className="flex h-24 w-24 items-center justify-center rounded-full border-2 border-white/40 bg-white/15 text-3xl font-black text-white">
                {String(player.name || player.code || "?").slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="mt-3 text-xl font-black leading-tight text-white break-words">
              {player.name}
            </div>
            {player.code ? (
              <div className="mt-1 rounded-full bg-white/15 px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-white/75">
                {player.code}
              </div>
            ) : null}
          </div>
        </div>
      );
    };

    const postLastAnswerAction = async (url, payload = {}, { next = false } = {}) => {
      if (!notification?.id) return;
      if (next) {
        setNextLoading(true);
      } else {
        setSubmitLoading(true);
      }
      setSubmitError("");
      setNextError("");
      try {
        const res = await api.post(url, {
          notification_id: notification.id,
          ...payload,
        });
        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        const updatedNotification = err?.response?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
        const code = err?.response?.data?.error || "request_failed";
        if (next) {
          setNextError(code);
        } else {
          setSubmitError(code);
        }
      } finally {
        if (next) {
          setNextLoading(false);
        } else {
          setSubmitLoading(false);
        }
      }
    };

    return (
      <RoundAssignmentWrapper showMessage={false}>
        <div className="mb-3 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
          {t("party.common.round_label", {
            round: Number.isFinite(lastAnswerRoundIndex) && lastAnswerRoundIndex > 0 ? Math.floor(lastAnswerRoundIndex) : 1,
          })}
          {gameLabel ? ` · ${gameLabel}` : ""}
        </div>

        {phase === "ready" ? (
          <div className="w-full max-w-sm rounded-3xl border border-white/15 bg-white/10 px-5 py-6 text-center shadow-lg shadow-blue-950/40">
            <div className="text-6xl" aria-hidden="true">⏱️</div>
            <div className="mt-3 text-3xl font-black text-white">{t("party.last_answer.title")}</div>
            <div className="mt-2 text-sm font-semibold leading-relaxed text-white/70">{t("party.last_answer.intro")}</div>
          </div>
        ) : (
          <div className="w-full max-w-sm rounded-3xl bg-sky-700/90 px-4 py-5 text-center shadow-lg shadow-sky-950/60">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/70">
              {t("party.last_answer.question_label")}
            </div>
            <div className="mt-3 text-xl font-black leading-snug text-white break-words">
              {questionText}
            </div>
          </div>
        )}

        {phase === "ready" ? (
          <div className="mt-4 w-full max-w-sm space-y-3">
            <div className="rounded-2xl border border-white/15 bg-white/10 px-4 py-4 text-left">
              <div className="text-sm font-semibold text-white">{t("party.last_answer.how_it_works")}</div>
              <div className="mt-2 space-y-2 text-xs leading-relaxed text-white/80">
                <p>{t("party.last_answer.how_line_1")}</p>
                <p>{t("party.last_answer.how_line_2")}</p>
                <p>{t("party.last_answer.how_line_3")}</p>
              </div>
            </div>
            <div className="rounded-2xl bg-white/10 px-4 py-4 text-center">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/70">
                {t("party.last_answer.ready_label")}
              </div>
              <div className="mt-1 text-3xl font-black text-white">
                {readyCount}/{readyRequired}
              </div>
              <div className="mt-1 text-xs text-white/70">
                {t("party.last_answer.players_at_table", {
                  count: lastAnswerParticipantsCount || lastAnswerPlayers.length || "?",
                })}
              </div>
            </div>
            <button
              type="button"
              onClick={() => postLastAnswerAction("events/api/party-games/last-answer/ready/")}
              disabled={!canReady}
              className={
                "flex h-20 w-full items-center justify-center rounded-[26px] px-4 " +
                (!canReady
                  ? "bg-white/20 cursor-not-allowed"
                  : "bg-emerald-500/95 shadow-md shadow-emerald-950/50")
              }
            >
              <span className="text-sm font-semibold text-white">
                {submitLoading
                  ? t("party.last_answer.btn_saving")
                  : lastAnswerMyReady
                    ? t("party.last_answer.btn_ready_counted")
                    : t("party.last_answer.btn_ready")}
              </span>
            </button>
          </div>
        ) : null}

        {phase === "open" || phase === "countdown" ? (
          <div className="mt-4 w-full max-w-sm space-y-3">
            {lastAnswerLeader ? (
              renderLastAnswerPlayer(lastAnswerLeader, t("party.last_answer.last_accepted"), "green")
            ) : (
              <div className="rounded-2xl border border-dashed border-white/20 bg-white/8 px-4 py-5 text-center text-sm font-semibold text-white/75">
                {t("party.last_answer.no_accepted")}
              </div>
            )}

            {phase === "countdown" ? (
              <div
                className={
                  "rounded-2xl px-4 py-4 text-center shadow-md " +
                  (lastAnswerSecondsLeft > 5
                    ? "bg-emerald-500/20 shadow-emerald-950/35"
                    : "bg-rose-500/25 shadow-rose-950/40")
                }
              >
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
                  {t("party.last_answer.time_to_beat")}
                </div>
                <div className="mt-1 text-4xl font-black text-white">
                  {formatLastAnswerTimer(lastAnswerSecondsLeft)}
                </div>
                <div className="mt-2 text-xs text-white/75">
                  {t("party.last_answer.accepted_count", { count: answerCount })}
                </div>
              </div>
            ) : null}

            <button
              type="button"
              onClick={() => postLastAnswerAction("events/api/party-games/last-answer/claim/")}
              disabled={!canClaim}
              className={
                "flex h-20 w-full items-center justify-center rounded-[26px] px-4 " +
                (!canClaim
                  ? "bg-white/20 cursor-not-allowed"
                  : "bg-emerald-500/95 shadow-md shadow-emerald-950/50")
              }
            >
              <span className="text-lg font-black text-white">
                {submitLoading
                  ? t("party.last_answer.btn_claiming")
                  : isLeader
                    ? t("party.last_answer.btn_already_last")
                    : t("party.last_answer.btn_give_answer")}
              </span>
            </button>
          </div>
        ) : null}

        {phase === "answering" ? (
          <div className="mt-4 w-full max-w-sm space-y-3">
            {renderLastAnswerPlayer(
              lastAnswerCandidate,
              isCandidate ? t("party.last_answer.you_answering") : t("party.last_answer.answering_now"),
              "blue"
            )}
            <div className="rounded-2xl border border-amber-200/20 bg-amber-400/10 px-4 py-4 text-center">
              <div className="text-xs font-black uppercase tracking-[0.16em] text-amber-100/70">{t("party.last_answer.timer_paused")}</div>
              {lastAnswerLeader && lastAnswerPausedSecondsLeft > 0 ? <div className="mt-1 text-3xl font-black text-white">{formatLastAnswerTimer(lastAnswerPausedSecondsLeft)}</div> : null}
              <div className="mt-2 text-sm font-semibold leading-relaxed text-white/80">
                {isCandidate ? t("party.last_answer.say_and_decide") : t("party.last_answer.waiting_decision")}
              </div>
            </div>

            {isCandidate ? (
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => postLastAnswerAction("events/api/party-games/last-answer/resolve/", { decision: "rejected" })}
                  disabled={!canResolve}
                  className={
                    "min-h-20 rounded-[24px] px-3 py-4 text-center shadow-md " +
                    (!canResolve
                      ? "bg-white/20 cursor-not-allowed"
                      : "bg-rose-500/95 text-white shadow-rose-950/40")
                  }
                >
                  <span className="text-sm font-black leading-snug">{t("party.last_answer.my_answer_rejected")}</span>
                </button>
                <button
                  type="button"
                  onClick={() => postLastAnswerAction("events/api/party-games/last-answer/resolve/", { decision: "accepted" })}
                  disabled={!canResolve}
                  className={
                    "min-h-20 rounded-[24px] px-3 py-4 text-center shadow-md " +
                    (!canResolve
                      ? "bg-white/20 cursor-not-allowed"
                      : "bg-emerald-500/95 text-white shadow-emerald-950/40")
                  }
                >
                  <span className="text-sm font-black leading-snug">{t("party.last_answer.my_answer_accepted")}</span>
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        {phase === "winner" ? (
          <div className="mt-4 w-full max-w-sm space-y-3">
            {renderLastAnswerPlayer(
              lastAnswerWinner || lastAnswerLeader,
              isWinner ? t("party.last_answer.you_won") : t("party.last_answer.winner"),
              "green"
            )}
            <div className="rounded-2xl bg-white/10 px-4 py-4 text-center text-sm font-semibold text-white/80">
              {t("party.last_answer.winner_hint")}
            </div>
            <button
              type="button"
              onClick={() => postLastAnswerAction("events/api/party-games/last-answer/next/", {}, { next: true })}
              disabled={!canStartNext}
              className={
                "w-full rounded-full px-4 py-3 flex items-center justify-center " +
                (!canStartNext
                  ? "bg-white/20 cursor-not-allowed"
                  : "bg-emerald-500/95 shadow-md shadow-emerald-950/50")
              }
            >
              <span className="text-sm font-semibold text-white">
                {nextLoading ? t("party.last_answer.btn_starting") : t("party.last_answer.btn_start_new")}
              </span>
            </button>
          </div>
        ) : null}

        {lastAnswerPlayers.length ? (
          <div className="mt-4 flex max-w-sm flex-wrap justify-center gap-2">
            {lastAnswerPlayers.map((player) => (
              <div key={player.userId} className="flex items-center gap-2 rounded-full bg-white/10 px-2 py-1">
                {player.avatarUrl ? (
                  <img src={player.avatarUrl} alt={player.name} className="h-6 w-6 rounded-full object-cover" />
                ) : (
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-white/15 text-[10px] font-black text-white">
                    {String(player.name || player.code || "?").slice(0, 1).toUpperCase()}
                  </div>
                )}
                <span className="max-w-[90px] truncate text-[11px] font-semibold text-white/75">
                  {player.code || player.name}
                </span>
              </div>
            ))}
          </div>
        ) : null}

        {submitError ? (
          <div className="mt-2 text-xs text-red-100 text-center break-words">{submitError}</div>
        ) : null}
        {nextError ? (
          <div className="mt-2 text-xs text-red-100 text-center break-words">{nextError}</div>
        ) : null}
        {renderRulesFoldout()}
      </RoundAssignmentWrapper>
    );
  }

  if (mode === "closest_guess") {
    const phase = closestGuessPhase || "waiting";
    const questionTitle = isSlovakInterface
      ? (
          closestGuessQuestionTitleSk ||
          closestGuessQuestionTitleEn ||
          closestGuessQuestionTitle ||
          t("party.closest_guess.title")
        )
      : (
          closestGuessQuestionTitleEn ||
          closestGuessQuestionTitleSk ||
          closestGuessQuestionTitle ||
          t("party.closest_guess.title")
        );
    const promptText = isSlovakInterface
      ? (
          closestGuessPromptSk ||
          closestGuessPromptEn ||
          t("party.closest_guess.waiting_for_host")
        )
      : (
          closestGuessPromptEn ||
          closestGuessPromptSk ||
          t("party.closest_guess.waiting_for_host")
        );
    const displayUnit = isSlovakInterface
      ? (closestGuessUnitSk || closestGuessUnitEn || "")
      : (closestGuessUnitEn || closestGuessUnitSk || "");
    const decimalsHint = closestGuessValueDecimals > 0 ? t("party.closest_guess.decimals_hint") : "";
    const inputPlaceholder = displayUnit
      ? t("party.closest_guess.input_placeholder_with_unit", {
          unit: displayUnit,
          decimalsHint,
        })
      : t("party.closest_guess.input_placeholder");
    const hasSubmittedClosestGuessAnswer = closestGuessMyAnswer.length > 0;
    const acceptingAnswers = phase === "collecting" && closestGuessSecondsLeft > 0;
    const canSubmit =
      acceptingAnswers &&
      !submitLoading &&
      !hasSubmittedClosestGuessAnswer &&
      String(answerValue || "").trim().length > 0 &&
      closestGuessSecondsLeft > 0;
    const sortedTableResults = Object.entries(closestGuessTableResults)
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([tableNo, result]) => ({
        tableNumber: Number(tableNo),
        averageText: String(result.averageText || "").trim(),
        count: Number(result.count || 0),
        diffValue: Number(result.diffValue),
      }));
    const sortedScoreboard = Object.entries(closestGuessScoreboard)
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([tableNo, score]) => ({
        tableNumber: Number(tableNo),
        score: Number(score || 0),
      }));
    const formatClosestGuessError = (code) => {
      const normalizedCode = String(code || "").trim();
      if (normalizedCode === "answer_already_submitted") {
        return t("party.closest_guess.error_already_submitted");
      }
      if (normalizedCode === "question_already_revealed" || normalizedCode === "question_closed") {
        return t("party.closest_guess.error_question_closed");
      }
      if (normalizedCode === "invalid_answer") {
        return t("party.closest_guess.error_invalid_answer");
      }
      return t("party.closest_guess.error_submit_failed");
    };

    const requestSubmitClosestGuess = async () => {
      if (!canSubmit || !notification?.id) return;
      setSubmitLoading(true);
      setSubmitError("");
      try {
        const res = await api.post("events/api/party-games/closest-guess/submit/", {
          notification_id: notification.id,
          answer: String(answerValue || "").trim(),
        });
        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        const updatedNotification = err?.response?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
        setSubmitError(formatClosestGuessError(err?.response?.data?.error || "failed_to_submit_answer"));
      } finally {
        setSubmitLoading(false);
      }
    };

    return (
      <RoundAssignmentWrapper showMessage={false}>
        {quizRoundIndex ? (
          <div className="mb-3 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
            {t("party.closest_guess.question_label", { number: quizRoundIndex })}
          </div>
        ) : gameLabel ? (
          <div className="mb-3 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
            {gameLabel}
          </div>
        ) : null}

        <div className="w-full max-w-sm rounded-3xl py-5 px-4 bg-amber-600/90 shadow-lg shadow-amber-950/60 text-left">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/70 mb-3 text-center">
            {questionTitle}
          </div>
          <div className="text-base sm:text-lg leading-relaxed text-white font-semibold text-center">
            {promptText}
          </div>
          {closestGuessImageUrl ? (
            <div className="mt-4 flex justify-center">
              <img
                src={closestGuessImageUrl}
                alt={questionTitle || t("party.closest_guess.title")}
                className="w-full max-w-[280px] rounded-2xl border border-white/20 bg-white"
              />
            </div>
          ) : null}
        </div>

        {phase === "waiting" ? (
          <div className="mt-4 max-w-sm text-sm text-white/85 leading-relaxed text-center">
            {t("party.closest_guess.waiting_for_host")}
          </div>
        ) : null}

        {phase === "collecting" ? (
          <div className="mt-4 w-full max-w-sm">
            <div
              className={
                "rounded-2xl px-4 py-4 text-center shadow-md " +
                (closestGuessSecondsLeft > 15
                  ? "bg-white/12 shadow-sky-950/30"
                  : "bg-rose-500/25 shadow-rose-950/40")
              }
            >
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
                {t("party.closest_guess.time_left")}
              </div>
              <div className="mt-1 text-4xl font-extrabold text-white">
                {closestGuessSecondsLeft}s
              </div>
              <div className="mt-2 text-sm text-white/80">
                {closestGuessSecondsLeft > 0
                  ? (hasSubmittedClosestGuessAnswer
                    ? t("party.closest_guess.timer_hint_submitted")
                    : t("party.closest_guess.timer_hint_collecting"))
                  : (hasSubmittedClosestGuessAnswer
                    ? t("party.closest_guess.timer_hint_saved")
                    : t("party.closest_guess.timer_hint_no_answer"))}
              </div>
            </div>
            {hasSubmittedClosestGuessAnswer ? (
              <div className="mt-3 rounded-2xl border border-emerald-200/40 bg-emerald-500/20 px-4 py-4 text-center">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-50/80">
                  {t("party.closest_guess.answer_locked_label")}
                </div>
                <div className="mt-1 text-2xl font-extrabold text-white">
                  {closestGuessMyAnswer}
                </div>
                <div className="mt-2 text-xs text-white/75">
                  {t("party.closest_guess.answer_locked_hint")}
                </div>
              </div>
            ) : acceptingAnswers ? (
              <>
                <input
                  type="text"
                  value={answerValue}
                  onChange={(e) => setAnswerValue(e.target.value)}
                  inputMode="decimal"
                  placeholder={inputPlaceholder}
                  disabled={submitLoading}
                  className="mt-3 w-full rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-white placeholder:text-white/45 outline-none"
                  maxLength={24}
                  autoCorrect="off"
                  spellCheck={false}
                />
                <button
                  type="button"
                  onClick={requestSubmitClosestGuess}
                  disabled={!canSubmit}
                  className={
                    "mt-3 w-full rounded-full px-4 py-2 flex items-center justify-center " +
                    (!canSubmit
                      ? "bg-white/20 cursor-not-allowed"
                      : "bg-amber-500/95 shadow-md shadow-amber-950/50")
                  }
                >
                  <span className="text-sm font-semibold text-white">
                    {submitLoading
                      ? t("party.closest_guess.btn_submitting")
                      : t("party.closest_guess.btn_submit")}
                  </span>
                </button>
              </>
            ) : (
              <div className="mt-2 text-xs text-rose-100 text-center">
                {t("party.closest_guess.no_answer_wait_reveal")}
              </div>
            )}
          </div>
        ) : null}

        {phase === "revealed" ? (
          <div className="mt-4 w-full max-w-sm space-y-3">
            <div className="rounded-2xl bg-emerald-600/90 px-4 py-4 shadow-md shadow-emerald-950/50">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/75 mb-2">
                {t("party.closest_guess.correct_answer")}
              </div>
              <div className="text-2xl font-extrabold text-white">
                {closestGuessCorrectText}{displayUnit ? ` ${displayUnit}` : ""}
              </div>
              {closestGuessMyAnswer ? (
                <div className="mt-2 text-sm text-white/85">
                  {t("party.closest_guess.your_answer", { answer: closestGuessMyAnswer })}
                </div>
              ) : null}
            </div>
            {sortedTableResults.length ? (
              <div className="grid gap-3">
                {sortedTableResults.map((item) => {
                  const winner = closestGuessWinnerTables.includes(item.tableNumber);
                  return (
                    <div
                      key={item.tableNumber}
                      className={
                        "rounded-2xl border px-4 py-3 text-left " +
                        (winner ? "border-emerald-300 bg-emerald-500/20" : "border-white/15 bg-white/10")
                      }
                    >
                      <div className="text-sm font-semibold text-white">
                        {winner
                          ? t("party.closest_guess.table_winner_label", { number: item.tableNumber })
                          : t("party.closest_guess.table_label", { number: item.tableNumber })}
                      </div>
                      <div className="mt-1 text-sm text-white/80">
                        {t("party.closest_guess.average_line", {
                          average: item.averageText || "—",
                          unit: displayUnit ? ` ${displayUnit}` : "",
                          count: item.count,
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
            {sortedScoreboard.length ? (
              <div className="rounded-2xl border border-white/15 bg-white/10 px-4 py-4">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/75 mb-3">
                  {t("party.closest_guess.scoreboard")}
                </div>
                <div className="grid gap-2">
                  {sortedScoreboard.map((item) => (
                    <div key={item.tableNumber} className="text-sm text-white/85">
                      {t("party.closest_guess.scoreboard_line", {
                        number: item.tableNumber,
                        score: item.score,
                      })}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="text-xs text-white/70 text-center">
              {t("party.closest_guess.wait_next_question")}
            </div>
          </div>
        ) : null}

        {submitError ? (
          <div className="mt-2 text-xs text-red-100 text-center break-words">{submitError}</div>
        ) : null}
        {renderRulesFoldout()}
      </RoundAssignmentWrapper>
    );
  }

  if (mode === "photo_year") {
    const phase = photoYearEffectivePhase;
    const isDiscussion = phase === "discussion";
    const isCollecting = phase === "collecting";
    const isRevealed = phase === "revealed";
    const acceptingAnswers = isCollecting && photoYearSecondsLeft > 0 && !photoYearRoundSolved;
    const trimmedAnswer = String(answerValue || "").trim();
    const answersCount = Number.isFinite(photoYearAnswersCount) && photoYearAnswersCount > 0
      ? Math.floor(photoYearAnswersCount)
      : 0;
    const participantsCount =
      Number.isFinite(photoYearParticipantsCount) && photoYearParticipantsCount > 0
        ? Math.floor(photoYearParticipantsCount)
        : 0;
    const canSubmit =
      acceptingAnswers &&
      !submitLoading &&
      /^\d{3,4}$/.test(trimmedAnswer);
    const canStartNext = isRevealed && photoYearCanStartNext && !nextLoading;
    const currentAnswerText =
      photoYearMyAnswer || (photoYearMyAnswerValue > 0 ? String(photoYearMyAnswerValue) : "");

    const formatPhotoYearTimer = (seconds) => {
      const clamped = Math.max(0, Number(seconds) || 0);
      const mins = Math.floor(clamped / 60);
      const sec = clamped % 60;
      return `${String(mins).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
    };

    const formatYearDiff = (diff) => {
      if (!Number.isFinite(Number(diff))) return "";
      const value = Math.max(0, Math.floor(Number(diff)));
      return value === 1
        ? t("party.photo_year.year_away_one", { count: value })
        : t("party.photo_year.year_away_other", { count: value });
    };

    const requestSubmitPhotoYear = async () => {
      if (!canSubmit || !notification?.id) return;
      setSubmitLoading(true);
      setSubmitError("");
      setNextError("");
      try {
        const res = await api.post("events/api/party-games/photo-year/submit/", {
          notification_id: notification.id,
          answer: trimmedAnswer,
        });
        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        const updatedNotification = err?.response?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
        const code = err?.response?.data?.error;
        if (code === "discussion_active") {
          setSubmitError(t("party.photo_year.error_discussion_active"));
        } else if (code === "question_closed") {
          setSubmitError(t("party.photo_year.error_question_closed"));
        } else {
          setSubmitError(code || t("party.photo_year.error_submit_failed"));
        }
      } finally {
        setSubmitLoading(false);
      }
    };

    const requestNextPhotoYearRound = async () => {
      if (!canStartNext || !notification?.id) return;
      setNextLoading(true);
      setNextError("");
      setSubmitError("");
      try {
        const res = await api.post("events/api/party-games/photo-year/next/", {
          notification_id: notification.id,
        });
        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        const updatedNotification = err?.response?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
        setNextError(err?.response?.data?.error || t("party.photo_year.error_next_failed"));
      } finally {
        setNextLoading(false);
      }
    };

    return (
      <RoundAssignmentWrapper showMessage={false}>
        {quizRoundIndex ? (
          <div className="mb-3 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
            {t("party.photo_year.photo_label", { number: quizRoundIndex })}
          </div>
        ) : gameLabel ? (
          <div className="mb-3 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
            {gameLabel}
          </div>
        ) : null}

        <div className="w-full max-w-sm rounded-3xl overflow-hidden bg-zinc-950/95 shadow-lg shadow-black/55 border border-white/10">
          {photoYearImageUrl ? (
            <img
              src={photoYearImageUrl}
              alt={isRevealed && photoYearTitle ? photoYearTitle : t("party.photo_year.alt_photo")}
              className="w-full max-h-[360px] object-contain bg-black"
            />
          ) : (
            <div className="px-4 py-12 text-sm text-white/70">{t("party.photo_year.loading_photo")}</div>
          )}
          <div className="px-4 py-4 text-left">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/60">
              {t("party.photo_year.title")}
            </div>
            <div className="mt-2 text-sm text-white/80 leading-relaxed">
              {isRevealed
                ? (photoYearTitle || t("party.photo_year.revealed_hint"))
                : t("party.photo_year.discussion_hint")}
            </div>
          </div>
        </div>

        {!isRevealed ? (
          <div className="mt-4 w-full max-w-sm">
            <div
              className={
                "rounded-2xl px-4 py-4 text-center shadow-md " +
                (isDiscussion
                  ? "bg-violet-500/25 shadow-violet-950/40"
                  : (photoYearSecondsLeft > 15
                    ? "bg-emerald-500/20 shadow-emerald-950/35"
                    : "bg-rose-500/25 shadow-rose-950/40"))
              }
              >
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
                {isDiscussion ? t("party.photo_year.timer_discuss_first") : t("party.photo_year.timer_submit_year")}
              </div>
              <div className="mt-1 text-4xl font-extrabold text-white">
                {formatPhotoYearTimer(photoYearSecondsLeft)}
              </div>
              <div className="mt-2 text-sm text-white/80">
                {isDiscussion
                  ? t("party.photo_year.discussion_opens")
                  : (photoYearSecondsLeft > 0
                    ? t("party.photo_year.submit_before_end")
                    : t("party.photo_year.time_up_wait"))}
              </div>
            </div>

            <input
              type="text"
              value={answerValue}
              onChange={(e) => setAnswerValue(e.target.value.replace(/[^\d]/g, "").slice(0, 4))}
              inputMode="numeric"
              placeholder={isDiscussion ? t("party.photo_year.input_discussion") : t("party.photo_year.input_year")}
              disabled={!acceptingAnswers || submitLoading}
              className={
                "mt-3 w-full rounded-2xl border px-4 py-3 text-white placeholder:text-white/45 outline-none " +
                (!acceptingAnswers
                  ? "border-white/10 bg-white/5 opacity-70"
                  : "border-white/15 bg-white/10")
              }
              maxLength={4}
              autoCorrect="off"
              spellCheck={false}
            />

            <button
              type="button"
              onClick={requestSubmitPhotoYear}
              disabled={!canSubmit}
              className={
                "mt-3 w-full rounded-full px-4 py-2 flex items-center justify-center " +
                (!canSubmit
                  ? "bg-white/20 cursor-not-allowed"
                  : "bg-emerald-500/95 shadow-md shadow-emerald-950/50")
              }
            >
              <span className="text-sm font-semibold text-white text-center leading-snug">
                {submitLoading
                  ? t("party.photo_year.btn_submitting")
                  : (isDiscussion
                    ? t("party.photo_year.btn_answer_opens")
                    : (currentAnswerText ? t("party.photo_year.btn_update_year") : t("party.photo_year.btn_submit_year")))}
              </span>
            </button>

            <div className="mt-2 text-xs text-white/70 text-center">
              {participantsCount > 0
                ? t("party.photo_year.answers_submitted_total", { answers: answersCount, total: participantsCount })
                : t("party.photo_year.answers_submitted", { count: answersCount })}
            </div>
            {currentAnswerText ? (
              <div className="mt-1 text-xs text-white/75 text-center">
                {t("party.photo_year.your_current_answer", { answer: currentAnswerText })}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="mt-4 w-full max-w-sm space-y-3">
            <div className="rounded-2xl bg-emerald-600/90 px-4 py-4 shadow-md shadow-emerald-950/50 text-left">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/75 mb-2">
                {t("party.photo_year.correct_year")}
              </div>
              <div className="text-3xl font-extrabold text-white">
                {Number.isFinite(photoYearCorrectYear) && photoYearCorrectYear > 0
                  ? photoYearCorrectYear
                  : t("party.photo_year.revealed")}
              </div>
              {currentAnswerText ? (
                <div className="mt-2 text-sm text-white/85">
                  {t("party.photo_year.your_answer", { answer: currentAnswerText })}
                </div>
              ) : null}
              {photoYearCredit ? (
                <div className="mt-2 text-xs text-white/70">{photoYearCredit}</div>
              ) : null}
              {photoYearSourceUrl ? (
                <a
                  href={photoYearSourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex text-xs font-semibold text-white underline underline-offset-4"
                >
                  {t("party.photo_year.source")}
                </a>
              ) : null}
            </div>

            {photoYearResults.length ? (
              <div className="grid gap-3">
                {photoYearResults.map((item) => {
                  const winner = item.winner || photoYearWinnerUserIds.includes(item.userId);
                  return (
                    <div
                      key={item.userId}
                      className={
                        "rounded-2xl border px-4 py-3 text-left " +
                        (winner ? "border-emerald-300 bg-emerald-500/20" : "border-white/15 bg-white/10")
                      }
                    >
                      <div className="text-sm font-semibold text-white">
                        {winner
                          ? t("party.photo_year.result_name_closest", { name: item.name })
                          : item.name}
                      </div>
                      <div className="mt-1 text-sm text-white/80">
                        {item.year}{item.diff != null ? ` · ${formatYearDiff(item.diff)}` : ""}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white/75">
                {t("party.photo_year.no_answers")}
              </div>
            )}

            <button
              type="button"
              onClick={requestNextPhotoYearRound}
              disabled={!canStartNext}
              className={
                "w-full rounded-full px-4 py-2 flex items-center justify-center " +
                (!canStartNext
                  ? "bg-white/20 cursor-not-allowed"
                  : "bg-emerald-500/95 shadow-md shadow-emerald-950/50")
              }
            >
              <span className="text-sm font-semibold text-white text-center leading-snug">
                {nextLoading ? t("party.photo_year.btn_next_loading") : t("party.photo_year.btn_next")}
              </span>
            </button>
          </div>
        )}

        {submitError ? (
          <div className="mt-2 text-xs text-red-100 text-center break-words">{submitError}</div>
        ) : null}
        {nextError ? (
          <div className="mt-2 text-xs text-red-100 text-center break-words">{nextError}</div>
        ) : null}
        {renderRulesFoldout()}
      </RoundAssignmentWrapper>
    );
  }

  if (mode === "spy") {
    const spyRevealParticipant = spyParticipants.find((item) => item.userId === spyRevealedUserId) || null;
    const canOpenVote = spyCanStartVote && !submitLoading && !spyRoundSolved;
    const canContinueRound = spyCanContinue && !submitLoading && !spyRoundSolved;
    const canSubmitVote =
      spyCanVote &&
      !submitLoading &&
      Number.isFinite(Number(spyVoteTargetUserId || 0)) &&
      Number(spyVoteTargetUserId || 0) > 0;
    const canSubmitSpyGuess =
      isSpy &&
      spyCanSubmitFinalGuess &&
      !submitLoading &&
      String(spyGuessValue || "").trim().length > 0;
    const canStartNext = spyRoundSolved && spyCanStartNext && !nextLoading;
    const voteCount = Object.keys(spyVotes || {}).length;
    const totalVoters = Array.isArray(spyParticipants) ? spyParticipants.length : 0;
    const spyCategoryLabel = pickLocalizedText(spyCategory, spyCategorySk, t("party.spy.place_fallback"));

    const requestOpenVote = async () => {
      if (!canOpenVote || !notification?.id) return;
      setSubmitLoading(true);
      setSubmitError("");
      setNextError("");
      try {
        const res = await api.post("events/api/party-games/spy/start-vote/", {
          notification_id: notification.id,
        });
        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        setSubmitError(err?.response?.data?.error || t("party.spy.error_open_vote_failed"));
      } finally {
        setSubmitLoading(false);
      }
    };

    const requestSubmitVote = async () => {
      if (!canSubmitVote || !notification?.id) return;
      setSubmitLoading(true);
      setSubmitError("");
      setNextError("");
      try {
        const res = await api.post("events/api/party-games/spy/vote/", {
          notification_id: notification.id,
          target_user_id: Number(spyVoteTargetUserId),
        });
        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        setSubmitError(err?.response?.data?.error || t("party.spy.error_vote_failed"));
      } finally {
        setSubmitLoading(false);
      }
    };

    const requestContinueRound = async () => {
      if (!canContinueRound || !notification?.id) return;
      setSubmitLoading(true);
      setSubmitError("");
      setNextError("");
      try {
        const res = await api.post("events/api/party-games/spy/continue/", {
          notification_id: notification.id,
        });
        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        setSubmitError(err?.response?.data?.error || t("party.spy.error_continue_failed"));
      } finally {
        setSubmitLoading(false);
      }
    };

    const requestSpyGuess = async () => {
      if (!canSubmitSpyGuess || !notification?.id) return;
      setSubmitLoading(true);
      setSubmitError("");
      setNextError("");
      try {
        const res = await api.post("events/api/party-games/spy/guess/", {
          notification_id: notification.id,
          answer: String(spyGuessValue || "").trim(),
        });
        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        setSubmitError(err?.response?.data?.error || t("party.spy.error_guess_failed"));
      } finally {
        setSubmitLoading(false);
      }
    };

    const requestNextRound = async () => {
      if (!canStartNext || !notification?.id) return;
      setNextLoading(true);
      setNextError("");
      setSubmitError("");
      try {
        const res = await api.post("events/api/party-games/spy/next/", {
          notification_id: notification.id,
        });
        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        setNextError(err?.response?.data?.error || t("party.spy.error_next_failed"));
      } finally {
        setNextLoading(false);
      }
    };

    return (
      <RoundAssignmentWrapper showMessage={false}>
        <div className="mb-3 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
          {t("party.spy.clue_round", {
            round: Number.isFinite(spyClueRound) && spyClueRound > 0 ? spyClueRound : 1,
          })}
          {gameLabel ? ` · ${gameLabel}` : ""}
        </div>

        <div
          className={
            "w-full max-w-sm rounded-3xl py-5 px-4 shadow-lg text-left " +
            (spyIsEliminated
              ? "bg-white/12 shadow-slate-950/40"
              : isSpy
              ? "bg-gradient-to-b from-slate-900/95 to-slate-700/90 shadow-black/70"
              : "bg-emerald-600/80 shadow-emerald-900/70")
          }
        >
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/70 mb-3 text-center">
            {spyIsEliminated
              ? t("party.spy.status_round")
              : isSpy
                ? t("party.spy.role_spy")
                : t("party.spy.secret_place")}
          </div>
          {spyIsEliminated ? (
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">
                {t("party.spy.out_title")}
              </div>
              <p className="mt-3 text-sm text-white/80">
                {t("party.spy.out_body")}
              </p>
            </div>
          ) : isSpy ? (
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">
                {t("party.spy.spy_title")}
              </div>
              <p className="mt-3 text-sm text-white/80">
                {t("party.spy.category_line", { category: spyCategoryLabel })}
              </p>
            </div>
          ) : (
            <div className="text-center">
              <div className="text-4xl sm:text-5xl font-extrabold text-emerald-50 leading-none break-words">
                {(word || "").toString().toUpperCase() || t("party.spy.word_fallback")}
              </div>
              {startsFirst ? (
                <div className="mt-4 rounded-full bg-emerald-500/95 px-4 py-2">
                  <span className="text-sm font-semibold text-emerald-50">
                    {t("party.spy.you_start_clue_circle")}
                  </span>
                </div>
              ) : null}
            </div>
          )}
        </div>

        {!spyRoundSolved ? (
          <>
            <div className="mt-4 max-w-sm text-xs text-white/80 leading-relaxed text-center">
              {spyVotingOpen ? (
                <p>{t("party.spy.hint_vote_now")}</p>
              ) : spyIsEliminated ? (
                <p>{t("party.spy.hint_out_watch")}</p>
              ) : spyLastChanceOpen ? (
                <p>{t("party.spy.hint_last_chance")}</p>
              ) : spyRevealStatus === "not_spy" ? (
                <p>
                  {t("party.spy.hint_not_spy_continue", {
                    name: spyRevealParticipant?.name || t("party.spy.revealed_player"),
                  })}
                </p>
              ) : spyRevealStatus === "tie" ? (
                <p>{t("party.spy.hint_tie")}</p>
              ) : (
                <p>
                  {isSpy
                    ? t("party.spy.hint_spy")
                    : t("party.spy.hint_non_spy")}
                </p>
              )}
            </div>

            {spyVotingOpen ? (
              <div className="mt-4 w-full max-w-sm space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  {spyParticipants.map((participant) => {
                    const selected = Number(spyVoteTargetUserId || 0) === Number(participant.userId);
                    return (
                      <button
                        key={participant.userId}
                        type="button"
                        onClick={() => setSpyVoteTargetUserId(participant.userId)}
                        className={
                          "rounded-2xl border px-3 py-3 text-left transition " +
                          (selected
                            ? "border-cyan-300 bg-cyan-400/20"
                            : "border-white/15 bg-white/10")
                        }
                      >
                        <div className="flex items-center gap-3">
                          {participant.avatarUrl ? (
                            <img
                              src={participant.avatarUrl}
                              alt={participant.name}
                              className="h-12 w-12 rounded-full object-cover border border-white/30"
                            />
                          ) : (
                            <div className="h-12 w-12 rounded-full bg-white/15 border border-white/20 flex items-center justify-center text-white font-bold">
                              {String(participant.name || participant.code || "?").slice(0, 1).toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-white truncate">{participant.name}</div>
                            <div className="text-xs text-white/65 truncate">{participant.code}</div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={requestSubmitVote}
                  disabled={!canSubmitVote}
                  className={
                    "w-full rounded-full px-4 py-2 flex items-center justify-center " +
                    (!canSubmitVote ? "bg-white/20 cursor-not-allowed" : "bg-cyan-500/95 shadow-md shadow-cyan-950/50")
                  }
                >
                  <span className="text-sm font-semibold text-white">
                    {submitLoading
                      ? t("party.spy.btn_submitting_vote")
                      : spyHasVoted
                        ? t("party.spy.btn_update_vote")
                        : t("party.spy.btn_submit_vote")}
                  </span>
                </button>
                <div className="text-xs text-white/65 text-center">
                  {t("party.spy.votes_sent", { count: voteCount, total: totalVoters || "?" })}
                </div>
              </div>
            ) : null}

            {spyRevealStatus === "not_spy" || spyRevealStatus === "tie" ? (
              <div className="mt-4 w-full max-w-sm">
                <div className="rounded-2xl bg-white/10 border border-white/15 px-4 py-4 text-center">
                  <div className="text-sm font-semibold text-white">
                    {spyRevealStatus === "tie"
                      ? t("party.spy.tie_vote")
                      : t("party.spy.not_spy_result", {
                        name: spyRevealParticipant?.name || t("party.spy.that_player"),
                      })}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={requestContinueRound}
                  disabled={!canContinueRound}
                  className={
                    "mt-3 w-full rounded-full px-4 py-2 flex items-center justify-center " +
                    (!canContinueRound ? "bg-white/20 cursor-not-allowed" : "bg-emerald-500/95 shadow-md shadow-emerald-950/50")
                  }
                >
                  <span className="text-sm font-semibold text-white">
                    {submitLoading ? t("party.spy.btn_continuing") : t("party.spy.btn_continue_clues")}
                  </span>
                </button>
              </div>
            ) : null}

            {(isSpy && spyCanSubmitFinalGuess) || spyLastChanceOpen ? (
              <div className="mt-4 w-full max-w-sm">
                {!spyGuessFormOpen && !spyLastChanceOpen ? (
                  <button
                    type="button"
                    onClick={() => setSpyGuessFormOpen(true)}
                    className="w-full rounded-full px-4 py-2 flex items-center justify-center bg-fuchsia-500/95 shadow-md shadow-fuchsia-950/50"
                  >
                    <span className="text-sm font-semibold text-white">{t("party.spy.btn_reveal_guess")}</span>
                  </button>
                ) : null}
                {(spyGuessFormOpen || spyLastChanceOpen) && isSpy ? (
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={spyGuessValue}
                      onChange={(e) => setSpyGuessValue(e.target.value)}
                      placeholder={t("party.spy.input_guess_place")}
                      className="w-full rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-white placeholder:text-white/45 outline-none"
                      maxLength={96}
                      autoCapitalize="words"
                      autoCorrect="off"
                      spellCheck={false}
                    />
                    <button
                      type="button"
                      onClick={requestSpyGuess}
                      disabled={!canSubmitSpyGuess}
                      className={
                        "w-full rounded-full px-4 py-2 flex items-center justify-center " +
                        (!canSubmitSpyGuess ? "bg-white/20 cursor-not-allowed" : "bg-fuchsia-500/95 shadow-md shadow-fuchsia-950/50")
                      }
                    >
                      <span className="text-sm font-semibold text-white">
                        {submitLoading
                          ? t("party.spy.btn_checking")
                          : spyLastChanceOpen
                            ? t("party.spy.btn_final_guess")
                            : t("party.spy.btn_guess_now")}
                      </span>
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}

            {canOpenVote ? (
              <div className="mt-4 w-full max-w-sm">
                <button
                  type="button"
                  onClick={requestOpenVote}
                  disabled={!canOpenVote}
                  className={
                    "w-full rounded-full px-4 py-2 flex items-center justify-center " +
                    (!canOpenVote ? "bg-white/20 cursor-not-allowed" : "bg-emerald-500/95 shadow-md shadow-emerald-950/50")
                  }
                >
                  <span className="text-sm font-semibold text-white">
                    {submitLoading ? t("party.spy.btn_opening_vote") : t("party.spy.btn_start_voting")}
                  </span>
                </button>
              </div>
            ) : null}
          </>
        ) : (
          <div className="mt-4 w-full max-w-sm">
            <div className="rounded-2xl bg-emerald-600/90 px-4 py-4 shadow-md shadow-emerald-950/50">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/75 mb-2">
                {t("party.spy.result_label")}
              </div>
              <div className="text-xl sm:text-2xl font-semibold text-white break-words">
                {spyWinner === "spy"
                  ? t("party.spy.spy_wins")
                  : t("party.spy.table_wins")}
              </div>
              <div className="mt-2 text-sm text-white/80 break-words">
                {t("party.spy.secret_place_result", { place: spySolutionTitle || word || "—" })}
              </div>
              {hasSpyGuessResult ? (
                <div className="mt-2 text-xs text-white/75">
                  {t("party.spy.your_guess", { guess: spyGuessSubmittedText || "—" })} {spyGuessCorrectRaw ? "✓" : "✕"}
                </div>
              ) : null}
            </div>

            <button
              type="button"
              onClick={requestNextRound}
              disabled={!canStartNext}
              className={
                "mt-3 w-full rounded-full px-4 py-2 flex items-center justify-center " +
                (!canStartNext ? "bg-white/20 cursor-not-allowed" : "bg-emerald-500/95 shadow-md shadow-emerald-950/50")
              }
            >
              <span className="text-sm font-semibold text-white">
                {nextLoading ? t("party.spy.btn_next_loading") : t("party.spy.btn_next_round")}
              </span>
            </button>
          </div>
        )}

        {submitError ? (
          <div className="mt-2 text-xs text-red-100 text-center break-words">{submitError}</div>
        ) : null}
        {nextError ? (
          <div className="mt-2 text-xs text-red-100 text-center break-words">{nextError}</div>
        ) : null}
        {renderRulesFoldout()}
      </RoundAssignmentWrapper>
    );
  }

  if (mode === "portrait") {
    return (
      <RoundAssignmentWrapper showMessage={false}>
        <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-blue-200/80 mb-3">
          {t("party.portrait.pill_title")}
          {gameLabel ? ` · ${gameLabel}` : ""}
        </div>
        <div className="w-full max-w-xs rounded-3xl py-5 px-4 bg-blue-700/80 shadow-lg shadow-blue-900/70">
          {subjectPhotoUrl ? (
            <div className="flex flex-col items-center">
              <div className="h-32 w-32 overflow-hidden rounded-full border-2 border-white/70 bg-white/10 shadow-md shadow-blue-950/40">
                <img
                  src={subjectPhotoUrl}
                  alt={t("party.portrait.photo_alt_with_code", {
                    code: subjectCode || "CODE",
                  })}
                  className="h-full w-full rounded-full object-cover"
                />
              </div>
              <div className="mt-4 text-[11px] font-semibold tracking-[0.18em] uppercase text-blue-100/80">
                {t("party.portrait.label_code")}
              </div>
              <div className="mt-2 rounded-2xl bg-white/12 px-4 py-2">
                <span className="text-3xl sm:text-4xl font-extrabold text-white leading-none break-words">
                  {subjectCode || "CODE"}
                </span>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-1">
              <span className="text-4xl sm:text-5xl font-extrabold text-white leading-none break-words">
                {subjectCode || "CODE"}
              </span>
            </div>
          )}
        </div>

        <div className="mt-4 max-w-xs text-xs text-white/80 leading-relaxed">
          <p>
            {subjectPhotoUrl
              ? t("party.portrait.body_draw_photo_and_code", { code: subjectCode || "CODE" })
              : t("party.portrait.body_draw_code", { code: subjectCode || "CODE" })}
          </p>
        </div>

        {!subjectPhotoUrl && badgeUrl && (
          <button
            type="button"
            onClick={() => {
              window.location.href = badgeUrl;
            }}
            className="mt-4 w-full max-w-xs rounded-full bg-blue-500/95 shadow-md shadow-blue-900/60 px-4 py-2 flex items-center justify-center"
          >
            <span className="text-sm font-semibold text-white text-center leading-snug">
              {t("party.portrait.btn_open_badge")}
            </span>
          </button>
        )}
        {renderRulesFoldout()}
      </RoundAssignmentWrapper>
    );
  }

  // Prompts Q&A game branch
  if (mode === "prompts_qna") {
    const starter = qnaPlayers.find((player) => Number(player?.user_id) === qnaStarterUserId);
    const revealMission = () => {
      window.clearTimeout(qnaMissionHideTimer.current);
      setQnaMissionVisible(true);
      qnaMissionHideTimer.current = window.setTimeout(() => setQnaMissionVisible(false), 3000);
    };

    const requestPromptsNext = async () => {
      if (qnaViewerGuessed || nextLoading || !notification?.id) return;
      setNextLoading(true);
      setNextError("");
      try {
        const res = await api.post("events/api/party-games/prompts-qna/guessed/", {
          notification_id: notification.id,
        });
        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        const code = err?.response?.data?.error;
        setNextError(code || t("party.prompts.error_guessed_failed"));
      } finally {
        setNextLoading(false);
      }
    };

    const playerCardClass = (guessed) => "flex items-center gap-2 rounded-2xl border px-3 py-2 " + (guessed ? "border-emerald-300/35 bg-emerald-400/15 opacity-60" : "border-white/15 bg-white/10");

    const qnaView = (
      <RoundAssignmentWrapper showMessage={false}>
        <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-fuchsia-200/80">{t("party.prompts.pill_title")} {gameLabel ? ` · ${gameLabel}` : ""}</div>
        <button type="button" onClick={revealMission} className="w-full max-w-sm rounded-3xl border border-fuchsia-300/25 bg-fuchsia-700/90 px-4 py-5 shadow-lg shadow-fuchsia-900/70">
          {qnaMissionVisible ? <>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-fuchsia-100/65">{t("party.prompts.when_label")}</div>
            <div className="mt-1 text-lg font-black leading-snug text-white">{qnaTrigger}</div>
            <div className="my-3 h-px bg-white/15" />
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-fuchsia-100/65">{t("party.prompts.do_label")}</div>
            <div className="mt-1 text-lg font-black leading-snug text-white">{qnaReaction}</div>
          </> : <><div className="text-3xl">🔒</div><div className="mt-2 text-sm font-black text-white">{t("party.prompts.tap_to_reveal")}</div><div className="mt-1 text-xs font-semibold text-white/55">{t("party.prompts.hides_automatically")}</div></>}
        </button>
        <div className="mt-4 w-full max-w-sm rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-left">
          <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-white/50">{t("party.prompts.conversation_title")}</div>
          <div className="mt-1 text-sm font-black leading-snug text-white">{qnaQuestion}</div>
          {starter ? <div className="mt-2 text-xs font-semibold text-fuchsia-200">{t("party.prompts.starter", { name: starter.name || starter.code })}</div> : null}
        </div>
        <div className="mt-4 grid w-full max-w-sm grid-cols-2 gap-2">
          {qnaPlayers.map((player) => {
            const guessed = qnaGuessedIds.has(Number(player?.user_id));
            return <div key={player.user_id} className={playerCardClass(guessed)}><div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white text-[10px] font-black text-fuchsia-800">{player.avatar_url ? <img src={player.avatar_url} alt="" className="h-full w-full object-cover" /> : String(player.name || player.code || "?").slice(0, 2).toUpperCase()}</div><div className="min-w-0 flex-1 truncate text-xs font-black text-white">{player.name || player.code}</div><div className="text-sm">{guessed ? "✓" : "?"}</div></div>;
          })}
        </div>
        {qnaFinished ? <div className="mt-4 text-lg font-black text-emerald-200">🎉 {t("party.prompts.everyone_guessed")}</div> : qnaViewerGuessed ? <div className="mt-4 text-sm font-bold text-white/65">{t("party.prompts.you_are_guessed")}</div> : <button type="button" onClick={requestPromptsNext} disabled={nextLoading} className="mt-4 flex w-full max-w-sm items-center justify-center rounded-full bg-fuchsia-500/95 px-4 py-3 shadow-md shadow-fuchsia-900/60 disabled:opacity-50"><span className="text-sm font-black text-white">{nextLoading ? t("party.prompts.btn_loading") : t("party.prompts.btn_me_guessed")}</span></button>}
        {nextError ? <div className="mt-2 text-center text-xs text-red-100 break-words">{nextError}</div> : null}
        {renderRulesFoldout()}
      </RoundAssignmentWrapper>
    );

    return qnaView;
  }

  if (mode === "film_emoji") {
    const emojiLine = filmEmojiEmojis.length > 0 ? filmEmojiEmojis.join("  ") : "❓";
    const canSubmit =
      !submitLoading &&
      !filmEmojiRoundSolved &&
      submitCooldownLeft <= 0 &&
      answerValue.trim().length > 0;
    const canStartNext = filmEmojiRoundSolved && filmEmojiCanStartNext && !nextLoading;

    const requestSubmitAnswer = async () => {
      if (!canSubmit || !notification?.id) return;

      setSubmitLoading(true);
      setSubmitError("");
      setNextError("");
      try {
        const res = await api.post("events/api/party-games/film-emoji/submit/", {
          notification_id: notification.id,
          answer: answerValue.trim(),
        });

        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        const code = err?.response?.data?.error;
        const retryAfterSeconds = Number(err?.response?.data?.retry_after_seconds || 0);
        const updatedNotification = err?.response?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
        if (code === "submit_cooldown" && retryAfterSeconds > 0) {
          setSubmitCooldownLeft(Math.floor(retryAfterSeconds));
          setSubmitError(t("party.common.cooldown_error", { seconds: Math.floor(retryAfterSeconds) }));
        } else {
          setSubmitError(code || t("party.film_emoji.error_submit_failed"));
        }
      } finally {
        setSubmitLoading(false);
      }
    };

    const requestNextRound = async () => {
      if (!canStartNext || !notification?.id) return;

      setNextLoading(true);
      setNextError("");
      setSubmitError("");
      try {
        const res = await api.post("events/api/party-games/film-emoji/next/", {
          notification_id: notification.id,
        });

        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        const code = err?.response?.data?.error;
        setNextError(code || t("party.film_emoji.error_next_failed"));
      } finally {
        setNextLoading(false);
      }
    };

    return (
      <RoundAssignmentWrapper showMessage={false}>
        {quizRoundIndex ? (
          <div className="mb-3 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
            {t("party.common.round_label", { round: quizRoundIndex })}
          </div>
        ) : gameLabel ? (
          <div className="mb-3 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
            {gameLabel}
          </div>
        ) : null}

        <div className="w-full max-w-sm rounded-3xl py-5 px-4 bg-rose-700/90 shadow-lg shadow-rose-950/70 text-center">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/70 mb-3">
            {t("party.film_emoji.label_your_emojis")}
          </div>
          <div className="text-3xl sm:text-4xl leading-tight break-words">{emojiLine}</div>
        </div>

        <div className="mt-4 max-w-sm text-xs text-white/80 leading-relaxed text-center">
          <p>
            {t("party.film_emoji.body_share_clues", {
              count: Number.isFinite(filmEmojiAllEmojisCount) && filmEmojiAllEmojisCount > 0
                ? filmEmojiAllEmojisCount
                : "?",
            })}
          </p>
        </div>

        {!filmEmojiRoundSolved ? (
          <div className="mt-4 w-full max-w-sm">
            <input
              type="text"
              value={answerValue}
              onChange={(e) => setAnswerValue(e.target.value)}
              placeholder={t("party.film_emoji.input_placeholder")}
              className="w-full rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-white placeholder:text-white/45 outline-none"
              maxLength={255}
              autoCapitalize="words"
              autoCorrect="off"
              spellCheck={false}
            />

            <button
              type="button"
              onClick={requestSubmitAnswer}
              disabled={!canSubmit}
              className={
                "mt-3 w-full rounded-full px-4 py-2 flex items-center justify-center " +
                (!canSubmit
                  ? "bg-white/20 cursor-not-allowed"
                  : "bg-rose-500/95 shadow-md shadow-rose-950/50")
              }
            >
              <span className="text-sm font-semibold text-white text-center leading-snug">
                {submitLoading
                  ? t("party.film_emoji.btn_submit_loading")
                  : submitCooldownLeft > 0
                    ? t("party.common.wait_seconds", { seconds: submitCooldownLeft })
                    : t("party.film_emoji.btn_submit")}
              </span>
            </button>

            {hasFilmEmojiSubmissionResult && filmEmojiSubmissionCorrectRaw === false ? (
              <div className="mt-2 text-xs text-amber-100 text-center break-words">
                {t("party.film_emoji.feedback_wrong")}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="mt-4 w-full max-w-sm">
            <div className="rounded-2xl bg-emerald-600/90 px-4 py-4 shadow-md shadow-emerald-950/50">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/75 mb-2">
                {t("party.film_emoji.label_solution")}
              </div>
              <div className="text-xl sm:text-2xl font-semibold text-white break-words">
                {filmEmojiSolutionTitle || t("party.film_emoji.feedback_correct")}
              </div>
            </div>

            <button
              type="button"
              onClick={requestNextRound}
              disabled={!canStartNext}
              className={
                "mt-3 w-full rounded-full px-4 py-2 flex items-center justify-center " +
                (!canStartNext
                  ? "bg-white/20 cursor-not-allowed"
                  : "bg-emerald-500/95 shadow-md shadow-emerald-950/50")
              }
            >
              <span className="text-sm font-semibold text-white text-center leading-snug">
                {nextLoading
                  ? t("party.film_emoji.btn_next_loading")
                  : t("party.film_emoji.btn_next")}
              </span>
            </button>
          </div>
        )}

        {renderAnswerRevealControl({ isSolved: filmEmojiRoundSolved })}
        {submitError ? (
          <div className="mt-2 text-xs text-red-100 text-center break-words">{submitError}</div>
        ) : null}
        {nextError ? (
          <div className="mt-2 text-xs text-red-100 text-center break-words">{nextError}</div>
        ) : null}
        {renderRulesFoldout()}
      </RoundAssignmentWrapper>
    );
  }

  if (mode === "character_facts") {
    const canSubmit =
      !submitLoading &&
      !characterFactsRoundSolved &&
      submitCooldownLeft <= 0 &&
      answerValue.trim().length > 0;
    const canStartNext = characterFactsRoundSolved && characterFactsCanStartNext && !nextLoading;

    const revealCharacterFacts = () => {
      window.clearTimeout(characterFactsHideTimer.current);
      setCharacterFactsVisible(true);
      characterFactsHideTimer.current = window.setTimeout(() => {
        setCharacterFactsVisible(false);
      }, 3000);
    };

    const requestSubmitAnswer = async () => {
      if (!canSubmit || !notification?.id) return;

      setSubmitLoading(true);
      setSubmitError("");
      setNextError("");
      try {
        const res = await api.post("events/api/party-games/character-facts/submit/", {
          notification_id: notification.id,
          answer: answerValue.trim(),
        });

        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        const code = err?.response?.data?.error;
        const retryAfterSeconds = Number(err?.response?.data?.retry_after_seconds || 0);
        const updatedNotification = err?.response?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
        if (code === "submit_cooldown" && retryAfterSeconds > 0) {
          setSubmitCooldownLeft(Math.floor(retryAfterSeconds));
          setSubmitError(t("party.common.cooldown_error", { seconds: Math.floor(retryAfterSeconds) }));
        } else {
          setSubmitError(code || t("party.character_facts.error_submit_failed"));
        }
      } finally {
        setSubmitLoading(false);
      }
    };

    const requestNextRound = async () => {
      if (!canStartNext || !notification?.id) return;

      setNextLoading(true);
      setNextError("");
      setSubmitError("");
      try {
        const res = await api.post("events/api/party-games/character-facts/next/", {
          notification_id: notification.id,
        });

        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        const code = err?.response?.data?.error;
        setNextError(code || t("party.character_facts.error_next_failed"));
      } finally {
        setNextLoading(false);
      }
    };

    return (
      <RoundAssignmentWrapper showMessage={false}>
        {quizRoundIndex ? (
          <div className="mb-3 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
            {t("party.common.round_label", { round: quizRoundIndex })}
          </div>
        ) : gameLabel ? (
          <div className="mb-3 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
            {gameLabel}
          </div>
        ) : null}

        <div className="w-full max-w-sm rounded-3xl py-5 px-4 bg-violet-700/90 shadow-lg shadow-violet-950/70 text-left">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/70 mb-3 text-center">
            {t("party.character_facts.label_your_facts")}
          </div>

          {characterFactsItems.length > 0 && characterFactsVisible ? (
            <ul className="space-y-3">
              {characterFactsItems.map((item, idx) => (
                <li key={idx} className="rounded-2xl bg-white/10 px-3 py-3">
                  {item.en ? <p className="text-sm text-white leading-snug">{item.en}</p> : null}
                  {item.sk ? <p className="mt-1 text-sm text-white/80 leading-snug">{item.sk}</p> : null}
                </li>
              ))}
            </ul>
          ) : characterFactsItems.length > 0 ? (
            <button
              type="button"
              onClick={revealCharacterFacts}
              aria-label={t("party.character_facts.reveal_facts")}
              className="flex min-h-[132px] w-full flex-col items-center justify-center rounded-2xl bg-white/10 px-4 py-5 text-center"
            >
              <span className="text-3xl" aria-hidden="true">🔒</span>
              <span className="mt-2 text-sm font-semibold text-white">
                {t("party.character_facts.reveal_facts")}
              </span>
              <span className="mt-1 text-[11px] font-semibold text-white/55">
                {t("party.character_facts.reveal_facts_hint")}
              </span>
            </button>
          ) : (
            <p className="text-sm text-white/75 text-center">{t("party.character_facts.fallback_no_facts")}</p>
          )}
        </div>

        <div className="mt-4 max-w-sm text-xs text-white/80 leading-relaxed text-center">
          <p>
            {t("party.character_facts.body_share_clues", {
              count: Number.isFinite(characterFactsTotalCount) && characterFactsTotalCount > 0
                ? characterFactsTotalCount
                : "?",
            })}
          </p>
        </div>

        {!characterFactsRoundSolved ? (
          <div className="mt-4 w-full max-w-sm">
            <input
              type="text"
              value={answerValue}
              onChange={(e) => setAnswerValue(e.target.value)}
              placeholder={t("party.character_facts.input_placeholder")}
              className="w-full rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-white placeholder:text-white/45 outline-none"
              maxLength={255}
              autoCapitalize="words"
              autoCorrect="off"
              spellCheck={false}
            />

            <button
              type="button"
              onClick={requestSubmitAnswer}
              disabled={!canSubmit}
              className={
                "mt-3 w-full rounded-full px-4 py-2 flex items-center justify-center " +
                (!canSubmit
                  ? "bg-white/20 cursor-not-allowed"
                  : "bg-violet-500/95 shadow-md shadow-violet-950/50")
              }
            >
              <span className="text-sm font-semibold text-white text-center leading-snug">
                {submitLoading
                  ? t("party.character_facts.btn_submit_loading")
                  : submitCooldownLeft > 0
                    ? t("party.common.wait_seconds", { seconds: submitCooldownLeft })
                    : t("party.character_facts.btn_submit")}
              </span>
            </button>

            {hasCharacterFactsSubmissionResult && characterFactsSubmissionCorrectRaw === false ? (
              <div className="mt-2 text-xs text-amber-100 text-center break-words">
                {t("party.character_facts.feedback_wrong")}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="mt-4 w-full max-w-sm">
            <div className="rounded-2xl bg-emerald-600/90 px-4 py-4 shadow-md shadow-emerald-950/50">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/75 mb-2">
                {t("party.character_facts.label_solution")}
              </div>
              <div className="text-xl sm:text-2xl font-semibold text-white break-words">
                {characterFactsSolutionTitle || t("party.character_facts.feedback_correct")}
              </div>
            </div>

            <button
              type="button"
              onClick={requestNextRound}
              disabled={!canStartNext}
              className={
                "mt-3 w-full rounded-full px-4 py-2 flex items-center justify-center " +
                (!canStartNext
                  ? "bg-white/20 cursor-not-allowed"
                  : "bg-emerald-500/95 shadow-md shadow-emerald-950/50")
              }
            >
              <span className="text-sm font-semibold text-white text-center leading-snug">
                {nextLoading
                  ? t("party.character_facts.btn_next_loading")
                  : t("party.character_facts.btn_next")}
              </span>
            </button>
          </div>
        )}

        {renderAnswerRevealControl({ isSolved: characterFactsRoundSolved })}
        {submitError ? (
          <div className="mt-2 text-xs text-red-100 text-center break-words">{submitError}</div>
        ) : null}
        {nextError ? (
          <div className="mt-2 text-xs text-red-100 text-center break-words">{nextError}</div>
        ) : null}
        {renderRulesFoldout()}
      </RoundAssignmentWrapper>
    );
  }

  if (mode === "question_words") {
    const canSubmit =
      !submitLoading &&
      !questionWordsRoundSolved &&
      submitCooldownLeft <= 0 &&
      answerValue.trim().length > 0;
    const canStartNext = questionWordsRoundSolved && questionWordsCanStartNext && !nextLoading;

    const revealQuestionWord = (index) => {
      window.clearTimeout(questionWordHideTimer.current);
      setQuestionWordVisibleIndex(index);
      questionWordHideTimer.current = window.setTimeout(() => {
        setQuestionWordVisibleIndex(null);
      }, 3000);
    };

    const requestSubmitAnswer = async () => {
      if (!canSubmit || !notification?.id) return;

      setSubmitLoading(true);
      setSubmitError("");
      setNextError("");
      try {
        const res = await api.post("events/api/party-games/question-words/submit/", {
          notification_id: notification.id,
          answer: answerValue.trim(),
        });

        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        const code = err?.response?.data?.error;
        const retryAfterSeconds = Number(err?.response?.data?.retry_after_seconds || 0);
        const updatedNotification = err?.response?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
        if (code === "submit_cooldown" && retryAfterSeconds > 0) {
          setSubmitCooldownLeft(Math.floor(retryAfterSeconds));
          setSubmitError(t("party.common.cooldown_error", { seconds: Math.floor(retryAfterSeconds) }));
        } else {
          setSubmitError(code || t("party.question_words.error_submit_failed"));
        }
      } finally {
        setSubmitLoading(false);
      }
    };

    const requestNextRound = async () => {
      if (!canStartNext || !notification?.id) return;

      setNextLoading(true);
      setNextError("");
      setSubmitError("");
      try {
        const res = await api.post("events/api/party-games/question-words/next/", {
          notification_id: notification.id,
        });

        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        const code = err?.response?.data?.error;
        setNextError(code || t("party.question_words.error_next_failed"));
      } finally {
        setNextLoading(false);
      }
    };

    return (
      <RoundAssignmentWrapper showMessage={false}>
        {quizRoundIndex ? (
          <div className="mb-3 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
            {t("party.common.round_label", { round: quizRoundIndex })}
          </div>
        ) : gameLabel ? (
          <div className="mb-3 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
            {gameLabel}
          </div>
        ) : null}

        <div className="w-full max-w-sm rounded-3xl py-5 px-4 bg-cyan-700/90 shadow-lg shadow-cyan-950/70 text-left">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/70 mb-3 text-center">
            {t("party.question_words.label_your_words")}
          </div>

          {questionWordsItems.length > 0 ? (
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {questionWordsItems.map((item, idx) => {
                const visible = questionWordVisibleIndex === idx;
                const localizedWord = pickLocalizedText(item.en, item.sk);
                return (
                  <li key={idx} className="overflow-hidden rounded-2xl bg-white/10 text-center">
                    <button
                      type="button"
                      onClick={() => revealQuestionWord(idx)}
                      aria-label={t("party.question_words.reveal_word")}
                      className="flex min-h-[72px] w-full flex-col items-center justify-center px-3 py-3"
                    >
                      {visible ? (
                        <>
                          <p className="text-lg font-semibold leading-snug text-white">{localizedWord}</p>
                          <p className="mt-1 text-[10px] font-semibold text-white/50">
                            {t("party.question_words.word_hides_again")}
                          </p>
                        </>
                      ) : (
                        <>
                          <span className="text-xl" aria-hidden="true">🔒</span>
                          <span className="mt-1 text-xs font-semibold text-white/80">
                            {t("party.question_words.reveal_word")}
                          </span>
                        </>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-white/75 text-center">{t("party.question_words.fallback_no_words")}</p>
          )}
        </div>

        <div className="mt-4 max-w-sm text-xs text-white/80 leading-relaxed text-center">
          <p>
            {t("party.question_words.body_share_clues", {
              count: Number.isFinite(questionWordsTotalCount) && questionWordsTotalCount > 0
                ? questionWordsTotalCount
                : "?",
            })}
          </p>
        </div>

        {!questionWordsRoundSolved ? (
          <div className="mt-4 w-full max-w-sm">
            <input
              type="text"
              value={answerValue}
              onChange={(e) => setAnswerValue(e.target.value)}
              placeholder={t("party.question_words.input_placeholder")}
              className="w-full rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-white placeholder:text-white/45 outline-none"
              maxLength={255}
              autoCapitalize="words"
              autoCorrect="off"
              spellCheck={false}
            />

            <button
              type="button"
              onClick={requestSubmitAnswer}
              disabled={!canSubmit}
              className={
                "mt-3 w-full rounded-full px-4 py-2 flex items-center justify-center " +
                (!canSubmit
                  ? "bg-white/20 cursor-not-allowed"
                  : "bg-cyan-500/95 shadow-md shadow-cyan-950/50")
              }
            >
              <span className="text-sm font-semibold text-white text-center leading-snug">
                {submitLoading
                  ? t("party.question_words.btn_submit_loading")
                  : submitCooldownLeft > 0
                    ? t("party.common.wait_seconds", { seconds: submitCooldownLeft })
                    : t("party.question_words.btn_submit")}
              </span>
            </button>

            {hasQuestionWordsSubmissionResult && questionWordsSubmissionCorrectRaw === false ? (
              <div className="mt-2 text-xs text-amber-100 text-center break-words">
                {t("party.question_words.feedback_wrong")}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="mt-4 w-full max-w-sm">
            <div className="rounded-2xl bg-emerald-600/90 px-4 py-4 shadow-md shadow-emerald-950/50">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/75 mb-2">
                {t("party.question_words.label_solution")}
              </div>
              <div className="text-xl sm:text-2xl font-semibold text-white break-words">
                {questionWordsSolutionTitleEnglish || t("party.question_words.feedback_correct")}
              </div>
            </div>

            <button
              type="button"
              onClick={requestNextRound}
              disabled={!canStartNext}
              className={
                "mt-3 w-full rounded-full px-4 py-2 flex items-center justify-center " +
                (!canStartNext
                  ? "bg-white/20 cursor-not-allowed"
                  : "bg-emerald-500/95 shadow-md shadow-emerald-950/50")
              }
            >
              <span className="text-sm font-semibold text-white text-center leading-snug">
                {nextLoading
                  ? t("party.question_words.btn_next_loading")
                  : t("party.question_words.btn_next")}
              </span>
            </button>
          </div>
        )}

        {renderAnswerRevealControl({ isSolved: questionWordsRoundSolved })}
        {submitError ? (
          <div className="mt-2 text-xs text-red-100 text-center break-words">{submitError}</div>
        ) : null}
        {nextError ? (
          <div className="mt-2 text-xs text-red-100 text-center break-words">{nextError}</div>
        ) : null}
        {renderRulesFoldout()}
      </RoundAssignmentWrapper>
    );
  }

  if (mode === "glyph_relay") {
    const isGuesser = glyphRelayRole === "guesser";
    const isExplainer = glyphRelayRole === "explainer";
    const currentFragmentStep = Number.isFinite(glyphRelayCurrentFragmentIndex)
      ? Math.max(1, Math.floor(glyphRelayCurrentFragmentIndex) + 1)
      : 1;
    const canAdvanceFragment =
      isExplainer &&
      !submitLoading &&
      !glyphRelayRoundSolved &&
      glyphRelayPhase === "explain";
    const isGuessPhaseOpen = glyphRelayPhase === "guess";
    const canSubmit =
      isGuesser &&
      !submitLoading &&
      !glyphRelayRoundSolved &&
      isGuessPhaseOpen &&
      glyphRelaySelected;
    const canStartNext = glyphRelayRoundSolved && glyphRelayCanStartNext && !nextLoading;

    const requestNextFragment = async () => {
      if (!canAdvanceFragment || !notification?.id) return;
      setSubmitLoading(true);
      setSubmitError("");
      setNextError("");
      try {
        const res = await api.post("events/api/party-games/glyph-relay/next-fragment/", {
          notification_id: notification.id,
        });
        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        const code = err?.response?.data?.error;
        const updatedNotification = err?.response?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
        setSubmitError(code || t("party.glyph_relay.error_advance_failed"));
      } finally {
        setSubmitLoading(false);
        setGlyphRelayRevealPressed(false);
      }
    };

    const requestSubmitAnswer = async () => {
      if (!canSubmit || !notification?.id) return;
      setSubmitLoading(true);
      setSubmitError("");
      setNextError("");
      try {
        const res = await api.post("events/api/party-games/glyph-relay/submit/", {
          notification_id: notification.id,
          selected_option_id: glyphRelaySelected,
        });
        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        const code = err?.response?.data?.error;
        const updatedNotification = err?.response?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
        setSubmitError(code || t("party.glyph_relay.error_submit_failed"));
      } finally {
        setSubmitLoading(false);
      }
    };

    const requestNextRound = async () => {
      if (!canStartNext || !notification?.id) return;
      setNextLoading(true);
      setNextError("");
      setSubmitError("");
      try {
        const res = await api.post("events/api/party-games/glyph-relay/next/", {
          notification_id: notification.id,
        });
        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        const code = err?.response?.data?.error;
        setNextError(code || t("party.glyph_relay.error_next_failed"));
      } finally {
        setNextLoading(false);
      }
    };

    return (
      <RoundAssignmentWrapper showMessage={false}>
        {quizRoundIndex ? (
          <div className="mb-3 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
            {t("party.common.round_label", { round: quizRoundIndex })}
          </div>
        ) : gameLabel ? (
          <div className="mb-3 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
            {gameLabel}
          </div>
        ) : null}

        <div className="w-full max-w-sm rounded-3xl py-5 px-4 bg-amber-700/90 shadow-lg shadow-amber-950/70 text-center">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/70 mb-3">
            {isGuesser
              ? isGuessPhaseOpen
                ? t("party.glyph_relay.heading_choose")
                : t("party.glyph_relay.heading_wait")
              : isExplainer
                ? t("party.glyph_relay.heading_fragment", {
                    current: currentFragmentStep,
                    total: glyphRelayFragmentCount || 4,
                  })
                : t("party.glyph_relay.heading_draw")}
          </div>

          {isGuesser ? (
            <p className="text-sm text-white/90 leading-relaxed">
              {t("party.glyph_relay.guesser_hint")}
            </p>
          ) : isExplainer ? (
            <div className="space-y-3">
              <p className="text-sm text-white/90 leading-relaxed">
                {t("party.glyph_relay.explainer_hint")}
              </p>
              <button
                type="button"
                onMouseDown={() => setGlyphRelayRevealPressed(true)}
                onMouseUp={() => setGlyphRelayRevealPressed(false)}
                onMouseLeave={() => setGlyphRelayRevealPressed(false)}
                onTouchStart={() => setGlyphRelayRevealPressed(true)}
                onTouchEnd={() => setGlyphRelayRevealPressed(false)}
                className="w-full rounded-full bg-white/15 border border-white/20 px-4 py-3 text-sm font-semibold text-white"
              >
                {t("party.glyph_relay.hold_reveal")}
              </button>
              {glyphRelayRevealPressed && glyphRelayCurrentFragment?.imageUrl ? (
                <div className="rounded-2xl bg-white p-3">
                  <img
                    src={glyphRelayCurrentFragment.imageUrl}
                    alt={t("party.glyph_relay.fragment_alt", { number: currentFragmentStep })}
                    className="mx-auto w-full max-w-[220px] rounded-xl border border-slate-200"
                  />
                </div>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-white/90 leading-relaxed">
              {t("party.glyph_relay.listener_hint")}
            </p>
          )}
        </div>

        {isGuesser ? (
          <div className="mt-4 w-full max-w-md">
            {isGuessPhaseOpen ? (
              <div className="grid grid-cols-2 gap-3">
                {glyphRelayOptions.map((option) => {
                  const selected = glyphRelaySelected === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setGlyphRelaySelected(option.id)}
                      className={
                        "rounded-2xl border p-3 transition " +
                        (selected
                          ? "border-white bg-white/20"
                          : "border-white/15 bg-white/8")
                      }
                    >
                      <img
                        src={option.imageUrl}
                        alt={option.label}
                        className="mx-auto w-full max-w-[120px] rounded-xl bg-white"
                      />
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-white/20 bg-white/8 px-4 py-6 text-sm text-white/80">
                {t("party.glyph_relay.options_wait")}
              </div>
            )}

            {!glyphRelayRoundSolved ? (
              <button
                type="button"
                onClick={requestSubmitAnswer}
                disabled={!canSubmit}
                className={
                  "mt-3 w-full rounded-full px-4 py-2 flex items-center justify-center " +
                  (!canSubmit
                    ? "bg-white/20 cursor-not-allowed"
                    : "bg-amber-500/95 shadow-md shadow-amber-950/50")
                }
                >
                  <span className="text-sm font-semibold text-white text-center leading-snug">
                  {submitLoading ? t("party.glyph_relay.btn_checking") : t("party.glyph_relay.btn_submit")}
                </span>
              </button>
            ) : (
            <div className="mt-3 rounded-2xl bg-emerald-600/90 px-4 py-4 shadow-md shadow-emerald-950/50">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/75 mb-2">
                {t("party.glyph_relay.solution")}
              </div>
              {glyphRelaySolutionOption?.imageUrl ? (
                <img
                  src={glyphRelaySolutionOption.imageUrl}
                  alt={glyphRelaySolutionOption.label || glyphRelaySolutionTitle || t("party.glyph_relay.correct_glyph")}
                  className="mx-auto mb-3 w-full max-w-[140px] rounded-xl bg-white"
                />
              ) : null}
              <div className="text-lg font-semibold text-white break-words">
                {glyphRelaySolutionTitle || glyphRelaySolutionOption?.label || t("party.glyph_relay.correct_glyph")}
              </div>
            </div>
            )}
          </div>
        ) : isExplainer ? (
          <button
            type="button"
            onClick={requestNextFragment}
            disabled={!canAdvanceFragment}
            className={
              "mt-4 w-full max-w-sm rounded-full px-4 py-2 flex items-center justify-center " +
              (!canAdvanceFragment
                ? "bg-white/20 cursor-not-allowed"
                : "bg-amber-500/95 shadow-md shadow-amber-950/50")
            }
          >
            <span className="text-sm font-semibold text-white text-center leading-snug">
              {submitLoading
                ? t("party.glyph_relay.btn_advancing")
                : glyphRelayPhase === "guess"
                  ? t("party.glyph_relay.guess_phase_open")
                  : currentFragmentStep >= (glyphRelayFragmentCount || 4)
                    ? t("party.glyph_relay.finish_explaining")
                    : t("party.glyph_relay.next_fragment")}
            </span>
          </button>
        ) : (
          <div className="mt-4 max-w-sm text-xs text-white/80 leading-relaxed text-center">
            <p>{t("party.glyph_relay.drawing_hint")}</p>
          </div>
        )}

        {!glyphRelayRoundSolved && isGuesser && glyphRelayPhase !== "guess" ? (
          <div className="mt-3 text-xs text-white/80 text-center">
            {t("party.glyph_relay.wait_explainers")}
          </div>
        ) : null}

        {hasGlyphRelaySubmissionResult && glyphRelaySubmissionCorrectRaw === false ? (
          <div className="mt-2 text-xs text-amber-100 text-center break-words">
            {t("party.glyph_relay.feedback_wrong")}
          </div>
        ) : null}

        {glyphRelayRoundSolved ? (
          <button
            type="button"
            onClick={requestNextRound}
            disabled={!canStartNext}
            className={
              "mt-3 w-full max-w-sm rounded-full px-4 py-2 flex items-center justify-center " +
              (!canStartNext
                ? "bg-white/20 cursor-not-allowed"
                : "bg-emerald-500/95 shadow-md shadow-emerald-950/50")
            }
          >
            <span className="text-sm font-semibold text-white text-center leading-snug">
              {nextLoading ? t("party.glyph_relay.btn_next_loading") : t("party.glyph_relay.btn_next")}
            </span>
          </button>
        ) : null}

        {submitError ? (
          <div className="mt-2 text-xs text-red-100 text-center break-words">{submitError}</div>
        ) : null}
        {nextError ? (
          <div className="mt-2 text-xs text-red-100 text-center break-words">{nextError}</div>
        ) : null}
        {renderRulesFoldout()}
      </RoundAssignmentWrapper>
    );
  }

  if (mode === "mixed_signals") {
    const isGuesser = mixedSignalsRole === "guesser";
    const mixedSignalsProgress = [0, 1, 2].map((idx) => Boolean(mixedSignalsWordResults[idx]));
    const mixedSignalsCurrentIndex = effectiveMixedSignalsRoundSolved
      ? -1
      : Math.max(0, mixedSignalsProgress.findIndex((isCorrect) => !isCorrect));
    const mixedSignalsCurrentAnswer = mixedSignalsCurrentIndex >= 0
      ? String(answerParts[mixedSignalsCurrentIndex] || "").trim()
      : "";
    const canSubmit =
      isGuesser &&
      !submitLoading &&
      !effectiveMixedSignalsRoundSolved &&
      submitCooldownLeft <= 0 &&
      mixedSignalsCurrentIndex >= 0 &&
      mixedSignalsCurrentAnswer.length > 0;
    const canStartNext = effectiveMixedSignalsRoundSolved && mixedSignalsCanStartNext && !nextLoading;
    const mixedSignalsFeedbackText = (() => {
      if (!hasMixedSignalsSubmissionResult || mixedSignalsSubmissionCorrectRaw !== false) return "";
      if (mixedSignalsCurrentIndex < 0) return "";
      if (mixedSignalsCorrectSubmittedCount > 0 && !mixedSignalsSubmittedMask[mixedSignalsCurrentIndex]) {
        return t("party.mixed_signals.feedback_correct_next_word", {
          number: mixedSignalsCurrentIndex + 1,
        });
      }
      if (mixedSignalsSubmittedMask[mixedSignalsCurrentIndex] && !mixedSignalsProgress[mixedSignalsCurrentIndex]) {
        return t("party.mixed_signals.feedback_word_wrong", {
          number: mixedSignalsCurrentIndex + 1,
        });
      }
      return "";
    })();

    const updateAnswerPart = (index, value) => {
      setAnswerParts((prev) => {
        const next = [...prev];
        next[index] = value;
        return next;
      });
    };

    const requestSubmitAnswer = async () => {
      if (!canSubmit || !notification?.id) return;
      setSubmitLoading(true);
      setSubmitError("");
      setNextError("");
      try {
        const submittedAnswers = ["", "", ""];
        submittedAnswers[mixedSignalsCurrentIndex] = mixedSignalsCurrentAnswer;
        const res = await api.post("events/api/party-games/mixed-signals/submit/", {
          notification_id: notification.id,
          answers: submittedAnswers,
        });
        if (res?.data?.is_correct) {
          setMixedSignalsSolvedOverrideRoundId(mixedSignalsRoundId);
        }
        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        const code = err?.response?.data?.error;
        const retryAfterSeconds = Number(err?.response?.data?.retry_after_seconds || 0);
        const updatedNotification = err?.response?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
        if (code === "submit_cooldown" && retryAfterSeconds > 0) {
          setSubmitCooldownLeft(Math.floor(retryAfterSeconds));
          setSubmitError(t("party.mixed_signals.error_cooldown", {
            seconds: Math.floor(retryAfterSeconds),
          }));
        } else {
          setSubmitError(code || t("party.mixed_signals.error_submit_failed"));
        }
      } finally {
        setSubmitLoading(false);
      }
    };

    const requestNextRound = async () => {
      if (!canStartNext || !notification?.id) return;
      setNextLoading(true);
      setNextError("");
      setSubmitError("");
      try {
        const res = await api.post("events/api/party-games/mixed-signals/next/", {
          notification_id: notification.id,
        });
        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        const code = err?.response?.data?.error;
        setNextError(code || t("party.mixed_signals.error_next_failed"));
      } finally {
        setNextLoading(false);
      }
    };

    return (
      <RoundAssignmentWrapper showMessage={false}>
        {quizRoundIndex ? (
          <div className="mb-3 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
            {t("party.common.round_label", { round: quizRoundIndex })}
          </div>
        ) : gameLabel ? (
          <div className="mb-3 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
            {gameLabel}
          </div>
        ) : null}

        <div className="w-full max-w-sm rounded-3xl py-5 px-4 bg-fuchsia-700/90 shadow-lg shadow-fuchsia-950/70 text-left">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/70 mb-3 text-center">
            {isGuesser
              ? t("party.mixed_signals.heading_guesser")
              : t("party.mixed_signals.heading_explainer")}
          </div>

          {isGuesser ? (
            <div className="text-center">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/60">
                {t("party.mixed_signals.word_of_total", {
                  number: mixedSignalsCurrentIndex + 1,
                  total: 3,
                })}
              </div>
              <div className="mt-2 text-3xl sm:text-4xl font-extrabold text-white">???</div>
              <p className="mt-3 text-sm text-white/80 leading-relaxed">
                {t("party.mixed_signals.guesser_hint")}
              </p>
              <div className="mt-4 grid grid-cols-3 gap-2">
                {mixedSignalsProgress.map((isCorrect, idx) => (
                  <div
                    key={idx}
                    className={
                      "rounded-2xl border px-2 py-2 text-center text-xs font-semibold " +
                      (isCorrect
                        ? "border-emerald-200/60 bg-emerald-500/35 text-white"
                        : idx === mixedSignalsCurrentIndex
                          ? "border-white/60 bg-white/20 text-white"
                          : "border-white/15 bg-white/8 text-white/55")
                    }
                  >
                    {isCorrect
                      ? t("party.mixed_signals.status_done")
                      : idx === mixedSignalsCurrentIndex
                        ? t("party.mixed_signals.status_now")
                        : t("party.mixed_signals.status_later")}
                  </div>
                ))}
              </div>
            </div>
          ) : mixedSignalsWords.length > 0 ? (
            <div className="space-y-3">
              {mixedSignalsWords.map((item, idx) => {
                const isCurrent = idx === mixedSignalsCurrentIndex;
                const isDone = mixedSignalsProgress[idx];
                return (
                  <div
                    key={idx}
                    className={
                      "rounded-2xl border px-3 py-3 text-center " +
                      (isCurrent
                        ? "border-white/70 bg-white/20 shadow-[0_0_0_1px_rgba(255,255,255,0.2)]"
                        : isDone
                          ? "border-emerald-200/50 bg-emerald-500/25"
                          : "border-white/10 bg-white/8")
                    }
                  >
                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/60 mb-2">
                      {isDone
                        ? t("party.mixed_signals.status_done")
                        : isCurrent
                          ? t("party.mixed_signals.explain_now")
                          : t("party.mixed_signals.word_label", { number: idx + 1 })}
                    </div>
                    {item.en ? (
                      <p className={`${isCurrent ? "text-3xl" : "text-lg"} font-extrabold text-white leading-snug`}>
                        {item.en}
                      </p>
                    ) : null}
                    {item.sk ? <p className="mt-1 text-sm text-white/75 leading-snug">{item.sk}</p> : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-white/75 text-center">
              {t("party.mixed_signals.fallback_no_cards")}
            </p>
          )}
        </div>

        {!effectiveMixedSignalsRoundSolved ? (
          <div className="mt-4 max-w-sm text-xs text-white/80 leading-relaxed text-center">
            <p>
              {isGuesser
                ? t("party.mixed_signals.guesser_unlock_hint")
                : t("party.mixed_signals.explainer_current_hint", {
                    number: mixedSignalsCurrentIndex + 1,
                  })}
            </p>
          </div>
        ) : null}

        {!effectiveMixedSignalsRoundSolved ? (
          isGuesser ? (
            <div className="mt-4 w-full max-w-sm space-y-3">
              <div className="grid grid-cols-3 gap-2">
                {[0, 1, 2].map((idx) => {
                  const isCorrect = mixedSignalsProgress[idx];
                  const isCurrent = idx === mixedSignalsCurrentIndex;
                  const submitted = String(mixedSignalsSubmittedWords[idx] || "").trim();
                  return (
                    <div
                      key={idx}
                      className={
                        "min-h-[54px] rounded-2xl border px-2 py-2 text-center text-xs font-semibold flex items-center justify-center " +
                        (isCorrect
                          ? "border-emerald-300 bg-emerald-500/35 text-white"
                          : isCurrent
                            ? "border-white/45 bg-white/15 text-white"
                            : "border-white/15 bg-white/8 text-white/50")
                      }
                    >
                      {isCorrect
                        ? (submitted || t("party.mixed_signals.word_label", { number: idx + 1 }))
                        : isCurrent
                          ? t("party.mixed_signals.word_label", { number: idx + 1 })
                          : "___"}
                    </div>
                  );
                })}
              </div>

              <input
                type="text"
                value={answerParts[mixedSignalsCurrentIndex] || ""}
                onChange={(e) => updateAnswerPart(mixedSignalsCurrentIndex, e.target.value)}
                placeholder={t("party.mixed_signals.input_placeholder", {
                  number: mixedSignalsCurrentIndex + 1,
                })}
                className="w-full rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-white placeholder:text-white/45 outline-none"
                maxLength={64}
                autoCapitalize="words"
                autoCorrect="off"
                spellCheck={false}
              />

              <button
                type="button"
                onClick={requestSubmitAnswer}
                disabled={!canSubmit}
                className={
                  "w-full rounded-full px-4 py-2 flex items-center justify-center " +
                  (!canSubmit
                    ? "bg-white/20 cursor-not-allowed"
                    : "bg-fuchsia-500/95 shadow-md shadow-fuchsia-950/50")
                }
              >
                <span className="text-sm font-semibold text-white text-center leading-snug">
                  {submitLoading
                    ? t("party.mixed_signals.btn_checking")
                    : submitCooldownLeft > 0
                      ? t("party.mixed_signals.btn_wait", { seconds: submitCooldownLeft })
                      : t("party.mixed_signals.btn_submit_word", {
                          number: mixedSignalsCurrentIndex + 1,
                        })}
                </span>
              </button>

              {mixedSignalsFeedbackText ? (
                <div className="mt-1 text-xs text-amber-100 text-center break-words">
                  {mixedSignalsFeedbackText}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="mt-4 w-full max-w-sm rounded-2xl border border-white/15 bg-white/8 px-4 py-4 text-center">
              <p className="text-sm text-white/80 leading-relaxed">
                {t("party.mixed_signals.explainer_wait_hint")}
              </p>
            </div>
          )
        ) : (
          <div className="mt-4 w-full max-w-sm">
            <div className="rounded-2xl border border-emerald-200/45 bg-emerald-500 px-4 py-4 shadow-[0_12px_28px_rgba(6,95,70,0.38)]">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/75 mb-2">
                {t("party.mixed_signals.label_solution")}
              </div>
              <div className="text-xl sm:text-2xl font-semibold text-white break-words">
                {mixedSignalsSolutionTitle || t("party.mixed_signals.feedback_correct")}
              </div>
            </div>

            <button
              type="button"
              onClick={requestNextRound}
              disabled={!canStartNext}
              className={
                "mt-3 w-full rounded-full px-4 py-2 flex items-center justify-center " +
                (!canStartNext
                  ? "bg-white/20 cursor-not-allowed"
                  : "bg-emerald-500/95 shadow-md shadow-emerald-950/50")
              }
            >
              <span className="text-sm font-semibold text-white text-center leading-snug">
                {nextLoading
                  ? t("party.mixed_signals.btn_next_loading")
                  : t("party.mixed_signals.btn_next")}
              </span>
            </button>
          </div>
        )}

        {submitError ? (
          <div className="mt-2 text-xs text-red-100 text-center break-words">{submitError}</div>
        ) : null}
        {nextError ? (
          <div className="mt-2 text-xs text-red-100 text-center break-words">{nextError}</div>
        ) : null}
        {renderRulesFoldout()}
      </RoundAssignmentWrapper>
    );
  }

  if (mode === "common_thread") {
    const isGuesser = commonThreadRole === "guesser";
    const wordsTotal = Number.isFinite(commonThreadWordsTotal) && commonThreadWordsTotal > 0
      ? Math.floor(commonThreadWordsTotal)
      : Math.max(1, commonThreadAnswers.length, commonThreadWordGuesses.length);
    const answerSlots = Array.from({ length: wordsTotal }, (_, idx) => idx);
    const wordsComplete =
      wordsTotal > 0 &&
      commonThreadWordResults.slice(0, wordsTotal).length >= wordsTotal &&
      commonThreadWordResults.slice(0, wordsTotal).every(Boolean);
    const categoryPhase = commonThreadPhase === "category" || wordsComplete;
    const solutionCategory = pickLocalizedText(commonThreadSolutionCategoryEn, commonThreadSolutionCategorySk);
    const canSubmitWords =
      isGuesser &&
      !submitLoading &&
      !commonThreadRoundSolved &&
      !categoryPhase &&
      commonThreadAnswers.some((item) => String(item || "").trim().length > 0);
    const canSubmitCategory =
      isGuesser &&
      !submitLoading &&
      !commonThreadRoundSolved &&
      categoryPhase &&
      String(commonThreadCategoryValue || "").trim().length > 0;
    const canStartNext = commonThreadRoundSolved && commonThreadCanStartNext && !nextLoading;

    const updateCommonThreadAnswer = (index, value) => {
      setCommonThreadAnswers((prev) => {
        const next = Array.from({ length: wordsTotal }, (_, idx) => String(prev[idx] || ""));
        next[index] = value;
        return next;
      });
    };

    const requestSubmitCommonThread = async () => {
      const canSubmit = categoryPhase ? canSubmitCategory : canSubmitWords;
      if (!canSubmit || !notification?.id) return;
      setSubmitLoading(true);
      setSubmitError("");
      setNextError("");
      try {
        const payload = {
          notification_id: notification.id,
          answers: commonThreadAnswers.map((item) => String(item || "").trim()),
        };
        if (categoryPhase) {
          payload.category = String(commonThreadCategoryValue || "").trim();
        }
        const res = await api.post("events/api/party-games/common-thread/submit/", payload);
        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        const updatedNotification = err?.response?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
        setSubmitError(err?.response?.data?.error || t("party.common_thread.error_submit_failed"));
      } finally {
        setSubmitLoading(false);
      }
    };

    const requestNextRound = async () => {
      if (!canStartNext || !notification?.id) return;
      setNextLoading(true);
      setNextError("");
      setSubmitError("");
      try {
        const res = await api.post("events/api/party-games/common-thread/next/", {
          notification_id: notification.id,
        });
        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        setNextError(err?.response?.data?.error || t("party.common_thread.error_next_failed"));
      } finally {
        setNextLoading(false);
      }
    };

    return (
      <RoundAssignmentWrapper showMessage={false}>
        {quizRoundIndex ? (
          <div className="mb-3 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
            {t("party.common.round_label", { round: quizRoundIndex })}
          </div>
        ) : gameLabel ? (
          <div className="mb-3 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
            {gameLabel}
          </div>
        ) : null}

        <div className="w-full max-w-sm rounded-3xl py-5 px-4 bg-teal-700/90 shadow-lg shadow-teal-950/70 text-center">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/70 mb-3">
            {isGuesser ? t("party.common_thread.heading_guesser") : t("party.common_thread.heading_explainer")}
          </div>
          {isGuesser ? (
            <div>
              <div className="text-3xl sm:text-4xl font-extrabold text-white">{t("party.common_thread.title")}</div>
              <p className="mt-3 text-sm text-white/80 leading-relaxed">
                {t("party.common_thread.guesser_body")}
              </p>
            </div>
          ) : commonThreadWord ? (
            <div className="rounded-2xl bg-white/10 px-4 py-4">
              {commonThreadWord.en ? (
                <div className="text-2xl font-black text-white leading-tight">{commonThreadWord.en}</div>
              ) : null}
              {commonThreadWord.sk ? (
                <div className="mt-2 text-lg font-semibold text-white/80 leading-tight">{commonThreadWord.sk}</div>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-white/75">{t("party.common_thread.wait_word")}</p>
          )}
        </div>

        {!isGuesser && !commonThreadRoundSolved ? (
          <div className="mt-4 max-w-sm text-xs text-white/80 leading-relaxed text-center">
            {t("party.common_thread.explainer_hint")}
          </div>
        ) : null}

        {!isGuesser && categoryPhase && !commonThreadRoundSolved
          ? renderAnswerRevealControl({ isSolved: commonThreadRoundSolved, force: true })
          : null}

        {isGuesser && !commonThreadRoundSolved ? (
          <div className="mt-4 w-full max-w-sm space-y-3">
            {answerSlots.map((idx) => {
              const submitted = String(commonThreadWordGuesses[idx] || "").trim().length > 0;
              const slotOk = submitted ? Boolean(commonThreadWordResults[idx]) : null;
              const ringClass =
                slotOk == null
                  ? "border-white/15 bg-white/10"
                  : slotOk
                    ? "border-emerald-300 bg-emerald-500/40 shadow-[0_0_0_1px_rgba(110,231,183,0.28)]"
                    : "border-rose-300/70 bg-rose-500/20";
              return (
                <input
                  key={idx}
                  type="text"
                  value={commonThreadAnswers[idx] || ""}
                  onChange={(e) => updateCommonThreadAnswer(idx, e.target.value)}
                  placeholder={t("party.common_thread.word_placeholder", { number: idx + 1 })}
                  disabled={categoryPhase && Boolean(commonThreadWordResults[idx])}
                  className={`w-full rounded-2xl border px-4 py-3 text-white placeholder:text-white/45 outline-none disabled:opacity-80 ${ringClass}`}
                  maxLength={64}
                  autoCapitalize="words"
                  autoCorrect="off"
                  spellCheck={false}
                />
              );
            })}

            {!categoryPhase ? (
              <button
                type="button"
                onClick={requestSubmitCommonThread}
                disabled={!canSubmitWords}
                className={
                  "w-full rounded-full px-4 py-2 flex items-center justify-center " +
                  (!canSubmitWords
                    ? "bg-white/20 cursor-not-allowed"
                    : "bg-teal-500/95 shadow-md shadow-teal-950/50")
                }
              >
                <span className="text-sm font-semibold text-white text-center leading-snug">
                  {submitLoading ? t("party.common_thread.btn_checking") : t("party.common_thread.btn_submit_words")}
                </span>
              </button>
            ) : (
              <div className="rounded-2xl border border-white/15 bg-white/8 px-3 py-3">
                <div className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-white/65">
                  {t("party.common_thread.category_label")}
                </div>
                <input
                  type="text"
                  value={commonThreadCategoryValue}
                  onChange={(e) => setCommonThreadCategoryValue(e.target.value)}
                  placeholder={t("party.common_thread.category_placeholder")}
                  className="w-full rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-white placeholder:text-white/45 outline-none"
                  maxLength={96}
                  autoCapitalize="words"
                  autoCorrect="off"
                  spellCheck={false}
                />
                <button
                  type="button"
                  onClick={requestSubmitCommonThread}
                  disabled={!canSubmitCategory}
                  className={
                    "mt-3 w-full rounded-full px-4 py-2 flex items-center justify-center " +
                    (!canSubmitCategory
                      ? "bg-white/20 cursor-not-allowed"
                      : "bg-teal-500/95 shadow-md shadow-teal-950/50")
                  }
                >
                  <span className="text-sm font-semibold text-white text-center leading-snug">
                    {submitLoading ? t("party.common_thread.btn_checking") : t("party.common_thread.btn_submit_category")}
                  </span>
                </button>
                {renderAnswerRevealControl({ isSolved: commonThreadRoundSolved, force: true })}
                {hasCommonThreadCategoryResult && commonThreadCategoryCorrectRaw === false ? (
                  <div className="mt-2 text-xs text-amber-100 text-center break-words">
                    {t("party.common_thread.category_wrong")}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        ) : null}

        {commonThreadRoundSolved ? (
          <div className="mt-4 w-full max-w-sm">
            <div className="rounded-2xl bg-emerald-600/90 px-4 py-4 shadow-md shadow-emerald-950/50">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/75 mb-2">
                {t("party.common_thread.connection")}
              </div>
              <div className="text-xl sm:text-2xl font-semibold text-white break-words">
                {solutionCategory || t("party.common_thread.correct")}
              </div>
              {commonThreadSolutionWords.length > 0 ? (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {commonThreadSolutionWords.map((item, idx) => (
                    <div key={idx} className="rounded-xl bg-white/12 px-2 py-2 text-sm text-white/90">
                      {item.en || item.sk}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

            <button
              type="button"
              onClick={requestNextRound}
              disabled={!canStartNext}
              className={
                "mt-3 w-full rounded-full px-4 py-2 flex items-center justify-center " +
                (!canStartNext
                  ? "bg-white/20 cursor-not-allowed"
                  : "bg-emerald-500/95 shadow-md shadow-emerald-950/50")
              }
            >
              <span className="text-sm font-semibold text-white text-center leading-snug">
                {nextLoading ? t("party.common_thread.btn_next_loading") : t("party.common_thread.btn_next")}
              </span>
            </button>
          </div>
        ) : null}

        {submitError ? (
          <div className="mt-2 text-xs text-red-100 text-center break-words">{submitError}</div>
        ) : null}
        {nextError ? (
          <div className="mt-2 text-xs text-red-100 text-center break-words">{nextError}</div>
        ) : null}
        {renderRulesFoldout()}
      </RoundAssignmentWrapper>
    );
  }

  if (mode === "line_puzzle") {
    const hasTile = Boolean(linePuzzleTileDataUrl);
    const tileNo =
      Number.isFinite(linePuzzleTileIndex) && linePuzzleTileIndex > 0
        ? Math.floor(linePuzzleTileIndex)
        : null;

    const canSubmit = !submitLoading && submitCooldownLeft <= 0 && Boolean(answerValue.trim());

    const submitAnswer = async () => {
      if (!canSubmit) return;
      setSubmitLoading(true);
      setSubmitError("");
      try {
        const res = await api.post("events/api/party-games/line-puzzle/submit/", {
          notification_id: notification.id,
          answer: answerValue.trim(),
        });
        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        const code = err?.response?.data?.error;
        const retryAfterSeconds = Number(err?.response?.data?.retry_after_seconds || 0);
        const updatedNotification = err?.response?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
        if (code === "submit_cooldown" && retryAfterSeconds > 0) {
          setSubmitCooldownLeft(Math.floor(retryAfterSeconds));
          setSubmitError(t("party.common.cooldown_error", { seconds: Math.floor(retryAfterSeconds) }));
        } else {
          setSubmitError(code || t("party.line_puzzle.error_submit_failed"));
        }
      } finally {
        setSubmitLoading(false);
      }
    };

    const requestNextRound = async () => {
      if (nextLoading) return;
      setNextLoading(true);
      setNextError("");
      try {
        const res = await api.post("events/api/party-games/line-puzzle/next/", {
          notification_id: notification.id,
        });
        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        const code = err?.response?.data?.error;
        setNextError(code || t("party.line_puzzle.error_next_failed"));
      } finally {
        setNextLoading(false);
      }
    };

    return (
      <RoundAssignmentWrapper showMessage={false}>
        <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-indigo-100/80 mb-3">
          {t("party.line_puzzle.pill_title")}
          {gameLabel ? ` · ${gameLabel}` : ""}
        </div>

        <div className="w-full max-w-sm rounded-3xl py-5 px-4 bg-indigo-800/90 shadow-lg shadow-indigo-950/70 text-left">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/70 mb-2">
            {t("party.line_puzzle.label_tile")}
            {tileNo ? ` #${tileNo}` : ""}
          </div>
          {hasTile ? (
            <img
              src={linePuzzleTileDataUrl}
              alt={t("party.line_puzzle.label_tile")}
              className="w-full rounded-xl border border-white/20 bg-white/5"
            />
          ) : (
            <p className="text-sm text-white/75 text-center">{t("party.line_puzzle.fallback_no_tile")}</p>
          )}
          <p className="mt-3 text-sm text-white/90 text-center">
            {t("party.line_puzzle.body_share_clues", {
              count: Number.isFinite(linePuzzleTilesTotal) && linePuzzleTilesTotal > 0
                ? Math.floor(linePuzzleTilesTotal)
                : 6,
            })}
          </p>
          {linePuzzleTableHasDistractor ? (
            <p className="mt-2 text-xs text-white/75 text-center">
              {t("party.line_puzzle.body_distractor_hint")}
            </p>
          ) : null}
        </div>

        {!linePuzzleRoundSolved ? (
          <div className="w-full max-w-sm mt-4">
            <input
              type="text"
              value={answerValue}
              onChange={(e) => setAnswerValue(e.target.value)}
              placeholder={t("party.line_puzzle.input_placeholder")}
              maxLength={255}
              className="w-full rounded-2xl border border-white/30 bg-white/10 px-4 py-3 text-sm text-white placeholder-white/60 outline-none"
            />
            <button
              type="button"
              onClick={submitAnswer}
              disabled={!canSubmit}
              className={
                "mt-3 w-full rounded-full px-4 py-2 flex items-center justify-center " +
                (!canSubmit
                  ? "bg-white/30 cursor-not-allowed"
                  : "bg-indigo-400/95")
              }
            >
              <span className="text-sm font-semibold text-white">
                {submitLoading
                  ? t("party.line_puzzle.btn_submit_loading")
                  : submitCooldownLeft > 0
                    ? t("party.common.wait_seconds", { seconds: submitCooldownLeft })
                    : t("party.line_puzzle.btn_submit")}
              </span>
            </button>
          </div>
        ) : null}

        {hasLinePuzzleSubmissionResult && linePuzzleSubmissionCorrectRaw === false ? (
          <div className="mt-3 w-full max-w-sm rounded-2xl border border-red-300/40 bg-red-500/10 px-3 py-2 text-sm text-red-100 text-center">
            {t("party.line_puzzle.feedback_wrong")}
          </div>
        ) : null}

        {linePuzzleRoundSolved ? (
          <div className="mt-3 w-full max-w-sm rounded-2xl border border-emerald-200/40 bg-emerald-500/10 px-3 py-3 text-center">
            <p className="text-xs uppercase tracking-[0.14em] text-emerald-100 font-semibold">
              {t("party.line_puzzle.label_solution")}
            </p>
            <p className="mt-1 text-sm text-white font-semibold">
              {linePuzzleSolutionTitle || t("party.line_puzzle.feedback_correct")}
            </p>
            <button
              type="button"
              onClick={requestNextRound}
              disabled={nextLoading || !linePuzzleCanStartNext}
              className={
                "mt-3 w-full rounded-full px-4 py-2 flex items-center justify-center " +
                (nextLoading || !linePuzzleCanStartNext
                  ? "bg-white/30 cursor-not-allowed"
                  : "bg-indigo-400/95")
              }
            >
              <span className="text-sm font-semibold text-white">
                {nextLoading
                  ? t("party.line_puzzle.btn_next_loading")
                  : t("party.line_puzzle.btn_next")}
              </span>
            </button>
          </div>
        ) : null}

        {renderAnswerRevealControl({ isSolved: linePuzzleRoundSolved })}
        {submitError ? (
          <div className="mt-2 text-xs text-red-100 text-center break-words">{submitError}</div>
        ) : null}
        {nextError ? (
          <div className="mt-2 text-xs text-red-100 text-center break-words">{nextError}</div>
        ) : null}
        {renderRulesFoldout()}
      </RoundAssignmentWrapper>
    );
  }

  if (mode === "mislead_quiz") {
    const hasQuestion = Boolean(misleadQuestionId && misleadQuestionEn && misleadQuestionSk);
    const isKnower = misleadRole === "knower";
    const roundClosed = misleadRoundClosed || misleadSecondsLeft <= 0;
    const canVote = !isKnower && !roundClosed;
    const selectedOption = misleadCurrentUserVote >= 0 ? misleadCurrentUserVote : null;
    const optionIndexes = [0, 1, 2, 3, 4];
    const voteEntries = Object.entries(misleadVotes || {});
    const totalVotes = voteEntries.length;
    const totalEligibleVoters = Object.keys(misleadParticipantCodes || {}).filter(
      (uid) => String(uid || "").trim() && String(uid || "").trim() !== misleadKnowerUserId
    ).length;
    const voteLabel = (uid) => {
      const key = String(uid || "").trim();
      return misleadParticipantCodes[key] || key || "—";
    };
    const votesByOption = optionIndexes.reduce((acc, idx) => {
      acc[idx] = voteEntries
        .filter(([, picked]) => Number(picked) === idx)
        .map(([uid]) => voteLabel(uid));
      return acc;
    }, {});
    const counts = optionIndexes.map((idx) => votesByOption[idx].length);
    const maxCount = Math.max(0, ...counts);
    const topOptions = optionIndexes.filter((idx) => votesByOption[idx].length === maxCount && maxCount > 0);
    const hasTie = topOptions.length !== 1;
    const winningOption = hasTie ? null : topOptions[0];
    const correct = Number.isFinite(misleadCorrectIndex) ? misleadCorrectIndex : -1;
    const teamWon = winningOption === correct;
    const knowerWon = winningOption != null && winningOption !== correct;

    const optionEn = (idx) => (misleadOptionsEn[idx] || "").trim();
    const optionSk = (idx) => (misleadOptionsSk[idx] || "").trim();

    const submitVote = async (idx) => {
      if (!canVote || submitLoading) return;
      setSubmitLoading(true);
      setSubmitError("");
      try {
        const res = await api.post("events/api/party-games/mislead-quiz/vote/", {
          notification_id: notification.id,
          option_index: idx,
        });
        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        const code = err?.response?.data?.error;
        setSubmitError(code || t("party.mislead_quiz.error_vote_failed"));
      } finally {
        setSubmitLoading(false);
      }
    };

    const startNextRound = async () => {
      if (nextLoading) return;
      setNextLoading(true);
      setNextError("");
      try {
        const res = await api.post("events/api/party-games/mislead-quiz/next/", {
          notification_id: notification.id,
        });
        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        const code = err?.response?.data?.error;
        setNextError(code || t("party.mislead_quiz.error_next_failed"));
      } finally {
        setNextLoading(false);
      }
    };

    return (
      <RoundAssignmentWrapper showMessage={false}>
        <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-indigo-100/80 mb-3">
          {t("party.mislead_quiz.pill_title")}
          {gameLabel ? ` · ${gameLabel}` : ""}
        </div>

        {hasQuestion ? (
          <div className="w-full max-w-sm rounded-3xl py-5 px-4 bg-indigo-800/90 shadow-lg shadow-indigo-950/70 text-left">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/70 mb-2">
              {t("party.mislead_quiz.label_question")}
            </div>
            <p className="text-sm text-white leading-snug">{misleadQuestionEn}</p>
            <p className="text-sm text-white/90 leading-snug mt-1">{misleadQuestionSk}</p>
            <div className="mt-4 space-y-2">
              {optionIndexes.map((idx) => {
                const selected = selectedOption === idx;
                return (
                  <button
                    type="button"
                    key={idx}
                    onClick={() => submitVote(idx)}
                    disabled={!canVote || submitLoading}
                    className={
                      "w-full rounded-xl px-3 py-2 text-left border " +
                      (selected
                        ? "bg-indigo-400/30 border-indigo-200/70"
                        : "bg-white/5 border-white/20") +
                      (!canVote ? " opacity-80" : "")
                    }
                  >
                    <div className="text-sm text-white font-semibold">[{String.fromCharCode(65 + idx)}] {optionEn(idx)}</div>
                    <div className="text-xs text-white/80 mt-0.5">{optionSk(idx)}</div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="w-full max-w-sm rounded-3xl py-5 px-4 bg-indigo-800/90 shadow-lg shadow-indigo-950/70 text-left">
            <p className="text-sm text-white/80">{t("party.mislead_quiz.fallback_no_question")}</p>
          </div>
        )}

        {isKnower ? (
          <div className="mt-3 w-full max-w-sm rounded-2xl border border-amber-200/40 bg-amber-500/10 px-3 py-3 text-left">
            <p className="text-xs uppercase tracking-[0.14em] text-amber-100 font-semibold">
              {t("party.mislead_quiz.knower_hint")}
            </p>
            <button
              type="button"
              onClick={() => setMisleadAnswerVisible((v) => !v)}
              className="mt-2 rounded-full bg-amber-400/90 text-amber-950 text-xs font-semibold px-3 py-1"
            >
              {misleadAnswerVisible
                ? t("party.mislead_quiz.btn_hide_answer")
                : t("party.mislead_quiz.btn_show_answer")}
            </button>
            {misleadAnswerVisible && correct >= 0 ? (
              <p className="mt-2 text-sm text-amber-50">
                {t("party.mislead_quiz.correct_answer_label")}{" "}
                <span className="font-bold">[{String.fromCharCode(65 + correct)}]</span>
              </p>
            ) : null}
          </div>
        ) : (
          <p className="mt-3 max-w-sm text-xs text-white/85">
            {t("party.mislead_quiz.player_hint")}
          </p>
        )}

        <div className="mt-2 text-xs text-white/75">
          {t("party.mislead_quiz.time_left_label")}: {Math.max(0, misleadSecondsLeft)}s
        </div>
        <div className="mt-1 text-xs text-white/70">
          {t("party.mislead_quiz.votes_progress", {
            current: totalVotes,
            total: totalEligibleVoters,
          })}
        </div>

        {roundClosed ? (
          <div className="mt-3 w-full max-w-sm rounded-2xl border border-white/20 bg-white/10 px-3 py-3 text-left">
            <p className="text-sm font-semibold text-white">
              {hasTie
                ? t("party.mislead_quiz.result_tie")
                : teamWon
                ? t("party.mislead_quiz.result_team_won")
                : knowerWon
                ? t("party.mislead_quiz.result_knower_won")
                : t("party.mislead_quiz.result_pending")}
            </p>
            {correct >= 0 ? (
              <div className="mt-2 rounded-xl border border-emerald-200/30 bg-emerald-500/10 px-3 py-2">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-emerald-100/85">
                  {t("party.mislead_quiz.correct_answer_label")}
                </p>
                <p className="mt-1 text-sm text-white">
                  <span className="font-bold">[{String.fromCharCode(65 + correct)}]</span>{" "}
                  {optionEn(correct) || "—"}
                </p>
                {optionSk(correct) ? (
                  <p className="mt-0.5 text-xs text-white/80">{optionSk(correct)}</p>
                ) : null}
              </div>
            ) : null}
            <div className="mt-2 space-y-1">
              {optionIndexes.map((idx) => (
                <p key={`res_${idx}`} className="text-xs text-white/85">
                  [{String.fromCharCode(65 + idx)}] {votesByOption[idx].join(", ") || "—"}
                </p>
              ))}
            </div>
            <button
              type="button"
              onClick={startNextRound}
              disabled={nextLoading}
              className={
                "mt-3 w-full rounded-full px-4 py-2 flex items-center justify-center " +
                (nextLoading ? "bg-white/30 cursor-wait" : "bg-indigo-400/95")
              }
            >
              <span className="text-sm font-semibold text-white">
                {nextLoading
                  ? t("party.mislead_quiz.btn_next_loading")
                  : t("party.mislead_quiz.btn_next")}
              </span>
            </button>
          </div>
        ) : null}

        {submitError ? <div className="mt-2 text-xs text-red-100 text-center break-words">{submitError}</div> : null}
        {nextError ? <div className="mt-2 text-xs text-red-100 text-center break-words">{nextError}</div> : null}
        {renderRulesFoldout()}
      </RoundAssignmentWrapper>
    );
  }

  if (mode === "wrong_answer") {
    const qEn = String(wrongAnswerActiveQuestion.en || "").trim();
    const qSk = String(wrongAnswerActiveQuestion.sk || "").trim();
    const hasQuestion = Boolean(qEn || qSk);

    const requestNextQuestion = async () => {
      if (nextLoading) return;
      if (!notification?.id) return;

      setNextLoading(true);
      setNextError("");
      try {
        const res = await api.post("events/api/party-games/wrong-answer/next/", {
          notification_id: notification.id,
        });

        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        const code = err?.response?.data?.error;
        setNextError(code || t("party.wrong_answer.error_next_failed"));
      } finally {
        setNextLoading(false);
      }
    };

    return (
      <RoundAssignmentWrapper showMessage={false}>
        <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-amber-100/80 mb-3">
          {t("party.wrong_answer.pill_title")}
          {gameLabel ? ` · ${gameLabel}` : ""}
        </div>

        <div className="w-full max-w-sm rounded-3xl py-5 px-4 bg-amber-700/90 shadow-lg shadow-amber-900/70 text-left">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/70 mb-2">
            {t("party.wrong_answer.label_question")}
          </div>

          {hasQuestion ? (
            <div className="space-y-3">
              {qEn && <p className="text-base text-white leading-snug">{qEn}</p>}
              {qSk && <p className="text-base text-white/90 leading-snug">{qSk}</p>}
            </div>
          ) : (
            <p className="text-sm text-white/75">
              {t("party.wrong_answer.fallback_no_question")}
            </p>
          )}
        </div>

        <div className="mt-4 max-w-sm text-xs text-white/80 leading-relaxed text-left">
          <p>{t("party.wrong_answer.body_hint")}</p>
        </div>

        <button
          type="button"
          onClick={requestNextQuestion}
          disabled={nextLoading}
          className={
            "mt-4 w-full max-w-sm rounded-full px-4 py-2 flex items-center justify-center " +
            (nextLoading
              ? "bg-white/30 cursor-wait"
              : "bg-amber-500/95 shadow-md shadow-amber-900/60")
          }
        >
          <span className="text-sm font-semibold text-white text-center leading-snug">
            {nextLoading
              ? t("party.wrong_answer.btn_loading")
              : t("party.wrong_answer.btn_next")}
          </span>
        </button>

        {nextError ? (
          <div className="mt-2 text-xs text-red-100 text-center break-words">{nextError}</div>
        ) : null}
        {renderRulesFoldout()}
      </RoundAssignmentWrapper>
    );
  }

  if (mode === "alias") {
    const wEn = String(aliasActiveWord.en || "").trim();
    const wSk = String(aliasActiveWord.sk || "").trim();
    const hasWord = Boolean(wEn || wSk);
    const aliasTimeExpired = aliasHasStarted && aliasSecondsLeft <= 0;
    const aliasCanUseNext = aliasTimerRunning && !aliasTimeExpired;

    const formatTimer = (seconds) => {
      const clamped = Math.max(0, Number(seconds) || 0);
      const mins = Math.floor(clamped / 60);
      const sec = clamped % 60;
      return `${String(mins).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
    };

    const requestNextWord = async () => {
      if (nextLoading || !aliasCanUseNext) return;
      if (!notification?.id) return;

      setNextLoading(true);
      setNextError("");
      try {
        const res = await api.post("events/api/party-games/alias/next/", {
          notification_id: notification.id,
        });

        const updatedNotification = res?.data?.notification;
        if (updatedNotification && typeof onNotificationUpdate === "function") {
          onNotificationUpdate(updatedNotification);
        }
      } catch (err) {
        const code = err?.response?.data?.error;
        setNextError(code || t("party.alias.error_next_failed"));
      } finally {
        setNextLoading(false);
      }
    };

    return (
      <RoundAssignmentWrapper showMessage={false}>
        <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-cyan-100/80 mb-3">
          {t("party.alias.pill_title")}
          {gameLabel ? ` · ${gameLabel}` : ""}
        </div>

        <div className="w-full max-w-sm rounded-3xl py-5 px-4 bg-cyan-800/90 shadow-lg shadow-cyan-950/70 text-left">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/70">
              {t("party.alias.label_word")}
            </div>
            <div
              className={
                "text-xs font-bold rounded-full px-3 py-1 " +
                (aliasTimeExpired ? "bg-red-400/90 text-white" : "bg-cyan-500/90 text-white")
              }
            >
              {formatTimer(aliasSecondsLeft)}
            </div>
          </div>

          {aliasTimeExpired ? (
            <p className="text-sm text-white/95 leading-snug">
              {t("party.alias.timer_done")}
            </p>
          ) : hasWord ? (
            <div className="space-y-3">
              {wEn && <p className="text-lg font-semibold text-white leading-snug">{wEn}</p>}
              {wSk && <p className="text-base text-white/90 leading-snug">{wSk}</p>}
            </div>
          ) : (
            <p className="text-sm text-white/75">{t("party.alias.fallback_no_word")}</p>
          )}
        </div>

        <div className="mt-4 max-w-sm text-xs text-white/80 leading-relaxed text-left">
          <p>
            {!aliasHasStarted
              ? t("party.alias.body_wait_start")
              : aliasTimeExpired
              ? t("party.alias.body_next_player")
              : t("party.alias.body_hint")}
          </p>
        </div>

        <button
          type="button"
          onClick={requestNextWord}
          disabled={nextLoading || !aliasCanUseNext}
          className={
            "mt-4 w-full max-w-sm rounded-full px-4 py-2 flex items-center justify-center " +
            (nextLoading || !aliasCanUseNext
              ? "bg-white/30 cursor-not-allowed"
              : "bg-cyan-500/95 shadow-md shadow-cyan-900/60")
          }
        >
          <span className="text-sm font-semibold text-white text-center leading-snug">
            {nextLoading ? t("party.alias.btn_loading") : t("party.alias.btn_next")}
          </span>
        </button>

        <button
          type="button"
          onClick={() => {
            setNextError("");
            startAliasTimer();
          }}
          className="mt-2 w-full max-w-sm rounded-full px-4 py-2 flex items-center justify-center bg-cyan-300/95 shadow-md shadow-cyan-900/40"
        >
          <span className="text-sm font-semibold text-cyan-950 text-center leading-snug">
            {aliasHasStarted ? t("party.alias.btn_restart") : t("party.alias.btn_start")}
          </span>
        </button>

        {nextError ? (
          <div className="mt-2 text-xs text-red-100 text-center break-words">{nextError}</div>
        ) : null}
        {renderRulesFoldout()}
      </RoundAssignmentWrapper>
    );
  }

  // Default: table assignment
  return (
    <RoundAssignmentWrapper showMessage={false}>
      <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-blue-200/80 mb-3">
        {t("party.table.title")}
      </div>
      <div className="w-full max-w-xs rounded-3xl py-6 px-4 bg-blue-700/80 shadow-lg shadow-blue-900/70 flex items-center justify-center">
        <span className="text-[72px] sm:text-[88px] font-extrabold text-white leading-none">
          {tableNumber != null ? tableNumber : "?"}
        </span>
      </div>

      {(startsFirst || starterCode) && (
        <div className="mt-4 w-full max-w-xs">
          <div
            className={
              "px-4 py-2 rounded-full shadow-lg flex items-center justify-center " +
              (startsFirst
                ? "bg-emerald-500/95 shadow-emerald-900/60"
                : "bg-blue-600/95 shadow-blue-900/60")
            }
          >
            <span className="text-base font-semibold text-white text-center leading-snug">
              {startsFirst
                ? t("party.table.starts_you")
                : starterCode
                ? t("party.table.starts_user_with_code", { code: starterCode })
                : t("party.table.starts_someone")}
            </span>
          </div>
        </div>
      )}
      {renderRulesFoldout()}
    </RoundAssignmentWrapper>
  );
};

const MailboxModal = ({
  visible = true,
  onClose,
  asPage = false,
  initialNotification = null,
  initialNotificationId = null,
}) => {
  const { t, i18n } = useTranslation("menu");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    markAsRead,
    markAllAsRead,
    refetch: refetchUnreadMailbox,
    unreadCount: mailboxUnreadCount = 0,
  } = useMailbox();
  const {
    actions: requiredActions,
    openPendingActions,
    openActionByNotificationId,
    refresh: refreshRequiredActions,
  } = useContext(RequiredActionsContext);
  const pendingRequiredActionsCount = Array.isArray(requiredActions)
    ? requiredActions.length
    : 0;
  const pendingRequiredActionsBadge = pendingRequiredActionsCount > 99
    ? "99+"
    : String(pendingRequiredActionsCount);

  const tabs = useMemo(
    () => [
      { key: "all", label: t("mailbox.tabs.all", { defaultValue: "All" }) },
      { key: "unread", label: t("mailbox.tabs.unread", { defaultValue: "Unread" }) },
      { key: "read", label: t("mailbox.tabs.read", { defaultValue: "Read" }) },
    ],
    [t]
  );

  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [markAllPending, setMarkAllPending] = useState(false);
  const initialNotificationHandledRef = useRef(false);
  const selectedTabKey = tabs[selectedTab]?.key || "all";
  const tabOptions = useMemo(
    () => tabs.map((tab) => ({ value: tab.key, label: tab.label })),
    [tabs]
  );
  const handleTabChange = useCallback(
    (nextKey) => {
      const nextIndex = tabs.findIndex((tab) => tab.key === nextKey);
      setSelectedTab(nextIndex >= 0 ? nextIndex : 0);
      setSelectedNotification(null);
    },
    [tabs]
  );
  const selectedRoundAssignmentOpen =
    visible &&
    Boolean(selectedNotification?.id) &&
    isRoundAssignmentNotification(selectedNotification);
  const mobileModalHeight = "100dvh";

  // ────────── desktop / mobile switch ──────────
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" && window.innerWidth >= 1024
  );

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Lock background scroll
  useBodyLock(visible && !asPage);

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    refetch: refetchNotifications,
  } = useInfiniteQuery({
    queryKey: ["notifications"],
    queryFn: async ({ pageParam = 0 }) => {
      const params = { offset: pageParam, limit: 20 };
      const res = await api.get("/notifications/", { params });
      return res.data; // { total, results }
    },
    getNextPageParam: (lastPage, allPages) => {
      const lastLen = Number(lastPage?.results?.length || 0);
      if (lastLen === 0) return undefined;
      const fetched = allPages.reduce((acc, p) => acc + (p?.results?.length || 0), 0);
      const total = Number(lastPage?.total || 0);
      return fetched < total ? fetched : undefined;
    },
    staleTime: 2 * 60 * 1000,
    refetchInterval: selectedRoundAssignmentOpen ? 2500 : false,
  });

  // Mailbox can reopen with a still-fresh React Query cache after a native
  // push/resume. Always reconcile both the list and the badge on entry so the
  // badge never points to a notification missing from the visible feed.
  useEffect(() => {
    if (!visible) return;
    void refetchNotifications();
    void refetchUnreadMailbox?.();
    void refreshRequiredActions?.({ silent: true });
  }, [refetchNotifications, refetchUnreadMailbox, refreshRequiredActions, visible]);

  const handleNotificationUpdate = useCallback(
    (updatedNotification) => {
      if (!updatedNotification?.id) return;

      setSelectedNotification((prev) => {
        if (!prev || prev.id !== updatedNotification.id) return prev;
        return { ...prev, ...updatedNotification };
      });

      queryClient.setQueriesData({ queryKey: ["notifications"] }, (oldData) => {
        if (!oldData || !Array.isArray(oldData.pages)) return oldData;

        const nextPages = oldData.pages.map((page) => {
          const results = Array.isArray(page?.results) ? page.results : [];
          let changed = false;
          const nextResults = results.map((item) => {
            if (item?.id !== updatedNotification.id) return item;
            changed = true;
            return { ...item, ...updatedNotification };
          });
          return changed ? { ...page, results: nextResults } : page;
        });

        return { ...oldData, pages: nextPages };
      });

      queryClient.setQueryData(["mailbox"], (oldData) => {
        if (!Array.isArray(oldData)) return oldData;
        return oldData.map((item) =>
          item?.id === updatedNotification.id ? { ...item, ...updatedNotification } : item
        );
      });
    },
    [queryClient]
  );

  const notifications = useMemo(
    () => (data ? data.pages.flatMap((p) => p.results || []) : []),
    [data]
  );

  useEffect(() => {
    if (!visible || initialNotificationHandledRef.current) return;

    const requestedId = Number(initialNotificationId || initialNotification?.id || 0);
    if (!requestedId) {
      initialNotificationHandledRef.current = true;
      return;
    }

    const loadedNotification = notifications.find(
      (item) => Number(item?.id || 0) === requestedId
    );
    const fallbackNotification =
      Number(initialNotification?.id || 0) === requestedId
        ? initialNotification
        : null;
    const targetNotification = loadedNotification || fallbackNotification;
    if (!targetNotification) return;

    initialNotificationHandledRef.current = true;
    const preserveUnread = isPendingHostRoleRequestNotification(targetNotification);
    if (!targetNotification.is_read && !preserveUnread) {
      markAsRead(requestedId);
      queryClient.setQueriesData({ queryKey: ["notifications"] }, (oldData) => {
        if (!oldData || !Array.isArray(oldData.pages)) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            results: (Array.isArray(page?.results) ? page.results : []).map((item) =>
              Number(item?.id || 0) === requestedId
                ? { ...item, is_read: true }
                : item
            ),
          })),
        };
      });
    }

    setSelectedNotification({
      ...targetNotification,
      is_read: preserveUnread ? targetNotification.is_read : true,
    });
  }, [
    initialNotification,
    initialNotificationId,
    markAsRead,
    notifications,
    queryClient,
    visible,
  ]);
  const loadedUnreadNotifications = useMemo(
    () => notifications.filter((item) => item && !item.is_read),
    [notifications]
  );
  const loadedUnreadCount = loadedUnreadNotifications.length;
  const totalUnreadCount = Math.max(loadedUnreadCount, Number(mailboxUnreadCount || 0));
  const markAllDisabled = markAllPending || totalUnreadCount === 0;
  const markAllLabel = markAllPending
    ? t("mailbox.actions.mark_all_pending", { defaultValue: "Reading..." })
    : t("mailbox.actions.mark_all_read", { defaultValue: "Read all" });

  useEffect(() => {
    const selectedId = Number(selectedNotification?.id || 0);
    if (!selectedId || !Array.isArray(notifications) || notifications.length === 0) return;

    const fresh = notifications.find((item) => Number(item?.id || 0) === selectedId);
    if (!fresh) return;

    setSelectedNotification((prev) => {
      if (!prev || Number(prev?.id || 0) !== selectedId) return prev;

      const prevMeta = normalizeNotificationMeta(prev.meta);
      const nextMeta = normalizeNotificationMeta(fresh.meta);
      const unchanged =
        prev.title === fresh.title &&
        prev.message === fresh.message &&
        Boolean(prev.is_read) === Boolean(fresh.is_read) &&
        prev.sender === fresh.sender &&
        prev.kind === fresh.kind &&
        JSON.stringify(prevMeta) === JSON.stringify(nextMeta);

      return unchanged ? prev : { ...prev, ...fresh };
    });
  }, [notifications, selectedNotification?.id]);

  const filteredNotifications = useMemo(() => {
    if (selectedTabKey === "unread") return notifications.filter((n) => !n?.is_read);
    if (selectedTabKey === "read") return notifications.filter((n) => !!n?.is_read);
    return notifications;
  }, [notifications, selectedTabKey]);

  const handleMarkAllRead = useCallback(async () => {
    if (markAllPending || totalUnreadCount === 0) return;

    setMarkAllPending(true);
    try {
      if (typeof markAllAsRead === "function") {
        await markAllAsRead();
      } else {
        await api.post("/notifications/read-all/");
      }

      queryClient.setQueriesData({ queryKey: ["notifications"] }, (oldData) => {
        if (!oldData || !Array.isArray(oldData.pages)) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            results: (Array.isArray(page?.results) ? page.results : []).map((item) =>
              item && !isPendingHostRoleRequestNotification(item)
                ? { ...item, is_read: true }
                : item
            ),
          })),
        };
      });
      queryClient.setQueryData(["mailbox"], []);
      setSelectedNotification((prev) => (
        prev && !isPendingHostRoleRequestNotification(prev)
          ? { ...prev, is_read: true }
          : prev
      ));
      refetchUnreadMailbox?.();
    } finally {
      setMarkAllPending(false);
    }
  }, [markAllAsRead, markAllPending, queryClient, refetchUnreadMailbox, totalUnreadCount]);

  const selectedNotificationAction = useMemo(
    () => resolveNotificationAction(selectedNotification, t),
    [selectedNotification, t]
  );
  const selectedNotificationMessage = useMemo(
    () => getNotificationDisplayMessage(selectedNotification, t),
    [selectedNotification, t]
  );

  const handleSelectedNotificationAction = useCallback(async () => {
    if (!selectedNotificationAction) return;

    if (selectedNotificationAction.kind === "required_action") {
      onClose?.();
      const opened = await openActionByNotificationId?.(
        selectedNotificationAction.notificationId
      );
      if (!opened) {
        window.dispatchEvent(
          new CustomEvent("global-info", {
            detail: {
              message: t(
                "mailbox.errors.host_role_request_unavailable",
                "This request is no longer available."
              ),
            },
          })
        );
      }
      return;
    }

    if (!selectedNotificationAction.to) return;
    onClose?.();
    navigate(selectedNotificationAction.to);
  }, [navigate, onClose, openActionByNotificationId, selectedNotificationAction, t]);

  const handleHeaderBack = useCallback(() => {
    if (selectedNotification) {
      shouldRestoreListScrollRef.current = true;
      setSelectedNotification(null);
      return;
    }
    onClose?.();
  }, [onClose, selectedNotification]);
  useNativeSwipeBack(handleHeaderBack, {
    enabled: visible && !isDesktop,
    priority: 40,
  });

  const groupedNotifications = useMemo(() => {
    const groups = [];
    for (const n of filteredNotifications) {
      const dateLabel = formatNotificationDate(n?.created_at);
      const lastGroup = groups[groups.length - 1];
      if (!lastGroup || lastGroup.dateLabel !== dateLabel) {
        groups.push({ dateLabel, items: [n] });
      } else {
        lastGroup.items.push(n);
      }
    }
    return groups;
  }, [filteredNotifications]);

  const scrollRootRef = useRef(null);
  const listScrollTopRef = useRef(0);
  const shouldRestoreListScrollRef = useRef(false);
  const datePillRefs = useRef([]);
  const dateGroupRefs = useRef([]);
  const dateEndRefs = useRef([]);

  const updateDatePillFade = useCallback(() => {
    const root = scrollRootRef.current;
    if (!root) return;

    const rootRect = root.getBoundingClientRect();
    const paddingTopPx = Number.parseFloat(window.getComputedStyle(root).paddingTop || "0") || 0;
    const stickyTop = rootRect.top + paddingTopPx + MAILBOX_FILTER_STICKY_STACK_PX;

    const pills = datePillRefs.current || [];
    const dateGroupRefsArr = dateGroupRefs.current || [];
    const dateEndRefsArr = dateEndRefs.current || [];

    for (let i = 0; i < pills.length; i += 1) {
      const el = pills[i];
      if (!el) continue;

      const rect = el.getBoundingClientRect();
      const stickyEps = 1;
      if (rect.top > stickyTop + stickyEps) {
        el.style.opacity = "1";
        continue;
      }

      const groupEl = dateGroupRefsArr[i];
      const endEl = dateEndRefsArr[i];
      if (groupEl && endEl) {
        if (!endEl) {
          el.style.opacity = "1";
          continue;
        }
        const endRect = endEl.getBoundingClientRect();

        const remaining = Math.max(0, endRect.top - stickyTop);

        const startFadePx = Math.max(20, root.clientHeight * 0.22);

        const tLinear = Math.max(0, Math.min(1, remaining / startFadePx));

        const opacityEarly = Math.pow(tLinear, 2);

        const pushedUp = stickyTop - rect.top;
        if (pushedUp > 0) {
          const fadeRange = Math.max(18, rect.height);
          const opacityPushed = Math.max(0, Math.min(1, 1 - pushedUp / fadeRange));
          el.style.opacity = String(Math.min(opacityEarly, opacityPushed));
        } else {
          el.style.opacity = String(opacityEarly);
        }

        continue;
      }

      const pushedUp = stickyTop - rect.top;
      if (pushedUp <= 0) {
        el.style.opacity = "1";
        continue;
      }

      const fadeRange = Math.max(18, rect.height);
      const opacity = Math.max(0, Math.min(1, 1 - pushedUp / fadeRange));
      el.style.opacity = String(opacity);
    }
  }, []);

  useEffect(() => {
    datePillRefs.current.length = groupedNotifications.length;
    dateGroupRefs.current.length = groupedNotifications.length;
    dateEndRefs.current.length = groupedNotifications.length;
  }, [groupedNotifications.length]);

  // ────────── Guards against infinite fetch loops ──────────
  const isLoadingMoreLocalRef = useRef(false);
  const lastLoadAtRef = useRef(0);
  const scrollRafRef = useRef(null);
  const autoFillAttemptsRef = useRef(0);

  useEffect(() => {
    autoFillAttemptsRef.current = 0;
  }, [selectedTab]);

  const handleLoadMore = useCallback(async () => {
    if (!hasNextPage) return;
    if (isFetchingNextPage) return;
    if (isLoadingMoreLocalRef.current) return;

    const now = Date.now();
    if (now - (lastLoadAtRef.current || 0) < 350) return;
    lastLoadAtRef.current = now;

    isLoadingMoreLocalRef.current = true;
    try {
      await fetchNextPage();
    } finally {
      isLoadingMoreLocalRef.current = false;
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (!visible) return;
    if (selectedNotification) return;
    if (!hasNextPage) return;

    const root = scrollRootRef.current;
    if (!root) return;

    const notScrollable = root.scrollHeight <= root.clientHeight + 12;
    if (notScrollable && autoFillAttemptsRef.current < 1) {
      autoFillAttemptsRef.current += 1;
      handleLoadMore();
    }
  }, [visible, selectedNotification, hasNextPage, groupedNotifications, handleLoadMore]);

  const onListScroll = useCallback(() => {
    const root = scrollRootRef.current;
    if (!root) return;
    if (!visible) return;
    if (selectedNotification) return;

    if (scrollRafRef.current != null) return;
    scrollRafRef.current = window.requestAnimationFrame(() => {
      scrollRafRef.current = null;

      if (!hasNextPage) {
        updateDatePillFade();
        return;
      }

      const scrollable = root.scrollHeight > root.clientHeight + 20;
      if (!scrollable) {
        updateDatePillFade();
        return;
      }

      const distanceToBottom = root.scrollHeight - root.clientHeight - root.scrollTop;
      if (distanceToBottom <= 220) {
        handleLoadMore();
      }

      updateDatePillFade();
    });
  }, [visible, selectedNotification, hasNextPage, handleLoadMore, updateDatePillFade]);

  useLayoutEffect(() => {
    if (!visible) return;
    if (selectedNotification) return;
    updateDatePillFade();
  }, [visible, selectedNotification, groupedNotifications.length, updateDatePillFade]);

  useLayoutEffect(() => {
    if (!visible) return;
    if (selectedNotification) return;
    if (!shouldRestoreListScrollRef.current) return;

    const root = scrollRootRef.current;
    if (!root) return;

    const targetTop = Number(listScrollTopRef.current || 0);
    shouldRestoreListScrollRef.current = false;

    window.requestAnimationFrame(() => {
      root.scrollTop = targetTop;
      updateDatePillFade();
    });
  }, [visible, selectedNotification, groupedNotifications.length, updateDatePillFade]);

  useEffect(() => {
    return () => {
      if (scrollRafRef.current != null) {
        window.cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    };
  }, []);

  if (!visible) return null;

  const mailboxBoxClass = asPage
    ? "relative flex h-full min-h-0 w-full flex-col overflow-hidden text-white pointer-events-auto"
    : "relative flex h-[100dvh] w-screen flex-col overflow-hidden text-white pointer-events-auto lg:h-[min(874px,calc(100vh-80px))] lg:w-[402px] lg:rounded-[45px]";
  const mailboxHeaderClass = asPage
    ? "pointer-events-none absolute inset-x-0 top-0 z-20 h-[var(--vg-desktop-top-panel-height)] px-[var(--vg-page-gutter)] lg:left-1/2 lg:right-auto lg:w-[var(--vg-desktop-top-panel-width)] lg:-translate-x-1/2 lg:px-0"
    : "relative z-20 h-[calc(92px+var(--vg-safe-area-top))] shrink-0 px-[var(--vg-page-gutter)] lg:h-[92px]";
  const mailboxHeaderSurfaceClass = asPage
    ? "absolute inset-x-0 top-0 h-[var(--vg-desktop-top-panel-height)] rounded-b-[45px] bg-gradient-to-r from-[rgba(13,24,55,0.58)] to-[rgba(37,69,157,0.58)] shadow-[0_20px_34px_rgba(0,0,0,0.18)] backdrop-blur-[34px] lg:rounded-b-[var(--vg-desktop-top-panel-radius)]"
    : "absolute inset-x-0 top-0 h-[calc(92px+var(--vg-safe-area-top))] rounded-b-[45px] bg-gradient-to-r from-[rgba(13,24,55,0.58)] to-[rgba(37,69,157,0.58)] shadow-[0_20px_34px_rgba(0,0,0,0.18)] backdrop-blur-[34px] lg:h-[92px]";
  const mailboxHeaderRowClass = asPage
    ? "pointer-events-auto relative mx-auto grid w-full max-w-[var(--vg-mobile-shell-max)] grid-cols-[51px_minmax(0,1fr)_51px] items-center gap-4 pt-[calc(10px+var(--vg-safe-area-top))] lg:max-w-none lg:px-5 lg:pt-[10px]"
    : "relative mx-auto grid w-full max-w-[var(--vg-mobile-shell-max)] grid-cols-[51px_minmax(0,1fr)_51px] items-center gap-4 pt-[calc(10px+var(--vg-safe-area-top))] lg:pt-[10px]";
  const mailboxScrollClass = asPage
    ? "custom-scrollbar flex-1 min-h-0 overflow-y-auto overscroll-contain touch-pan-y px-[var(--vg-page-gutter)] pt-[var(--vg-desktop-top-content-offset)] lg:mx-auto lg:w-[min(var(--vg-desktop-feed-content-max),calc(100%_-_96px))] lg:px-0"
    : "flex-1 min-h-0 overflow-y-auto overscroll-contain touch-pan-y px-[26px] pt-[20px]";

  const ModalBox = (
    <div
      className={mailboxBoxClass}
      style={{
        ...AUTH_GRADIENT_STYLE,
        fontFamily: "Inter, sans-serif",
        ...(isDesktop ? {} : { height: mobileModalHeight, maxHeight: mobileModalHeight }),
      }}
    >
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-[170px] bg-[linear-gradient(0deg,rgba(0,0,0,0.95)_0%,rgba(0,0,0,0.72)_42%,rgba(0,0,0,0)_100%)]" />

      <header data-testid="mailbox-header" className={mailboxHeaderClass}>
        <div
          data-testid="mailbox-header-surface"
          className={mailboxHeaderSurfaceClass}
          aria-hidden="true"
        />
        <div data-testid="mailbox-header-row" className={mailboxHeaderRowClass}>
          <button
            className="flex h-[51px] w-[51px] items-center justify-center rounded-full bg-white/[0.12] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_8px_18px_rgba(7,16,44,0.18)] backdrop-blur-[16px] transition active:scale-[0.98]"
            aria-label={
              selectedNotification
                ? t("mailbox.detail.back")
                : t("mailbox.header.close_aria")
            }
            onClick={handleHeaderBack}
            type="button"
          >
            <img src="/assets/landing/back_arrow.svg" alt="" className="h-[24px] w-[24px]" />
          </button>

          <h2 className="truncate text-center text-[32px] font-semibold leading-[35px] text-white">
            {t("mailbox.header.title")}
          </h2>

          {pendingRequiredActionsCount > 0 ? (
            <button
              type="button"
              onClick={openPendingActions}
              className="relative flex h-[51px] w-[51px] items-center justify-center rounded-full bg-white/[0.12] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_8px_18px_rgba(7,16,44,0.18)] backdrop-blur-[16px] transition active:scale-[0.98]"
              aria-label={t("mailbox.header.required_actions_aria", {
                count: pendingRequiredActionsCount,
              })}
              data-testid="mailbox-required-actions-button"
            >
              <span
                className="text-[28px] font-semibold leading-none"
                aria-hidden="true"
              >
                !
              </span>
              <span
                className="absolute -right-1 -top-1 flex h-[21px] min-w-[21px] items-center justify-center rounded-full border-2 border-[#456FD2] bg-white px-1 text-[10px] font-extrabold leading-none text-[#1F74FF] shadow-[0_3px_8px_rgba(7,16,44,0.28)]"
                aria-hidden="true"
                data-testid="mailbox-required-actions-badge"
              >
                {pendingRequiredActionsBadge}
              </span>
            </button>
          ) : (
            <div aria-hidden="true" />
          )}
        </div>
      </header>

      <div className="relative z-10 flex-1 min-h-0 flex flex-col">
        <div
          ref={scrollRootRef}
          data-testid="mailbox-scroll-root"
          data-mailbox-scroll-root="true"
          className={`${mailboxScrollClass} ${
            selectedNotification
              ? "pb-[calc(var(--bottom-nav-clearance,112px)+1.75rem)]"
              : asPage
                ? "pb-10"
                : "pb-[calc(var(--bottom-nav-clearance,112px)+1rem)] lg:pb-4"
          }`}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
          onScroll={onListScroll}
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {!selectedNotification ? (
            <div
              data-testid="mailbox-filter-bar"
              data-mailbox-filter-bar="true"
              className="sticky top-0 z-30 mb-4 flex items-center justify-between gap-3 lg:pl-3"
            >
              <div className="min-w-0 flex-1">
                <SegmentedModeToggle
                  value={selectedTabKey}
                  options={tabOptions}
                  onChange={handleTabChange}
                  ariaLabel={t("mailbox.header.title")}
                  className="vg-glass-control h-10 w-full max-w-none !overflow-visible !rounded-[99px] !border-white/10"
                  style={{ width: "100%" }}
                  knobClassName="h-[34px] !rounded-[99px] !border-0"
                  buttonClassName="min-w-0 px-2 !text-[13px] !font-medium !leading-5 !text-white"
                  surfaceStyle={{
                    backgroundImage:
                      "linear-gradient(180deg,rgba(255,255,255,0.02)_0%,rgba(255,255,255,0.1)_100%)",
                  }}
                  activeStyle={{
                    backgroundImage: "none",
                    backgroundColor: "rgba(0,0,0,0.5)",
                    boxShadow:
                      "0 1px 0 rgba(0,0,0,0.05), 0 4px 4px rgba(0,0,0,0.05), 0 10px 10px rgba(0,0,0,0.1)",
                  }}
                  measureKnob
                  knobInset={3}
                />
              </div>
              <button
                type="button"
                onClick={handleMarkAllRead}
                disabled={markAllDisabled}
                className={`h-[42px] shrink-0 rounded-full px-4 text-[13px] font-bold leading-none transition ${
                  markAllDisabled
                    ? "cursor-not-allowed bg-white/18 text-white/55 shadow-none"
                    : "bg-white text-[#1D2C54] shadow-[0_8px_18px_rgba(0,0,0,0.16)] active:scale-[0.98]"
                }`}
                style={markAllDisabled ? { WebkitTextFillColor: "rgba(255,255,255,0.55)" } : undefined}
              >
                {markAllLabel}
              </button>
            </div>
          ) : null}
          <div
            className={
              selectedNotification
                ? ""
                : ""
            }
          >
	            {selectedNotification ? (
	              /* Detail view */
	              <article className="px-1 pt-3 text-white">
	                <header>
	                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-white/48">
	                    {t("mailbox.detail.topic_label", { defaultValue: "Topic" })}
	                  </p>
	                  <h3 className="text-[24px] font-semibold leading-[29px] text-white">
	                    {selectedNotification.title}
	                  </h3>

	                  <div className="mt-5 border-y border-white/14 py-4">
	                    <div className="flex min-w-0 items-center justify-between gap-4">
	                      <div className="min-w-0">
	                        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-white/48">
	                          {t("mailbox.detail.from_label")}
	                        </p>
	                        <p className="mt-1 truncate text-[15px] font-medium leading-5 text-white/90">
	                          {String(selectedNotification.sender || "").trim() || "System"}
	                        </p>
	                      </div>
	                      <time className="shrink-0 pt-[17px] text-right text-[15px] font-medium leading-5 text-white/76">
	                        {formatNotificationDateTime(selectedNotification.created_at)}
	                      </time>
	                    </div>
                  </div>
                </header>

                <div className="pt-6">
                  {isRoundAssignmentNotification(selectedNotification) ? (
                    <RoundAssignmentContent
                      notification={selectedNotification}
                      onNotificationUpdate={handleNotificationUpdate}
                    />
	                  ) : (
	                    <>
	                      <p className="whitespace-pre-wrap break-words text-[15px] font-medium leading-[24px] text-white/90">
	                        {selectedNotificationAction ? (
	                          removeInlineLinksForActionMessage(selectedNotificationMessage)
                          || t("mailbox.detail.empty_message", "Message content is unavailable.")
                        ) : (
                          <Linkify options={linkifyOptions}>
                            {selectedNotificationMessage || t("mailbox.detail.empty_message", "Message content is unavailable.")}
	                          </Linkify>
	                        )}
	                      </p>
	                      <p className="mt-8 whitespace-pre-line text-[15px] font-medium leading-[24px] text-white/72">
	                        {t("mailbox.detail.signature", {
	                          lng: getNotificationLanguage(selectedNotification, i18n?.resolvedLanguage || i18n?.language),
	                          defaultValue: "Best regards,\nVaultGrid Team",
	                        })}
                      </p>
                    </>
                  )}
                </div>

                {selectedNotificationAction ? (
                  <div className="mt-7 shrink-0">
                    <button
                      type="button"
                      onClick={handleSelectedNotificationAction}
                      className="h-12 w-full rounded-full bg-white text-[15px] font-bold text-[#1D2C54] shadow-[0_10px_24px_rgba(0,0,0,0.18)]"
                    >
                      {selectedNotificationAction.label}
                    </button>
                  </div>
                ) : null}
              </article>
            ) : (
              /* List view */
              <>
                {isLoading ? <MailboxListLoadingSkeleton /> : null}

                {isError && (
                  <div className="flex justify-center py-6">
                    <span className="text-red-200">{t("mailbox.list.failed")}</span>
                  </div>
                )}

                {!isLoading && !isError && filteredNotifications.length === 0 && (
                  <div className="flex justify-center py-6">
                    <span className="text-white">{t("mailbox.list.empty")}</span>
                  </div>
                )}

                {groupedNotifications.map((group, idx) => (
                  <div
                    ref={(el) => (dateGroupRefs.current[idx] = el)}
                    key={group.dateLabel || `group-${group.items[0]?.id}`}
                    className={idx === 0 ? "" : "mt-3"}
                  >
                    <div data-testid="mailbox-date-sticky" className="sticky top-[58px] z-20 mb-2">
                      <div ref={(el) => (datePillRefs.current[idx] = el)}>
                        <DatePill label={group.dateLabel} />
                      </div>
                    </div>

                    {group.items.map((n) => {
                      const isUnread = !n.is_read;
                      const titleColor = isUnread ? "text-white" : "text-white/55";
                      const messageColor = isUnread ? "text-white/82" : "text-white/42";
                      const notificationAction = resolveNotificationAction(n, t);
                      const displayMessage = getNotificationDisplayMessage(n, t);
                      const previewMessage = notificationAction
                        ? (removeInlineLinksForActionMessage(displayMessage) || displayMessage)
                        : displayMessage;
                      return (
                        <div key={n.id} className="mb-2">
                          <button
                            type="button"
                            onClick={() => {
                              const root = scrollRootRef.current;
                              listScrollTopRef.current = Number(root?.scrollTop || 0);
                              const preserveUnread = isPendingHostRoleRequestNotification(n);
                              if (isUnread && !preserveUnread) {
                                markAsRead(n.id);
                                queryClient.setQueriesData({ queryKey: ["notifications"] }, (oldData) => {
                                  if (!oldData || !Array.isArray(oldData.pages)) return oldData;
                                  return {
                                    ...oldData,
                                    pages: oldData.pages.map((page) => ({
                                      ...page,
                                      results: (Array.isArray(page?.results) ? page.results : []).map((item) =>
                                        Number(item?.id || 0) === Number(n.id || 0)
                                          ? { ...item, is_read: true }
                                          : item
                                      ),
                                    })),
                                  };
                                });
                              }
                              setSelectedNotification({
                                ...n,
                                is_read: preserveUnread ? n.is_read : true,
                              });
                            }}
                            className={`flex w-full items-center rounded-[22px] border px-4 py-3 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-[14px] transition active:scale-[0.995] ${
                              isUnread
                                ? "border-white/14 bg-white/[0.09]"
                                : "border-white/8 bg-white/[0.045]"
                            }`}
                          >
                            <span
                              className={`mr-3 h-2.5 w-2.5 shrink-0 rounded-full ${
                                isUnread ? "bg-[#85F153] shadow-[0_0_10px_rgba(133,241,83,0.55)]" : "bg-white/18"
                              }`}
                              aria-hidden="true"
                            />
                            <div className="flex min-w-0 flex-1 flex-col">
                              <div className="flex min-w-0 items-baseline">
                                <span className={`${titleColor} mr-1 shrink-0 text-xs font-semibold italic`}>
                                  {t("mailbox.list.badge")}
                                </span>
                                <span className={`${titleColor} min-w-0 truncate text-base font-extrabold`}>
                                  {n.title}
                                </span>
                              </div>
                              <span
                                className={`block w-full truncate break-words text-left text-xs font-medium ${messageColor}`}
                              >
                                {notificationAction ? (
                                  previewMessage
                                ) : (
                                  <Linkify
                                    options={{
                                      attributes: {
                                        target: "_blank",
                                        rel: "noopener noreferrer",
                                      },
                                    }}
                                  >
                                    {previewMessage}
                                  </Linkify>
                                )}
                              </span>
                            </div>
                            <span className="ml-3 shrink-0 text-2xl leading-none text-white/42">›</span>
                          </button>
                        </div>
                      );
                    })}
                    {/* End-of-day sentinel for smooth fade */}
                    <div ref={(el) => (dateEndRefs.current[idx] = el)} className="h-px w-full" />
                  </div>
                ))}

                {/* Skeleton row while the next notification page is loading. */}
                {!isLoading &&
                  !isError &&
                  groupedNotifications.length > 0 &&
                  isFetchingNextPage &&
                  hasNextPage && (
                    <MailboxListLoadingSkeleton rows={1} compact />
                  )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (asPage) {
    return (
      <main className="flex h-full min-h-0 w-full items-stretch justify-center overflow-hidden">
        {ModalBox}
      </main>
    );
  }

  if (!isDesktop) {
    // Vault uses fixed content layers up to z-81; keep Mailbox above them
    // while leaving the shared bottom navigation (z-90) accessible.
    return (
      <div
        className="fixed inset-0 z-[85] flex items-start justify-center overflow-hidden bg-transparent pointer-events-auto"
      >
        {ModalBox}
      </div>
    );
  }

  const desktopModal = (
    <main className="absolute inset-0 z-50 flex items-center justify-center">
      <div
        onClick={onClose}
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        aria-hidden="true"
      />
      <div className="relative z-50 overflow-hidden rounded-[45px] shadow-2xl">{ModalBox}</div>
    </main>
  );

  const root = document.getElementById("modal-root");
  return root ? createPortal(desktopModal, root) : desktopModal;
};

export default MailboxModal;
